import {
  FileWithBadge,
  MonitorWithInstructor,
} from '@components/common/icons/custom-icons'
import {
  ActivitiesType,
  CourseDescriptionFoldType,
  CourseFirstFoldType,
  CourseNameType,
  courseResourceType,
  FaqType,
  recommendedCoursesType,
  SkillsYouWillGainFoldType,
  TestimonialType,
} from '@lib/types/course-landing-page'
import { ModuleType } from '@lib/types/course-modules'
import { BarChart, Clock, FileText } from 'react-feather'

export const COURSE_NAMES = {
  SCRATCH_PROGRAMMING: 'SCRATCH_PROGRAMMING',
  PYTHON_FOR_KIDS: 'PYTHON_FOR_KIDS',
  ROBOTICS: 'ROBOTICS',
  ROBLOX: 'ROBLOX',
  AP_COMPUTER_SCIENCE_A: 'AP_COMPUTER_SCIENCE_A',
  HPE_CODEWARS_CODE_BATTLE: 'HPE_CODEWARS_CODE_BATTLE',
  HPE_CODEWARS_HACKATHON: 'HPE_CODEWARS_HACKATHON',
  AI_AND_ML: 'AI_AND_ML',
  AI_GENIUS: 'AI_GENIUS',
  AI_EXPERT: 'AI_EXPERT',
  GAME_DEV: 'GAME_DEV',
  APP_DEV: 'APP_DEV',
  WEB_DEV: 'WEB_DEV',
  THUNKABLE: 'THUNKABLE',
  JAVA: 'JAVA',
  TRINKET: 'TRINKET',
  PYGAME: 'PYGAME',
  ANIMATION: 'ANIMATION',
  TURTLE: 'TURTLE',
  MIT_APP_INVENTOR: 'MIT_APP_INVENTOR',
  BOOTSTRAP: 'BOOTSTRAP',
}

export const COURSE_TITLES = {
  PYTHON_FOR_KIDS: 'Python Champion: Enhance Your Skills',
  SCRATCH_PROGRAMMING: 'Scratch Programming: Unleash Your Creativity',
  ROBLOX: 'Roblox',
  AP_COMPUTER_SCIENCE_A: 'AP Computer Science A',
  HPE_CODEWARS_CODE_BATTLE: 'HPE CODEWARS CODE BATTLE',
  HPE_CODEWARS_HACKATHON: 'HPE CODEWARS HACKATHON',
  AI_AND_ML: 'AI & Machine Learning for Kids and Teens',
  THUNKABLE: 'Thunkable for Kids and Teens',
  JAVA: 'Java for Kids and Teens',
  TRINKET: 'Trinket for Kids and Teens',
  PYGAME: 'Pygame for Kids and Teens',
  ANIMATION: 'Animation for Kids and Teens',
  TURTLE: 'Turtle for Kids and Teens',
  MIT_APP_INVENTOR: 'MIT App Inventor for Kids and Teens',
  BOOTSTRAP: 'Bootstrap for Kids and Teens',
  AI_GENIUS: 'AI Genius — ChatGPT, AI-powered Apps & Games',
  AI_EXPERT: 'AI Expert — Hands-On AI, LLMs & Python',
  GAME_DEV: 'Game Development for Kids: Build Fun Games',
  APP_DEV: 'App Development for Kids: Create AI-Powered Apps',
  WEB_DEV: 'Website Development: Learn to Build Websites',
}

export const COURSE_INFO: Record<CourseNameType, recommendedCoursesType> = {
  SCRATCH_PROGRAMMING: {
    courseName: COURSE_TITLES['SCRATCH_PROGRAMMING'],
    link: '/courses/scratch-programming/',
    courseQuery: 'scratch-programming-trial-class',
    image: 'scratch-course',
    grade: 'Grade 1-8',
    activity: '50+',
    lesson: 36,
    duration: '2 - 4 months',
    learningOutcome: [
      'Learn the Coding Language',
      'Make Stuff Move (Animations & Games)',
      'Meet the Super-Smart Machines (AI Basics)',
      'Design Interactive Stories and Art',
    ],
  },
  AI_GENIUS: {
    courseName: COURSE_TITLES['AI_GENIUS'],
    courseQuery: 'ai-trial-class',
    image: 'ai-genuis-course',
    grade: 'Grade 3-7',
    activity: '48+',
    lesson: 48,
    duration: '4 - 6 months',
    learningOutcome: [
      'Create AI-powered games and mobile applications',
      'Develop translators and speech-to-text tools',
      'Create tools like carbon calculators',
      'Develop a chatbot using OpenAI integration',
    ],
  },
  AI_EXPERT: {
    courseName: COURSE_TITLES['AI_EXPERT'],
    courseQuery: 'ai-trial-class',
    image: 'ai-expert-course',
    grade: 'Grade 8-12',
    activity: '48+',
    lesson: 48,
    duration: '4 - 6 months',
    learningOutcome: [
      'Learn image processing with OpenCV',
      'Turn text into stunning AI-generated images',
      'Master Python for Artificial Intelligence',
      'Build real world Projects using LLMs',
    ],
  },
  PYTHON_FOR_KIDS: {
    courseName: COURSE_TITLES['PYTHON_FOR_KIDS'],
    link: '/courses/python-for-kids/',
    courseQuery: 'python-for-kids-trial-class',
    image: 'pyhton-course',
    grade: 'Grade 6-12',
    activity: '90+',
    lesson: 44,
    duration: '4 - 6 months',
    learningOutcome: [
      'Master Python programming',
      'Build interactive games',
      'Develop GUI applications',
      'Understand & apply OOP concepts',
    ],
  },
  GAME_DEV: {
    courseName: COURSE_TITLES['GAME_DEV'],
    link: '/courses/game-development/',
    courseQuery: 'game-development-trial-class',
    image: 'game-dev-course',
    grade: 'Grade 4-8',
    activity: '50+',
    lesson: 36,
    duration: '2 - 4 months',
    learningOutcome: [
      'Code Like a Pro (For Games)',
      'Become a Game Creator (Scratch & Javascript)',
      'Think Like a Gamer (But Smarter)',
      'Animate Characters and Objects (Bringing Games to Life)',
    ],
  },
  WEB_DEV: {
    courseName: COURSE_TITLES['WEB_DEV'],
    link: '/courses/web-development/',
    courseQuery: 'web-development-trial-class',
    image: 'web-dev-course',
    grade: 'Grade 8-12',
    activity: '90+',
    lesson: 90,
    duration: '10 - 12 months',
    learningOutcome: [
      'Build responsive websites',
      'Develop interactive web pages',
      'Master website development',
      'Implement best practices in web dev',
    ],
  },
  APP_DEV: {
    courseName: COURSE_TITLES['APP_DEV'],
    link: '/courses/app-development/',
    courseQuery: 'app-development-trial-class',
    image: 'app-dev-course',
    grade: 'Grade 4-8',
    activity: '50+',
    lesson: 36,
    duration: '2 - 4 months',
    learningOutcome: [
      'Build Interactive Interfaces',
      'Design Awesome Apps',
      'Make Your App Super Smart (Integrate camera, accelerometers, GPS, etc.)',
      'Share Your App with the World',
    ],
  },
}

export const COURSE_FIRST_FOLD: Record<CourseNameType, CourseFirstFoldType> = {
  SCRATCH_PROGRAMMING: {
    name: 'Learn Scratch Programming with Top Coding Course Instructors',
    description:
      'Codingal offers online Scratch programming classes for kids with expert Computer Science instructors',
    activities: COURSE_INFO['SCRATCH_PROGRAMMING'].activity,
    modules: 6,
    lessons: COURSE_INFO['SCRATCH_PROGRAMMING'].lesson,
    quizzes: 36,
    certificate: 'Scratch developer certificate',
    curriculumPDFLink:
      'https://codingal.s3.ap-south-1.amazonaws.com/web/pdfs/curriculum/courses/v7/codingal-scratch-curriculum.pdf',
    firstFoldTable: [
      {
        icon: (
          <>
            <BarChart
              className="text-orange-600 xl:inline w-5 xl:mb-1 xl:pr-1"
              height={16}
            />
          </>
        ),
        title: 'Eligibility',
        description: COURSE_INFO['SCRATCH_PROGRAMMING'].grade,
      },
      {
        icon: (
          <>
            <MonitorWithInstructor className="text-orange-600 xl:inline w-4 xl:pr-1" />
          </>
        ),
        title: 'Program Delivery',
        description: 'Online',
      },
      {
        icon: (
          <>
            <FileWithBadge className="text-orange-600 xl:inline w-4 xl:pr-1" />
          </>
        ),
        title: 'Certification by',
        description: 'STEM.org',
      },
      {
        icon: (
          <>
            <Clock
              className="text-orange-600 xl:inline w-5 mb-1 xl:pr-1"
              height={16}
            />{' '}
          </>
        ),
        title: 'Duration',
        description: COURSE_INFO['SCRATCH_PROGRAMMING'].duration,
      },
      {
        icon: (
          <>
            <FileText
              className="text-orange-600 xl:inline w-5 mb-1 xl:pr-1"
              height={16}
            />
          </>
        ),
        title: 'Program',
        description: '75+ activities & projects',
      },
    ],
  },
  HPE_CODEWARS_CODE_BATTLE: {
    name: 'Ace the HPE CodeWars Code Battle competition',
    description:
      'Learn the fundamentals of algorithm to solve complex coding problems',
    modules: 2,
    lessons: 12,
    quizzes: 100,
    certificate: 'Code Battle course certificate',
  },
  HPE_CODEWARS_HACKATHON: {
    name: 'Ace the HPE CodeWars Hackathon',
    description:
      'Learn to build your coding project with computer science expert',
    modules: 2,
    lessons: 12,
    quizzes: 100,
    certificate: 'Hackathon course certificate',
  },
  PYTHON_FOR_KIDS: {
    name:
      'Learn Python with the Top Coding Course Instructors for Kids and Teens',
    description:
      'Online 1:1 Python programming classes for kids with expert Computer Science teachers',
    activities: '100+',
    modules: 7,
    lessons: 44,
    quizzes: 44,
    certificate: 'Python developer certificate',
    curriculumPDFLink:
      'https://codingal.s3.ap-south-1.amazonaws.com/web/pdfs/curriculum/courses/v7/codingal-python-curriculum.pdf',
    firstFoldTable: [
      {
        icon: (
          <>
            <BarChart
              className="text-orange-600 xl:inline w-5 xl:mb-1 xl:pr-1"
              height={16}
            />
          </>
        ),
        title: 'Eligibility',
        description: 'Grade 6-12',
      },
      {
        icon: (
          <>
            <MonitorWithInstructor className="text-orange-600 xl:inline w-4 xl:pr-1" />
          </>
        ),
        title: 'Program Delivery',
        description: 'Online',
      },
      {
        icon: (
          <>
            <FileWithBadge className="text-orange-600 xl:inline w-4 xl:pr-1" />
          </>
        ),
        title: 'Certification by',
        description: 'STEM.org',
      },
      {
        icon: (
          <>
            <Clock
              className="text-orange-600 xl:inline w-5 mb-1 xl:pr-1"
              height={16}
            />{' '}
          </>
        ),
        title: 'Duration',
        description: '5 Months',
      },
      {
        icon: (
          <>
            <FileText
              className="text-orange-600 xl:inline w-5 mb-1 xl:pr-1"
              height={16}
            />
          </>
        ),
        title: 'Program',
        description: '100+ activities & projects',
      },
    ],
  },
  ROBLOX: {
    name: 'Learn Lua with the Top Roblox Coding Courses for Kids and Teens',
    description:
      'Learn to create and publish games on Roblox Studio. Codingal offers online 1:1 coding classes for Roblox coding with expert Computer Science teachers.',
    activities: '30+',
    modules: 6,
    lessons: 36,
    quizzes: 36,
    certificate: 'Roblox game developer certificate',
    curriculumPDFLink:
      'https://codingal.s3.ap-south-1.amazonaws.com/web/pdfs/curriculum/courses/v7/roblox-curriculum.pdf',
    firstFoldTable: [
      {
        icon: (
          <>
            <BarChart
              className="text-orange-600 xl:inline w-5 xl:mb-1 xl:pr-1"
              height={16}
            />
          </>
        ),
        title: 'Eligibility',
        description: 'Grade 4-8',
      },
      {
        icon: (
          <>
            <MonitorWithInstructor className="text-orange-600 xl:inline w-4 xl:pr-1" />
          </>
        ),
        title: 'Program Delivery',
        description: 'Online',
      },
      {
        icon: (
          <>
            <FileWithBadge className="text-orange-600 xl:inline w-4 xl:pr-1" />
          </>
        ),
        title: 'Certification by',
        description: 'STEM.org',
      },
      {
        icon: (
          <>
            <Clock
              className="text-orange-600 xl:inline w-5 mb-1 xl:pr-1"
              height={16}
            />{' '}
          </>
        ),
        title: 'Duration',
        description: '4 Months',
      },
      {
        icon: (
          <>
            <FileText
              className="text-orange-600 xl:inline w-5 mb-1 xl:pr-1"
              height={16}
            />
          </>
        ),
        title: 'Program',
        description: '30+ activities & projects',
      },
    ],
  },
  AP_COMPUTER_SCIENCE_A: {
    name: 'Learn AP Computer Science A  with the Top Coding Courses',
    description:
      'Prepare for the AP Computer Science A exam with Codingal. Online 1:1 classes for teens with expert instructors',
    activities: '100+',
    modules: 8,
    lessons: 48,
    quizzes: 48,
    certificate: 'AP Computer Science A certificate',
    curriculumPDFLink:
      'https://codingal.s3.ap-south-1.amazonaws.com/web/pdfs/curriculum/courses/v7/ap-computer-science-a.pdf',
    firstFoldTable: [
      {
        icon: (
          <>
            <BarChart
              className="text-orange-600 xl:inline w-5 xl:mb-1 xl:pr-1"
              height={16}
            />
          </>
        ),
        title: 'Eligibility',
        description: 'Grade 11-12',
      },
      {
        icon: (
          <>
            <MonitorWithInstructor className="text-orange-600 xl:inline w-4 xl:pr-1" />
          </>
        ),
        title: 'Program Delivery',
        description: 'Online',
      },
      {
        icon: (
          <>
            <FileWithBadge className="text-orange-600 xl:inline w-4 xl:pr-1" />
          </>
        ),
        title: 'Certification by',
        description: 'STEM.org',
      },
      {
        icon: (
          <>
            <Clock
              className="text-orange-600 xl:inline w-5 mb-1 xl:pr-1"
              height={16}
            />{' '}
          </>
        ),
        title: 'Duration',
        description: '5 Months',
      },
      {
        icon: (
          <>
            <FileText
              className="text-orange-600 xl:inline w-5 mb-1 xl:pr-1"
              height={16}
            />
          </>
        ),
        title: 'Program',
        description: '100+ activities & projects',
      },
    ],
  },
  AI_AND_ML: {
    name:
      'Learn Artificial Intelligence with the Top ML and AI Courses for Kids & Teens',
    description:
      'Our AI and Machine Learning courses introduce the exciting world of AI to students in grades 3-12. Through engaging, hands-on projects, kids explore real-life applications of AI! We offer two courses—AI Genius for kids and AI Expert for teens. Learners will discover concepts used in virtual assistants, self-driving cars, healthcare, and more. These courses make complex ideas easy to understand, spark curiosity, and build skills for the future!',
    activities: '50+',
    modules: 8,
    lessons: 48,
    quizzes: 48,
    certificate: 'AI & ML expert certificates',
    videoURL:
      'https://codingal.s3.ap-south-1.amazonaws.com/web/videos/Introduction_to_AI__ML.mp4',
    firstFoldTable: [
      {
        icon: (
          <>
            <BarChart
              className="text-orange-600 xl:inline w-5 xl:mb-1 xl:pr-1"
              height={16}
            />
          </>
        ),
        title: 'Eligibility',
        description: 'Grade 3-12',
      },
      {
        icon: (
          <>
            <MonitorWithInstructor className="text-orange-600 xl:inline w-4 h-4 xl:pr-1" />
          </>
        ),
        title: 'Program Delivery',
        description: 'Online',
      },
      {
        icon: (
          <>
            <FileWithBadge className="text-orange-600 xl:inline w-4 h-4 xl:pr-1" />
          </>
        ),
        title: 'Certification by',
        description: 'STEM.org',
      },
      {
        icon: (
          <>
            <Clock
              className="text-orange-600 xl:inline w-5 mb-1 xl:pr-1"
              height={16}
            />{' '}
          </>
        ),
        title: 'Duration',
        description: '3-6 Months',
      },
      {
        icon: (
          <>
            <FileText
              className="text-orange-600 xl:inline w-5 mb-1 xl:pr-1"
              height={16}
            />
          </>
        ),
        title: 'Program',
        description: '50+ activities & projects',
      },
    ],
  },
  THUNKABLE: {
    name: 'Top Coding Class Instructors for Kids and Teens to Learn Thunkable',
    description:
      'At Codingal, we offer a K-12-aligned curriculum that introduces students to the world of app development through Thunkable, a fun and intuitive block-based platform. Designed for young minds, this course helps students move beyond basic coding and start building real-world apps they’ll be proud to share. Throughout the course, students explore exciting features such as sensors to build motion-based games like Subway Surfers or Temple Run, location tracking to create apps that detect and use geographic data, and custom screen design to learn how to craft user-friendly, visually appealing interfaces. They also get hands-on with Artificial Intelligence by integrating smart tools like text-to-speech and language translation, and learn to manage real-time data using platforms like Firebase or Google Sheets. From predicting the weather and building health trackers to scanning QR codes, recognizing images, and chatting live with friends, students gain practical, hands-on experience while having fun. This course blends creativity, logic, and real-world application, empowering kids to build innovative apps that solve real problems—all while nurturing their love for technology.',
    activities: '50+',
    modules: 6,
    lessons: 36,
    quizzes: 36,
    certificate: 'Thunkable expert certificates',
    firstFoldTable: [
      {
        icon: (
          <>
            <BarChart
              className="text-orange-600 xl:inline w-5 xl:mb-1 xl:pr-1"
              height={16}
            />
          </>
        ),
        title: 'Eligibility',
        description: 'Grade 4-8',
      },
      {
        icon: (
          <>
            <MonitorWithInstructor className="text-orange-600 xl:inline w-4 h-4 xl:pr-1" />
          </>
        ),
        title: 'Program Delivery',
        description: 'Online',
      },
      {
        icon: (
          <>
            <FileWithBadge className="text-orange-600 xl:inline w-4 h-4 xl:pr-1" />
          </>
        ),
        title: 'Certification by',
        description: 'STEM.org',
      },
      {
        icon: (
          <>
            <Clock
              className="text-orange-600 xl:inline w-5 mb-1 xl:pr-1"
              height={16}
            />{' '}
          </>
        ),
        title: 'Duration',
        description: '2-4 Months',
      },
      {
        icon: (
          <>
            <FileText
              className="text-orange-600 xl:inline w-5 mb-1 xl:pr-1"
              height={16}
            />
          </>
        ),
        title: 'Program',
        description: '50+ activities & projects',
      },
    ],
  },
  JAVA: {
    name:
      'Learn Java with the Top Coding Course Instructors  for Kids and Teens',
    description:
      'Embark on an exciting journey into the world of Java with Codingal, where kids and teens learn from top coding instructors through a structured, engaging curriculum designed to build a strong foundation in programming. Starting with core concepts like data types, operators, and control structures, your child will gradually progress to mastering loops, arrays, functions, and even advanced topics like recursion and algorithmic thinking. Every lesson is carefully crafted to boost their confidence, spark creativity, and enhance problem-solving skills through interactive coding challenges and real-world projects. What your child will learn includes mastering core programming concepts, developing essential problem-solving tools, and applying advanced logic techniques—all while having fun and staying engaged. Whether your child is just starting out or looking to take their skills to the next level, learning Java at Codingal sets them up for success not just in tech, but in any future career that values analytical thinking and creativity.',
    activities: '50+',
    modules: 8,
    quizzes: 0,
    certificate: 'Java expert certificates',
    firstFoldTable: [
      {
        icon: (
          <>
            <BarChart
              className="text-orange-600 xl:inline w-5 xl:mb-1 xl:pr-1"
              height={16}
            />
          </>
        ),
        title: 'Eligibility',
        description: 'Grade 8-12',
      },
      {
        icon: (
          <>
            <MonitorWithInstructor className="text-orange-600 xl:inline w-4 h-4 xl:pr-1" />
          </>
        ),
        title: 'Program Delivery',
        description: 'Online',
      },
      {
        icon: (
          <>
            <FileWithBadge className="text-orange-600 xl:inline w-4 h-4 xl:pr-1" />
          </>
        ),
        title: 'Certification by',
        description: 'STEM.org',
      },
      {
        icon: (
          <>
            <Clock
              className="text-orange-600 xl:inline w-5 mb-1 xl:pr-1"
              height={16}
            />{' '}
          </>
        ),
        title: 'Duration',
        description: '2-4 Months',
      },
      {
        icon: (
          <>
            <FileText
              className="text-orange-600 xl:inline w-5 mb-1 xl:pr-1"
              height={16}
            />
          </>
        ),
        title: 'Program',
        description: '50+ activities & projects',
      },
    ],
  },
  TRINKET: {
    name: 'Top Coding Class Instructors for Kids and Teens to Learn Trinket',
    description:
      'Trinket is like a magical playground where kids can bring their ideas to life through code. Using simple blocks and Python’s Turtle module, they’ll create drawings, fun animations, and even their own games—just like real programmers! Whether they’re designing a house, a caterpillar, or colorful shapes, kids will see their creations instantly come alive on screen. Trinket is completely browser-based and easy to use, making it perfect for beginners. As they explore, kids build a strong foundation in Python while developing creativity, logic, and problem-solving skills in a fun, visual, and interactive way.',
    activities: '10+',
    modules: 1,
    lessons: 6,
    quizzes: 6,
    certificate: 'Trinket expert certificates',
    firstFoldTable: [
      {
        icon: (
          <>
            <BarChart
              className="text-orange-600 xl:inline w-5 xl:mb-1 xl:pr-1"
              height={16}
            />
          </>
        ),
        title: 'Eligibility',
        description: 'Grade 1-3',
      },
      {
        icon: (
          <>
            <MonitorWithInstructor className="text-orange-600 xl:inline w-4 h-4 xl:pr-1" />
          </>
        ),
        title: 'Program Delivery',
        description: 'Online',
      },
      {
        icon: (
          <>
            <FileWithBadge className="text-orange-600 xl:inline w-4 h-4 xl:pr-1" />
          </>
        ),
        title: 'Certification by',
        description: 'STEM.org',
      },
      {
        icon: (
          <>
            <Clock
              className="text-orange-600 xl:inline w-5 mb-1 xl:pr-1"
              height={16}
            />{' '}
          </>
        ),
        title: 'Duration',
        description: '1 Month',
      },
      {
        icon: (
          <>
            <FileText
              className="text-orange-600 xl:inline w-5 mb-1 xl:pr-1"
              height={16}
            />
          </>
        ),
        title: 'Program',
        description: '10+ activities & projects',
      },
    ],
  },
  PYGAME: {
    name: 'Top Coding Class Instructors for Kids and Teens to Learn Pygame',
    description:
      "Pygame is a game development library for the Python programming language, designed to make learning to code both fun and accessible. It provides a collection of Python modules that simplify the process of creating games. With Pygame's easy-to-use toolkit, your child can build vibrant games by designing levels, implementing game logic, and bringing their imagination to life. Pygame allows students to create stunning visuals, add captivating sounds, and develop interactive experiences. By learning Pygame at Codingal, your child will not only master coding skills but also develop problem-solving, creativity, and critical thinking. Give your child an exciting head start in the world of game development with Codingal, where they’ll create their own games while having fun and learning valuable coding skills!",
    activities: '50+',
    modules: 1,
    lessons: 6,
    quizzes: 6,
    certificate: 'Pygame expert certificates',
    firstFoldTable: [
      {
        icon: (
          <>
            <BarChart
              className="text-orange-600 xl:inline w-5 xl:mb-1 xl:pr-1"
              height={16}
            />
          </>
        ),
        title: 'Eligibility',
        description: 'Grade 4-8',
      },
      {
        icon: (
          <>
            <MonitorWithInstructor className="text-orange-600 xl:inline w-4 h-4 xl:pr-1" />
          </>
        ),
        title: 'Program Delivery',
        description: 'Online',
      },
      {
        icon: (
          <>
            <FileWithBadge className="text-orange-600 xl:inline w-4 h-4 xl:pr-1" />
          </>
        ),
        title: 'Certification by',
        description: 'STEM.org',
      },
      {
        icon: (
          <>
            <Clock
              className="text-orange-600 xl:inline w-5 mb-1 xl:pr-1"
              height={16}
            />{' '}
          </>
        ),
        title: 'Duration',
        description: '2-4 Months',
      },
      {
        icon: (
          <>
            <FileText
              className="text-orange-600 xl:inline w-5 mb-1 xl:pr-1"
              height={16}
            />
          </>
        ),
        title: 'Program',
        description: '50+ activities & projects',
      },
    ],
  },
  ANIMATION: {
    name: 'Top Coding Class Instructors for Kids and Teens to Learn Animation',
    description:
      "Step into the world of engaging animations with the Codingal curriculum, where creativity meets coding! Using platforms like Sprite Lab, Play Lab, Game Lab, and Scratch, students transform their ideas into dynamic animations and interactive stories. Whether it's making characters groove in a Dance Party, orchestrating a hilarious Banana Chase, or animating a thrilling Space Adventure, these projects teach essential programming concepts like loops, events, and conditions—all through fun, hands-on activities. Students create interactive animations like a dazzling Fireworks Show, a whimsical Underwater Scene, or even a quirky Monster Dance-Off. By animating sprites, syncing movements to music, and designing custom effects, they bring their imagination to life while learning coding fundamentals. With every project, learners develop problem-solving skills and discover the magic of animation, making each session an exciting journey of creativity and innovation! Moving on as students transition to text-based coding, they also explore basic  Animation concepts that are commonly used  in websites using HTML CSS and basic 2D animations using Pygame while they learn  Python.",
    activities: '100+',
    quizzes: 36,
    certificate: 'Animation expert certificates',
    firstFoldTable: [
      {
        icon: (
          <>
            <BarChart
              className="text-orange-600 xl:inline w-5 xl:mb-1 xl:pr-1"
              height={16}
            />
          </>
        ),
        title: 'Eligibility',
        description: 'Grade 1-12',
      },
      {
        icon: (
          <>
            <MonitorWithInstructor className="text-orange-600 xl:inline w-4 h-4 xl:pr-1" />
          </>
        ),
        title: 'Program Delivery',
        description: 'Online',
      },
      {
        icon: (
          <>
            <FileWithBadge className="text-orange-600 xl:inline w-4 h-4 xl:pr-1" />
          </>
        ),
        title: 'Certification by',
        description: 'STEM.org',
      },
      {
        icon: (
          <>
            <Clock
              className="text-orange-600 xl:inline w-5 mb-1 xl:pr-1"
              height={16}
            />{' '}
          </>
        ),
        title: 'Duration',
        description: '1:1 45 mins class',
      },
      {
        icon: (
          <>
            <FileText
              className="text-orange-600 xl:inline w-5 mb-1 xl:pr-1"
              height={16}
            />
          </>
        ),
        title: 'Program',
        description: '100+ activities & projects',
      },
    ],
  },
  TURTLE: {
    name:
      'Learn Turtle with the Top Coding Course Instructors for Kids and Teens',
    description:
      "Dive into the world of Python Turtle Graphics and unlock your child's potential to create amazing graphics and designs! At Codingal, we use Turtle as a fun and engaging way to teach Python programming to kids and teens. From drawing simple shapes to designing complex patterns, Turtle helps young learners grasp essential coding concepts like loops, functions, and conditionals—all while fostering creativity. With Turtle graphics, students can create everything from geometric patterns to interactive designs, bringing their ideas to life through code.",
    activities: '100+',
    displayName: 'Turtle (Python)',
    firstFoldTable: [
      {
        icon: (
          <>
            <BarChart
              className="text-orange-600 xl:inline w-5 xl:mb-1 xl:pr-1"
              height={16}
            />
          </>
        ),
        title: 'Eligibility',
        description: 'Grade 1-12',
      },
      {
        icon: (
          <>
            <MonitorWithInstructor className="text-orange-600 xl:inline w-4 h-4 xl:pr-1" />
          </>
        ),
        title: 'Program Delivery',
        description: 'Online',
      },
      {
        icon: (
          <>
            <FileWithBadge className="text-orange-600 xl:inline w-4 h-4 xl:pr-1" />
          </>
        ),
        title: 'Certification by',
        description: 'STEM.org',
      },
      {
        icon: (
          <>
            <Clock
              className="text-orange-600 xl:inline w-5 mb-1 xl:pr-1"
              height={16}
            />{' '}
          </>
        ),
        title: 'Duration',
        description: '45-60 mins each',
      },
      {
        icon: (
          <>
            <FileText
              className="text-orange-600 xl:inline w-5 mb-1 xl:pr-1"
              height={16}
            />
          </>
        ),
        title: 'Program',
        description: '100+ activities & projects',
      },
    ],
  },
  MIT_APP_INVENTOR: {
    name:
      'Learn MIT App Inventor with the Top Coding Course Instructors for Kids and Teens',
    description:
      'Want your child to go beyond just using apps? With MIT App Inventor, they’ll learn how to build them. Codingal’s MIT App Inventor course for kids and teens introduces the fundamentals of mobile app development using a drag-and-drop coding environment, powered by real programming logic. It’s the perfect platform for beginners—and with our expert instructors, your child will create real apps while gaining essential AI & coding skills that will prepare them for the digital future.',
    activities: '100+',
    displayName: 'MIT App Inventor',
    firstFoldTable: [
      {
        icon: (
          <>
            <BarChart
              className="text-orange-600 xl:inline w-5 xl:mb-1 xl:pr-1"
              height={16}
            />
          </>
        ),
        title: 'Eligibility',
        description: 'Grade 1-12',
      },
      {
        icon: (
          <>
            <MonitorWithInstructor className="text-orange-600 xl:inline w-4 h-4 xl:pr-1" />
          </>
        ),
        title: 'Program Delivery',
        description: 'Online',
      },
      {
        icon: (
          <>
            <FileWithBadge className="text-orange-600 xl:inline w-4 h-4 xl:pr-1" />
          </>
        ),
        title: 'Certification by',
        description: 'STEM.org',
      },
      {
        icon: (
          <>
            <Clock
              className="text-orange-600 xl:inline w-5 mb-1 xl:pr-1"
              height={16}
            />{' '}
          </>
        ),
        title: 'Duration',
        description: '45-60 mins each',
      },
      {
        icon: (
          <>
            <FileText
              className="text-orange-600 xl:inline w-5 mb-1 xl:pr-1"
              height={16}
            />
          </>
        ),
        title: 'Program',
        description: '100+ activities & projects',
      },
    ],
  },
  BOOTSTRAP: {
    name:
      'Learn Bootstrap with the Top Coding Course Instructors for Kids and Teens',
    description:
      'Give your child the tools to build their very own websites—just like professional developers! As part of our web development curriculum, students will learn how to use Bootstrap, a popular framework for designing responsive websites that work seamlessly across phones, tablets, and computers. Led by expert teachers, this course introduces kids and teens to Bootstrap and other essential web development concepts. With step-by-step guidance, students learn to create real web pages, customize their designs, and use Bootstrap’s pre-designed components to build interactive and visually appealing websites quickly. This hands-on approach helps students gain confidence in web development while using a professional tool widely used.',
    activities: '100+',
    displayName: 'Bootstrap',
    firstFoldTable: [
      {
        icon: (
          <>
            <BarChart
              className="text-orange-600 xl:inline w-5 xl:mb-1 xl:pr-1"
              height={16}
            />
          </>
        ),
        title: 'Eligibility',
        description: 'Grade 1-12',
      },
      {
        icon: (
          <>
            <MonitorWithInstructor className="text-orange-600 xl:inline w-4 h-4 xl:pr-1" />
          </>
        ),
        title: 'Program Delivery',
        description: 'Online',
      },
      {
        icon: (
          <>
            <FileWithBadge className="text-orange-600 xl:inline w-4 h-4 xl:pr-1" />
          </>
        ),
        title: 'Certification by',
        description: 'STEM.org',
      },
      {
        icon: (
          <>
            <Clock
              className="text-orange-600 xl:inline w-5 mb-1 xl:pr-1"
              height={16}
            />{' '}
          </>
        ),
        title: 'Duration',
        description: '45-60 mins each',
      },
      {
        icon: (
          <>
            <FileText
              className="text-orange-600 xl:inline w-5 mb-1 xl:pr-1"
              height={16}
            />
          </>
        ),
        title: 'Program',
        description: '100+ activities & projects',
      },
    ],
  },
}

export const COURSE_DESCRIPTION_FOLD: Record<
  CourseNameType,
  CourseDescriptionFoldType
> = {
  SCRATCH_PROGRAMMING: {
    title: 'What is Scratch Coding?',
    expandedDescription: (
      <>
        <p>
          Scratch is a free block-based programming language designed primarily
          for kids.
        </p>
        <p>
          It is a free educational website developed by MIT that provides a
          great way to learn to code early.
        </p>
        <p>
          Scratch programming is a visual programming language in which you have
          to drag and drop blocks of code and attach them in a way you connect a
          jigsaw puzzle. It can be used online or offline on your system.
        </p>
      </>
    ),
    readMoreLink:
      'https://www.codingal.com/coding-for-kids/blog/introduction-to-scratch-coding/',
  },
  HPE_CODEWARS_HACKATHON: {
    title: 'What is HPE CodeWars Hackathon?',
    expandedDescription: (
      <p>
        This is an online coding competition hosted by HPE for Grade 8-12
        students. The students can team up with friends to solve real problems
        using code. They can ideate, create an application, game, or website
        around a theme.
      </p>
    ),
  },
  HPE_CODEWARS_CODE_BATTLE: {
    title: 'What is HPE CodeWars Code Battle?',
    expandedDescription: (
      <p>
        This is a virtual programming challenge hosted by HPE for Grade 8-12
        students. The students will solve coding problems using programming
        language like C, C++, Java & Python.
      </p>
    ),
  },
  PYTHON_FOR_KIDS: {
    title: 'What is Python for Kids and Teens?',
    description:
      'Python is a text-based programming language suitable for kids to learn. It is one of the most popular programming languages. Python is used to develop apps and software. For example, YouTube and Instagram are built using Python.',
    expandedDescription: (
      <>
        <p>
          Python is used for web development, game development, building apps,
          machine learning, and more.{' '}
          <strong>Python Classes For Kids and Teens</strong> will quickly
          introduce them to principles of coding concepts and boost their
          analytical skills
        </p>
        <p>
          Python programming teaches kidsand teens concepts of variables,
          keywords, operators, loops, if-else statements, conditional
          statements, functions, recursion, exception handling, list,
          dictionary, data structures, object-oriented programming, and more. In
          this course, students will also learn to build a game and GUI from
          scratch using Python.
        </p>
        <p>
          Python programming will help kids and teens to get a grip on the
          basics of programming. Then, as kids explore what Python coding
          offers, they can learn various coding skills, from problem-solving to
          analytical thinking, that will grow with them on their coding journey
          beyond Python. It breaks coding into more easily grasped chunks so
          children can build their understanding while actively building actual
          code. It also helps the kids and teens to solve real-world problems
          using data structures and object-oriented programming in Python.
        </p>
      </>
    ),
  },
  ROBLOX: {
    title: 'What is Roblox?',
    description:
      'Roblox is a widely popular online gaming platform for kids to create and play games. Roblox is not a game; it’s a platform on which people play games created by other developers. What makes Roblox different is all the games on Roblox are made by its users. Now it depends on the creativity and imagination of the user on what kind of game they want to build.',
    expandedDescription: (
      <>
        <p>
          Games on Roblox are called ‘experiences’. Kids can play ‘experiences’
          developed by users across the globe.
        </p>
      </>
    ),
  },
  AP_COMPUTER_SCIENCE_A: {
    title: 'What is AP Computer Science A?',
    description:
      'AP in AP Computer Science A stands for Advanced Placement. It‘s an introductory college-level course. It covers java programming fundamentals, data structures and algorithms, strings and arrays and object-oriented programming.',
    expandedDescription: (
      <>
        <p>
          The AP Computer Science A course & exam is based on the java focused
          computing fundamentals. Students can understand their coding skills by
          analysing the logic and writing style and through testing code by
          understanding control flow and syntaxes. AP Computer Science A exam
          covered both multiple-choice and free-response questions.
        </p>
      </>
    ),
  },
  AI_AND_ML: {
    title: 'What is AI for kids?',
    description:
      'AI, or Artificial Intelligence, creates machines that think and learn like humans. It processes information, makes decisions, and adapts to new situations—just like our brains.',
    expandedDescription: (
      <>
        <p>
          In our AI Genius for Kids and AI Expert for Teens courses, we dive
          into how AI, a branch of computer science, performs tasks that require
          human intelligence: reasoning, recognizing shapes, understanding
          speech and images, playing games, and even driving cars!
        </p>
        <p>
          AI is everywhere—voice assistants like Siri and Alexa uses Machine
          Learning & Natural Language Processing to understand commands.
          Self-driving cars, like Tesla, use intelligent algorithms to navigate
          on their own!
        </p>
        <p>
          From autocorrect to YouTube recommendations, Instagram Reels, ChatGPT,
          and more, AI shapes our everyday lives. Join us to discover how it all
          works and its impact on the world.
        </p>
      </>
    ),
  },
  THUNKABLE: {
    title: 'What is Thunkable for kids?',
    description:
      'The Application Development for Kids Course at Codingal takes students on an exciting journey from building simple apps to creating their own cool, fully functional AI powered projects!',
    expandedDescription: (
      <>
        <p>
          In this course, students begin by creating fun, beginner-friendly apps
          like Health Trackers and Doodling Boards. They&#39;ll quickly level up
          to integrate cool AI features such as Text-to-Speech, Speech-to-Text,
          and even build their own language translation apps!
        </p>
        <p>
          As they move through the Intermediate and Advanced modules, students
          will develop games, weather apps, and messaging tools, while learning
          how to add interactive features like voice recognition and virtual
          assistants.
        </p>
        <p>
          By the end of the course, students will complete their Capstone
          Project, applying everything they&#39;ve learned— from basic app
          design to advanced AI concepts— to create something truly unique and
          innovative. With every step, they’ll explore the exciting world of AI,
          all while building apps that have real-world impact.
        </p>
      </>
    ),
  },
  JAVA: {
    title: 'What is Java for kids?',
    description:
      'Java is a powerful computer programming language that lets us create everything from mobile apps to video games! Think of it as a set of building blocks you can use to design and bring your digital ideas to life—big or small. It’s a cornerstone of computer science, helping us build interactive programs, manage data, and even power popular games like Minecraft.',
    expandedDescription: (
      <>
        <p>
          Java is everywhere—Android apps run on Java, and many websites depend
          on it for smooth and secure functionality. Whether you’re writing
          simple programs or running massive servers, Java’s reliable
          performance makes it stand out.
        </p>
        <p>
          From learning the basics and writing your first lines of code to
          building exciting projects and games, Java opens up a world of
          possibilities. Discover how Java shapes our tech-driven lives—and how
          you can become a Java coding master!
        </p>
      </>
    ),
  },
  TRINKET: {
    title: 'What is Trinket for kids?',
    description:
      'Trinket for kids is an exciting online platform where young learners can start coding right in their browser—no need to install anything! It’s a fun digital playground where you can create games, animations, and interactive projects using simple programming languages like Python. With Trinket, kids can dive into coding in a hands-on way, making it perfect for beginners to explore and learn at their own pace.',
    expandedDescription: (
      <>
        <p>
          The platform’s user-friendly design helps students see their code come
          to life and build confidence as they experiment with their projects.
        </p>
        <p>
          Trinket encourages creativity by letting students tackle challenges
          and create whatever they imagine. From games to interactive art, the
          possibilities are endless, fostering problem-solving and logical
          thinking.
        </p>
        <p>
          At Codingal, we support students every step of the way. Whether
          you&#39;re a beginner or have some experience, Trinket is the perfect
          place to dive into coding, have fun, and bring your ideas to life!
        </p>
      </>
    ),
  },
  PYGAME: {
    title: 'What is Pygame for kids?',
    description:
      'The Application Development for Kids Course at Codingal takes students on an exciting journey from building simple apps to creating their own cool, fully functional AI powered projects!',
    expandedDescription: (
      <>
        <p>
          In this course, students begin by creating fun, beginner-friendly apps
          like Health Trackers and Doodling Boards. They&#39;ll quickly level up
          to integrate cool AI features such as Text-to-Speech, Speech-to-Text,
          and even build their own language translation apps!
        </p>
        <p>
          As they move through the Intermediate and Advanced modules, students
          will develop games, weather apps, and messaging tools, while learning
          how to add interactive features like voice recognition and virtual
          assistants.
        </p>
        <p>
          By the end of the course, students will complete their Capstone
          Project, applying everything they&#39;ve learned— from basic app
          design to advanced AI concepts— to create something truly unique and
          innovative. With every step, they’ll explore the exciting world of AI,
          all while building apps that have real-world impact.
        </p>
      </>
    ),
  },

  ANIMATION: {
    title: 'What is Animation for Kids and Teens?',
    description:
      'Animation is like a magical tool that brings pictures to life, making them move, talk, and do exciting things. It’s what makes games and cartoons so fun and full of action! For example, in Minecraft, animation helps your characters run, build, and explore. In Fortnite, it’s what makes your avatars dance and glide across the screen. Even in Angry Birds, the way the birds fly and crash into towers is all thanks to animation!',
    expandedDescription: (
      <>
        <p>
          Cartoons also use animation to tell amazing stories. Think about Tom
          and Jerry, where Tom chases Jerry with funny movements, or SpongeBob
          SquarePants, where SpongeBob laughs and flips burgers under the sea.
          Even movies like Frozen, where Elsa creates icy magic, or The Lion
          King, where Simba roars on Pride Rock, are brought to life with
          animation. Animation turns simple drawings into characters we love and
          makes every story exciting to watch!
        </p>
        <p>
          In our text‒based programming curriculum, you’ll dive into the
          exciting world of animation and learn how to create some seriously
          cool effects! Imagine this: every time you visit a website, you see
          buttons that change color when you hover over them, cards that grow
          bigger or glow with shadows, and even interactive games that respond
          to your clicks!
        </p>
        <p>
          These effects are all made possible by animation, and you’ll learn how
          to create them yourself. Whether it&#39;s making characters jump,
          objects spin, or text change colors, animation lets you bring your
          ideas to life on the screen. You’ll start by learning simple animation
          tricks and gradually build up to making your own animated games and
          interactive websites. Get ready to turn your imagination into reality
          with animation!
        </p>
      </>
    ),
  },
}

export const SKILLS_YOU_WILL_GAIN: Record<
  CourseNameType,
  SkillsYouWillGainFoldType
> = {
  SCRATCH_PROGRAMMING: {
    title:
      'The best place for kids and teens to learn Scratch basics and fundamental',
    description:
      'Learn the basic principles of coding using Scratch, start using events, coordinates and sprites, and create your own programs using code blocks.',
    skills: [
      'Learn the foundation of Computer Science using Scratch programming',
      'Become an expert in Scratch programming',
      'Learn to solve problems by creating app, games, websites',
      'Develop creative thinking to solve the problems',
      'Improve analytical thinking while creating your projects on Scratch',
      'Create engaging games, animation & apps with block based programming',
    ],
  },
  HPE_CODEWARS_HACKATHON: {
    skills: [
      'Website building principles',
      'Creative and design thinking',
      'HTML, CSS and Bootstrap',
      'Python and Python Flask',
      'JavaScript, SQL and NodeJS',
      'Website hosting',
    ],
  },
  HPE_CODEWARS_CODE_BATTLE: {
    skills: [
      'Algorithm skills and techniques',
      'Data science skills and techniques',
      'Computational thinking',
      'Problem solving',
      'Competitive coding skills',
      'Python development',
    ],
  },
  PYTHON_FOR_KIDS: {
    title:
      'The best place for kids and teens to learn Python basics and fundamentals',
    description:
      'Learn basics of programming, code interactive apps and games with this intensive online Python for Kids course.',
    skills: [
      'Learn the foundation of Computer Science using Python programming',
      'Become an expert in Python programming',
      'Learn to solve problems by creating app, games, websites',
      'Develop creative thinking to solve the problems',
      'Improve analytical thinking while creating your projects on Python',
      'Create engaging games, animation & apps with Python programming',
    ],
  },
  ROBLOX: {
    title: 'The best place for kids to learn the basics and fundamentals',
    description:
      'Learn basics of programming, code interactive 3D games with this intensive online Roblox course.',
    skills: [
      'Learn the foundation of Computer Science using Lua programming',
      'Become an expert in Roblox',
      'Learn to solve problems by creating games',
      'Develop creative thinking to solve the problems',
      'Improve analytical thinking while creating your projects on Roblox',
      'Create engaging 3D games with Roblox',
    ],
  },
  AP_COMPUTER_SCIENCE_A: {
    title: 'The best place for kids to learn the basics and fundamentals',
    description:
      'Learn fundamentals of Java programming and topics relevant to AP Computer Science A course and exam.',
    skills: [
      'Learn the foundation of Java',
      'Become an expert in AP Computer Science A',
      'Learn to solve the Free Response Question',
      'Develop creative thinking to solve the problems',
      'Improve analytical thinking',
    ],
  },
}

export const ACTIVITIES: Record<CourseNameType, ActivitiesType> = {
  SCRATCH_PROGRAMMING: {
    title: '40+ Fun activities and lessons',
    activities: [
      {
        iframeURL: 'https://scratch.mit.edu/projects/601010296/embed',
        activityName: 'Dance Party with Motu Patlu',
        activityDescription:
          'Dance with your favorite cartoon character  while learning Scratch.',
        altText: 'Dance Party with Motu Patlu on Scratch',
        activityImage: 'scratch-programming/dance-party-with-motu-patlu',
      },
      {
        iframeURL: 'https://scratch.mit.edu/projects/617242446/embed',
        activityName: 'Mario Dash',
        altText: 'Mario Dash using Scratch platform',
        activityDescription:
          'Create your own version of the popular game Super Mario and score points for avoiding obstacles in your path.',
        activityImage: 'scratch-programming/mario-dash-v1',
      },
      {
        iframeURL: 'https://scratch.mit.edu/projects/617240132/embed',
        activityName: 'Ping Pong Game',
        altText: 'Ping Pong game on Scratch',
        activityDescription:
          'Play your favorite ping pong game while learning Scratch.',
        activityImage: 'scratch-programming/ping-pong-game',
      },
      {
        iframeURL: 'https://scratch.mit.edu/projects/617242052/embed',
        activityName: 'Integer Game',
        altText: 'Integer Game',
        activityDescription:
          'Create a game to learn about integers using a number line.',
        activityImage: 'scratch-programming/integer-game-v1',
      },
      {
        iframeURL: 'https://scratch.mit.edu/projects/587229635/embed',
        activityName: 'Card Creator',
        altText: 'Card Creator on Scratch',
        activityDescription:
          'Create cards for any ocassion while learning Scratch.',
        activityImage: 'scratch-programming/card-creator',
      },
      {
        iframeURL: 'https://scratch.mit.edu/projects/599455896/embed',
        activityName: 'Space Animation',
        altText: 'Space animation on Scratch',
        activityDescription:
          'Dive in to the infinite space with Space animation while learning Scratch.',
        activityImage: 'scratch-programming/space-animation',
      },
    ],
  },
  HPE_CODEWARS_HACKATHON: {
    title: 'Activities to master Hackathon',
    activities: [
      {
        activityName: 'Carousels',
        activityDescription:
          'Create a carousel to display multiple images that users can scroll through and to make your website more interactive.',
        activityImage: 'hpe-codewars-hackathon/activities/game-1',
        altText: 'Carousels',
      },
      {
        activityName: 'Box model',
        activityDescription:
          'Create multiple sections to display different kinds of content and to make your website easy to read and navigate.',
        activityImage: 'hpe-codewars-hackathon/activities/game-2',
        altText: 'Box model',
      },
      {
        activityName: 'Containers',
        activityDescription:
          'Create colorful boxes to give your website a bold, sprightly look and to make the content on your website stand out.',
        activityImage: 'hpe-codewars-hackathon/activities/game-3',
        altText: 'Containers',
      },
    ],
  },
  HPE_CODEWARS_CODE_BATTLE: {
    title: 'Activities to master the Code Battle',
    activities: [
      {
        activityName: 'Two odd occurring numbers',
        activityDescription:
          'If you are given a list of numbers in which all numbers occur at even times except two, write a program to find those two numbers.',
        activityImage: 'hpe-codewars-code-battle/activities/game-1',
        altText: 'Two odd occurring numbers',
      },
      {
        activityName: 'Generate all substrings of a given string',
        activityDescription:
          'Write a program that generates all the possible substrings from any string entered by the user.',
        activityImage: 'hpe-codewars-code-battle/activities/game-2',
        altText: 'Generate all substrings of a given string',
      },
      {
        activityName: 'Swap two numbers without using third variable',
        activityDescription:
          'Write a program to swap two numbers without using a third variable using Bitwise operators.',
        activityImage: 'hpe-codewars-code-battle/activities/game-3',
        altText: 'Swap two numbers without using third variable',
      },
    ],
  },
  PYTHON_FOR_KIDS: {
    title:
      '90+ Fun activities and lessons for kids to learn Python programming',
    activities: [
      {
        activityName: 'Space Invader game',
        activityDescription:
          'Learn to create an exciting video game that can help the player shoot the space invaders and gain points using the Python library Pygame.',
        activityImage: 'python-for-kids/activities/game-1',
        altText: 'Space Invader game',
        iframeURL:
          'https://replit.com/@Codingal/Space-Invader-Game?v=1&embed=true',
      },
      {
        activityName: 'Diamond Pattern',
        activityDescription:
          'Learn how to create a diamond shaped pattern using nested loops in Python.',
        activityImage: 'python-for-kids/activities/game-2',
        altText: 'Diamond Pattern',
        iframeURL: 'https://replit.com/@Codingal/L5BF1?v=1&embed=true',
      },
      {
        activityName: 'Denomination Calculator',
        activityDescription:
          'Create a multi window GUI application that can help the user to calculate the total number of denominations of 2000, 500 and 100 for amount entered, by using the Python library Tkinter.',
        activityImage: 'python-for-kids/activities/game-3',
        altText: 'Denomination Calculator',
        iframeURL:
          'https://replit.com/@Codingal/Denomination-Counter-Application?v=1&embed=true',
      },
      {
        activityName: 'Restaurant Management System',
        activityDescription:
          'Create a Restaurant Management System to bill and track orders using the Python library Tkinter.',
        activityImage: 'python-for-kids/activities/game-4',
        altText: 'Restaurant Management System',
        iframeURL: 'https://replit.com/@Codingal/SPCM7L6A1?v=1&embed=true',
      },
      {
        activityName: 'Text Editor',
        activityDescription:
          'Build text editor to save and open your files using the Python library Tkinter.',
        activityImage: 'python-for-kids/activities/game-5',
        altText: 'Text Editor',
        iframeURL: 'https://replit.com/@Codingal/SPCM7L4A1?v=1&embed=true',
      },
      {
        activityName: 'Pygame',
        activityDescription:
          'Create exciting games you played in your childhood using the Pygame library in Python.',
        activityImage: 'python-for-kids/activities/game-6',
        altText: 'Pygame',
        iframeURL:
          'https://replit.com/@Codingal/Level-up-this-game?v=1&embed=true',
      },
    ],
  },
  ROBLOX: {
    title: '90+ Fun activities and lessons for kids to learn Roblox',
    activities: [
      {
        activityName: 'Blocker game',
        activityDescription:
          'Build a blocker game in which you can change the speed and height of the player.',
        activityImage: 'roblox/activities/game-1',
        altText: 'Blocker game',
      },
      {
        activityName: 'End game',
        activityDescription:
          'Learn to perform various tasks by making numerous obstacles including disappearing obstacles, lava kill blocks and obstacles of different shapes.',
        activityImage: 'roblox/activities/game-2',
        altText: 'End game',
      },
      {
        activityName: 'Scrambled legs',
        activityDescription:
          'In this game, you have to get through multiple levels to reach the final goal, but it won‘t be that easy! You will have high jumps to take, tough choices to make and race against time.',
        activityImage: 'roblox/activities/game-3',
        altText: 'Scrambled legs',
      },
      {
        activityName: 'Magical stairway',
        activityDescription:
          'In this game, you have to get through the stairway, but beware, they keep on disappearing.',
        activityImage: 'roblox/activities/game-4',
        altText: 'Magical stairway',
      },
    ],
  },
  AP_COMPUTER_SCIENCE_A: {
    title:
      '100+ Fun activities and lessons for teens to learn AP Computer Science A',
    activities: [
      {
        activityName: 'Hey Java',
        activityDescription:
          'Creating the First program in Java. i.e., Hello Java',
        activityImage: 'ap-computer-science-a/activities/game-1',
        altText: 'Hey Java',
      },
      {
        activityName: 'Intro to Data Types',
        activityDescription: 'In this program, you will learn data types',
        activityImage: 'ap-computer-science-a/activities/game-2',
        altText: 'Intro to Data Types',
      },
      {
        activityName: 'Inheritance in one shot',
        activityDescription:
          'In this program, you will learn the concept of inheritance.',
        activityImage: 'ap-computer-science-a/activities/game-3',
        altText: 'Inheritance in one shot',
      },
      {
        activityName: 'Method Overloading',
        activityDescription:
          'In this activity, you will learn about method overloading in Java.',
        activityImage: 'ap-computer-science-a/activities/game-4',
        altText: 'Method Overloading',
      },
    ],
  },

  AI_AND_ML: {
    title: 'Fun trial lessons for kids & teens to learn AI & Machine Learning',
    description:
      'Our curriculum team invites your child to a fun coding trial lesson! Choose from six exciting activities for beginners and advanced learners. In a 45-minute 1:1 class, they’ll create a live project. Sign up for a free lesson today to kickstart their coding and AI journey!',
    activities: [
      {
        activityName: 'AI Taco Chomp Activity',
        activityDescription: `Build a fun AI-powered game where tacos fall from the sky, and players use real-time face detection to 'chomp' and eat them before they hit the ground!`,
        activityImage: 'ai-taco',
        altText: 'AI Taco Chomp Activity',
        videoURL:
          'https://codingal.s3.ap-south-1.amazonaws.com/web/videos/AI_Taco_Chomp.mp4',
      },
      {
        activityName: 'Petsona',
        activityDescription:
          'Students make a project where they enter their name, and ChatGPT creates a unique imaginary pet that reflects their personality traits',
        activityImage: 'petsona',
        altText: 'Petsona Activity',
        videoURL:
          'https://codingal.s3.ap-south-1.amazonaws.com/web/videos/Petsona.mp4',
      },
      {
        activityName: 'Transify',
        activityDescription:
          'Learners will create a fun translator activity in Scratch that converts text from one language to another and reads it aloud using text-to-speech',
        activityImage: 'transify',
        altText: 'Transify',
        videoURL:
          'https://codingal.s3.ap-south-1.amazonaws.com/web/videos/Transify.mp4',
      },
      {
        activityName: 'AI Mood Detector',
        activityDescription:
          'In this activity, students will use TextBlob, a simple NLP library in Python, to detect the sentiment of any sentence they enter.',
        activityImage: 'ai-mood-detector',
        altText: 'AI Mood Detector',
        videoURL:
          'https://codingal.s3.ap-south-1.amazonaws.com/web/videos/AI_Mood_Detector.mp4',
      },
      {
        activityName: 'AI Art Buddy',
        activityDescription:
          'In this Activity you will learn to use requests & PIL libraries to connect to an AI-Powered Image generation API',
        activityImage: 'ai-art-buddy',
        altText: 'AI Art Buddy',
        videoURL:
          'https://codingal.s3.ap-south-1.amazonaws.com/web/videos/AI_Art_Buddy.mp4',
      },
      {
        activityName: 'AI GuessMaster',
        activityDescription:
          'Build an exciting AI-powered game that guesses your secret number based on your hints and feedback',
        activityImage: 'ai-guessmaster',
        altText: 'AI GuessMaster',
        videoURL:
          'https://codingal.s3.ap-south-1.amazonaws.com/web/videos/AI_GuessMaster.mp4',
      },
    ],
    conclusion: (
      <>
        <p>
          For beginners, the{' '}
          <span className="font-600">
            AI Genius — ChatGPT, AI-powered Apps & Games
          </span>{' '}
          introduces students to the exciting world of AI through platforms like
          Scratch, Thunkable, and Pictoblox and so on. This hands-on course
          ensures that students build a project in every class, making learning
          interactive and fun. They’ll create AI-powered art and music, classify
          doodles, and build chatbots, stories, games, and apps while exploring
          concepts like sentiment analysis, sound analysis, image and text
          recognition.
        </p>
        <p>
          For more advanced learners, the{' '}
          <span className="font-600">
            AI Expert— Hands-On AI, LLMs & Python
          </span>{' '}
          course offers a project-based experience. Students use LLMs for
          text-to-image, image-to-text, and text generation, and work with LLMs
          available on Hugging Face and Google Gemini APIs to create chatbots,
          translators, and image recognition tools, learning prompt engineering
          along the way.
        </p>
        <p>
          Codingal provides a fun, gamified learning experience with expert
          teachers, making it the best coding school for kids and teens to dive
          into AI!
        </p>
      </>
    ),
  },

  THUNKABLE: {
    title: 'Fun activities and lessons for kids to learn Thunkable',
    description:
      'Our curriculum team invites your child to a fun coding trial lesson! Choose from six exciting activities for beginners and advanced learners. In a 45-minute 1:1 class, they’ll create a live project. Sign up for a free lesson today to kickstart their coding and AI journey!',
    activities: [
      {
        activityName: 'Swift Lingo',
        activityDescription: `Develop an AI-powered language translator app with integrated text-to-speech functionality on Thunkable.`,
        activityImage: 'swift-lingo',
        altText: 'Swift Lingo Activity',
        iframeURL:
          'https://iframe.mediadelivery.net/embed/239253/b4eae936-c62c-4916-82b9-96f9239a3b38?autoplay=true&loop=false&muted=false&preload=true&responsive=true',
      },
    ],
  },
  JAVA: {
    title: 'Fun activities and lessons for kids to learn Java',
    description:
      'At Codingal, learning Java is an adventure filled with engaging and creative projects that spark your child’s imagination and enhance their programming skills. Here are some of the fun activities your child will enjoy:',
    activities: [
      {
        activityName: 'Palindrome Checker',
        activityDescription: `Create a Java program to check if a word or phrase is a palindrome—ignoring spaces—and discover the exciting basics of Java programming!`,
        activityImage: 'palindrome-checker',
        altText: 'Palindrome Checker Activity',
        iframeURL:
          'https://iframe.mediadelivery.net/embed/239253/c35a0c95-a747-49e0-88c4-8fe513901731?autoplay=true&loop=false&muted=false&preload=true&responsive=true',
      },
      {
        activityName: 'Reverse String',
        activityDescription: `Master Java basics by reversing strings in our exciting trial class! Join now and start coding interactive solutions today!`,
        activityImage: 'reverse-string',
        altText: 'Reverse String Activity',
        iframeURL:
          'https://iframe.mediadelivery.net/embed/239253/0fc7d153-3582-4799-8793-6713abf11676?autoplay=true&loop=false&muted=false&preload=true&responsive=true',
      },
    ],
  },
  TRINKET: {
    title: 'Fun activities and lessons for kids to learn Trinket',
    description:
      'Our curriculum team invites your child to a fun coding trial lesson! Choose from six exciting activities for beginners and advanced learners. In a 45-minute free coding class, they’ll create a live project. Sign up for a free lesson today to kickstart their coding and AI journey!',
    activities: [
      {
        activityName: 'Drag & Drop Function',
        activityDescription: `Create an interactive experience where users can quickly move, copy, or reorder objects by dragging and dropping them on screen!`,
        activityImage: 'drag-and-drop-function',
        altText: 'Drag & Drop FunctionActivity',
        iframeURL:
          'https://iframe.mediadelivery.net/embed/239253/3c69c543-c2c4-4026-87e1-535c8fcbd439?autoplay=true&loop=false&muted=false&preload=true&responsive=true',
      },
      {
        activityName: 'Flashlight Application',
        activityDescription: `Turn your device into a powerful flashlight with a simple tap, providing instant light whenever you need it!`,
        activityImage: 'flashlight-application',
        altText: 'Flashlight Application Activity',
        iframeURL:
          'https://iframe.mediadelivery.net/embed/239253/43c29cac-9dc7-4850-8d9a-bf0ad1867c93?autoplay=true&loop=false&muted=false&preload=true&responsive=true',
      },
      {
        activityName: 'Dance Party with Spiderman',
        activityDescription: `Make Spider-Man groove to the beat by using simple actions and music in Scratch, creating your own fun dance routine!`,
        activityImage: 'dance-party-with-spiderman',
        altText: 'Dance Party with Spiderman',
        iframeURL:
          'https://iframe.mediadelivery.net/embed/239253/359bd39b-1375-4e84-a2fd-e797fbf43e27?autoplay=true&loop=false&muted=false&preload=true&responsive=true',
      },
    ],
  },
  PYGAME: {
    title: 'Fun activities and lessons for kids to learn Pygame',
    description:
      'Our curriculum team invites your child to a fun coding trial lesson! Choose from six exciting activities for beginners and advanced learners. In a 45-minute 1:1 class, they’ll create a live project. Sign up for a free lesson today to kickstart their coding and AI journey!',
    activities: [
      {
        activityName: 'Python Quiz',
        activityDescription: `Build a fun and interactive quiz game using Python.`,
        activityImage: 'python-quiz',
        altText: 'Palindrome Checker Activity',
        iframeURL:
          'https://iframe.mediadelivery.net/embed/239253/830dd4aa-0fb3-4d6a-8d8a-f10c5151da01?autoplay=true&loop=false&muted=false&preload=true&responsive=true',
      },
      {
        activityName: 'Basic Chatbot',
        activityDescription: `Build a simple chatbot using Python that greets users, asks about their feelings, and responds to their hobbies.`,
        activityImage: 'basic-chatbot',
        altText: 'Basic Chatbot Activity',
        iframeURL:
          'https://iframe.mediadelivery.net/embed/239253/f5ceb016-7924-43c5-b873-176df9d96366?autoplay=true&loop=false&muted=false&preload=true&responsive=true',
      },
      {
        activityName: 'Password Generator',
        activityDescription:
          'Create a Python tool to generate strong, random passwords based on user-defined length.',
        activityImage: 'password-generator',
        altText: 'Password Generator Activity',
        iframeURL:
          'https://iframe.mediadelivery.net/embed/239253/f01c46cf-7e6b-4367-bfa8-83347cfd3484?autoplay=true&loop=false&muted=false&preload=true&responsive=true',
      },
    ],
  },
  ANIMATION: {
    title: 'Fun activities and lessons for kids to learn Animation',
    description:
      'At Codingal, learning Animation is an adventure filled with engaging and creative projects that spark your child’s imagination and enhance their programming skills. Here are some of the fun activities your child will enjoy:',
    activities: [
      {
        activityName: 'Dance Party with Spiderman',
        activityDescription: `Make Spider-Man groove to the beat by using simple actions and music in Scratch, creating your own fun dance routine!`,
        activityImage: 'dance-party-with-spiderman',
        altText: 'Dance Party with Spiderman Activity',
        iframeURL:
          'https://iframe.mediadelivery.net/embed/239253/359bd39b-1375-4e84-a2fd-e797fbf43e27?autoplay=true&loop=false&muted=false&preload=true&responsive=true',
      },
      {
        activityName: 'Flappy Bird Game',
        activityDescription: `Create a game where a bird avoids obstacles to score points.`,
        activityImage: 'flappy-bird-game',
        altText: 'Flappy Bird Game Activity',
        iframeURL:
          'https://iframe.mediadelivery.net/embed/239253/725a87a7-95e4-4c05-9eef-9ce64e9586a5?autoplay=true&loop=false&muted=false&preload=true&responsive=true',
      },
      {
        activityName: 'Play the Drums',
        activityDescription: `Create a motion-sensing drum kit using video sensing, sound effects, and event triggers in Scratch.`,
        activityImage: 'play-the-drums',
        altText: 'Play the Drums Activity',
        iframeURL:
          'https://iframe.mediadelivery.net/embed/239253/847a494b-3138-408d-819a-cc28fca2b168?autoplay=true&loop=false&muted=false&preload=true&responsive=true',
      },
      {
        activityName: 'Space Animation',
        activityDescription: `Use Scratch to create a scene where a rocket moves between planets while stars twinkle in the background.`,
        activityImage: 'space-animation',
        altText: 'Space Animation Activity',
        iframeURL:
          'https://iframe.mediadelivery.net/embed/239253/28933160-2f57-422b-adc8-8508e9ad1a1f?autoplay=true&loop=false&muted=false&preload=true&responsive=true',
      },
      {
        activityName: 'Star Wars',
        activityDescription: `Build an exciting Star Wars-themed game using block-based coding on Code.org.`,
        activityImage: 'star-wars',
        altText: 'Star Wars Activity',
        iframeURL:
          'https://iframe.mediadelivery.net/embed/239253/465666e3-3a57-4526-a653-4dc31f4fa643?autoplay=true&loop=false&muted=false&preload=true&responsive=true',
      },
      {
        activityName: 'Save the Minion',
        activityDescription: `Use video sensing and conditionals to interact with the screen and save a Minion by making the right moves at the right time!`,
        activityImage: 'save-the-minion',
        altText: 'Save the Minion Activity',
        iframeURL:
          'https://iframe.mediadelivery.net/embed/239253/06e854c6-6eee-4666-b75a-7981773ca142?autoplay=true&loop=false&muted=false&preload=true&responsive=true',
      },
      {
        activityName: 'Emoji Board',
        activityDescription: `Create an interactive webpage that displays emojis representing moods, allowing users to explore their feelings with visual feedback.`,
        activityImage: 'emoji-board',
        altText: 'Emoji Board Activity',
        iframeURL:
          'https://iframe.mediadelivery.net/embed/239253/41f46e1a-bdd6-4265-8d1d-a19e95e3c392?autoplay=true&loop=false&muted=false&preload=true&responsive=true',
      },
      {
        activityName: 'Student Profile Card',
        activityDescription: `Build an exciting Star Wars-themed game using block-based coding on Code.org.`,
        activityImage: 'student-profile-card',
        altText: 'Student Profile Card Activity',
        iframeURL:
          'https://iframe.mediadelivery.net/embed/239253/b1efd76b-15c1-4574-b760-a7e95296c043?autoplay=true&loop=false&muted=false&preload=true&responsive=true',
      },
      {
        activityName: 'Instagram Login Page Clone',
        activityDescription: `Create a fun Instagram login page using HTML and CSS, learning to add forms, buttons, and links!`,
        activityImage: 'instagram-login-page-clone',
        altText: 'Instagram Login Page Clone Activity',
        iframeURL:
          'https://iframe.mediadelivery.net/embed/239253/6957c9be-71be-4c49-9f05-3d50bd2168cf?autoplay=true&loop=false&muted=false&preload=true&responsive=true',
      },
    ],
  },
  TURTLE: {
    title: 'Fun activities and lessons for kids to learn Turtle programming',
    description:
      'At Codingal, we make learning Python Turtle both fun and educational. With expert instructors providing step-by-step guidance, kids gradually progress from basic drawing techniques to more complex designs and creative projects. Each lesson is carefully designed to develop both technical coding skills and creative problem-solving. Students will have a hands-on experience creating everything from simple shapes to interactive graphics, all while mastering Python programming concepts. Ready for your child to explore the world of Python coding? Enroll in a free trial lesson and dive into the exciting realm of Turtle graphics—where learning to code is both fun and impactful!',
    activities: [
      {
        activityName: 'Basic Chatbot',
        activityDescription: `Build a simple chatbot using Python that greets users, asks about their feelings, and responds to their hobbies.`,
        activityImage: 'basic-chatbot',
        altText: 'Basic Chatbot Activity',
        iframeURL:
          'https://iframe.mediadelivery.net/embed/239253/f5ceb016-7924-43c5-b873-176df9d96366?autoplay=true&loop=false&muted=false&preload=true&responsive=true',
      },
      {
        activityName: 'Password Generator',
        activityDescription:
          'Create a Python tool to generate strong, random passwords based on user-defined length.',
        activityImage: 'password-generator',
        altText: 'Password Generator Activity',
        iframeURL:
          'https://iframe.mediadelivery.net/embed/239253/f01c46cf-7e6b-4367-bfa8-83347cfd3484?autoplay=true&loop=false&muted=false&preload=true&responsive=true',
      },
      {
        activityName: 'Python Quiz',
        activityDescription: `Build a fun and interactive quiz game using Python.`,
        activityImage: 'python-quiz',
        altText: 'Palindrome Checker Activity',
        iframeURL:
          'https://iframe.mediadelivery.net/embed/239253/830dd4aa-0fb3-4d6a-8d8a-f10c5151da01?autoplay=true&loop=false&muted=false&preload=true&responsive=true',
      },
    ],
  },
  MIT_APP_INVENTOR: {
    title: 'Fun activities and lessons for kids to learn MIT App Inventor',
    description:
      'MIT App Inventor is the perfect starting point for beginners, and at Codingal, we make learning coding for kids even more exciting with a curriculum designed just for kids like you! Our lessons teach the basics of coding while encouraging creativity, allowing students to build real apps like quiz games, health trackers, or even a doodling board. They’ll learn how coding works, solve problems, and develop important skills for the digital world. Join us and start your child’s coding for kids journey today!',
    activities: [
      {
        activityName: 'Swift Lingo',
        activityDescription: `Develop an AI-powered language translator app with integrated text-to-speech functionality on Thunkable.`,
        activityImage: 'swift-lingo',
        altText: 'Swift Lingo Activity',
        iframeURL:
          'https://iframe.mediadelivery.net/embed/239253/b4eae936-c62c-4916-82b9-96f9239a3b38?autoplay=true&loop=false&muted=false&preload=true&responsive=true',
      },
    ],
  },
  BOOTSTRAP: {
    title:
      'Fun activities and lessons for kids to learn the Bootstrap CSS framework',
    description:
      'In our trial lessons, kids get an exciting introduction to building things right from the first class. They dive into the magical world of coding and AI, creating simple projects that spark curiosity and lay the foundation for their learning journey.',
    activities: [
      {
        activityName: 'Dance Party with Spiderman',
        activityDescription: `Make Spider-Man groove to the beat by using simple actions and music in Scratch, creating your own fun dance routine!`,
        activityImage: 'dance-party-with-spiderman',
        altText: 'Dance Party with Spiderman Activity',
        iframeURL:
          'https://iframe.mediadelivery.net/embed/239253/359bd39b-1375-4e84-a2fd-e797fbf43e27?autoplay=true&loop=false&muted=false&preload=true&responsive=true',
      },
      {
        activityName: 'Flappy Bird Game',
        activityDescription: `Create a game where a bird avoids obstacles to score points.`,
        activityImage: 'flappy-bird-game',
        altText: 'Flappy Bird Game Activity',
        iframeURL:
          'https://iframe.mediadelivery.net/embed/239253/725a87a7-95e4-4c05-9eef-9ce64e9586a5?autoplay=true&loop=false&muted=false&preload=true&responsive=true',
      },
      {
        activityName: 'Play the Drums',
        activityDescription: `Create a motion-sensing drum kit using video sensing, sound effects, and event triggers in Scratch.`,
        activityImage: 'play-the-drums',
        altText: 'Play the Drums Activity',
        iframeURL:
          'https://iframe.mediadelivery.net/embed/239253/847a494b-3138-408d-819a-cc28fca2b168?autoplay=true&loop=false&muted=false&preload=true&responsive=true',
      },
      {
        activityName: 'Space Animation',
        activityDescription: `Use Scratch to create a scene where a rocket moves between planets while stars twinkle in the background.`,
        activityImage: 'space-animation',
        altText: 'Space Animation Activity',
        iframeURL:
          'https://iframe.mediadelivery.net/embed/239253/28933160-2f57-422b-adc8-8508e9ad1a1f?autoplay=true&loop=false&muted=false&preload=true&responsive=true',
      },
      {
        activityName: 'Star Wars',
        activityDescription: `Build an exciting Star Wars-themed game using block-based coding on Code.org.`,
        activityImage: 'star-wars',
        altText: 'Star Wars Activity',
        iframeURL:
          'https://iframe.mediadelivery.net/embed/239253/465666e3-3a57-4526-a653-4dc31f4fa643?autoplay=true&loop=false&muted=false&preload=true&responsive=true',
      },
      {
        activityName: 'Save the Minion',
        activityDescription: `Use video sensing and conditionals to interact with the screen and save a Minion by making the right moves at the right time!`,
        activityImage: 'save-the-minion',
        altText: 'Save the Minion Activity',
        iframeURL:
          'https://iframe.mediadelivery.net/embed/239253/06e854c6-6eee-4666-b75a-7981773ca142?autoplay=true&loop=false&muted=false&preload=true&responsive=true',
      },
      {
        activityName: 'Emoji Board',
        activityDescription: `Create an interactive webpage that displays emojis representing moods, allowing users to explore their feelings with visual feedback.`,
        activityImage: 'emoji-board',
        altText: 'Emoji Board Activity',
        iframeURL:
          'https://iframe.mediadelivery.net/embed/239253/41f46e1a-bdd6-4265-8d1d-a19e95e3c392?autoplay=true&loop=false&muted=false&preload=true&responsive=true',
      },
      {
        activityName: 'Student Profile Card',
        activityDescription: `Build an exciting Star Wars-themed game using block-based coding on Code.org.`,
        activityImage: 'student-profile-card',
        altText: 'Student Profile Card Activity',
        iframeURL:
          'https://iframe.mediadelivery.net/embed/239253/b1efd76b-15c1-4574-b760-a7e95296c043?autoplay=true&loop=false&muted=false&preload=true&responsive=true',
      },
      {
        activityName: 'Instagram Login Page Clone',
        activityDescription: `Create a fun Instagram login page using HTML and CSS, learning to add forms, buttons, and links!`,
        activityImage: 'instagram-login-page-clone',
        altText: 'Instagram Login Page Clone Activity',
        iframeURL:
          'https://iframe.mediadelivery.net/embed/239253/6957c9be-71be-4c49-9f05-3d50bd2168cf?autoplay=true&loop=false&muted=false&preload=true&responsive=true',
      },
    ],
  },
}

export const TESTIMONIAL: Record<CourseNameType, TestimonialType> = {
  SCRATCH_PROGRAMMING: {
    courseName: 'Scratch Programming',
    testimonials: [
      {
        image: 'scratch-programming/testimonials/james',
        name: 'James',
        content:
          'I really enjoy my Scratch classes, my teacher is very kind. I like Codingal classes very much.',
        subheading: 'Grade 1, Codingal Student',
      },
      {
        image: 'scratch-programming/testimonials/amelia',
        name: 'Amelia',
        content:
          'I am learning to create games using the blocks. My teacher makes the class more fun for me, thank you Codingal.',
        subheading: 'Grade 4, Codingal Student',
      },
      {
        image: 'scratch-programming/testimonials/vikrant',
        name: 'Vikrant',
        content:
          'I recently created an integer game on Scratch. I am excited about creating many more games and stories.',
        subheading: 'Grade 3, Codingal Student',
      },
      {
        image: 'scratch-programming/testimonials/ada',
        name: 'Ada Sade',
        content:
          "With Codingal's Scratch course, my daughter started her 1st coding lesson. The teachers are very nice and experienced.",
        subheading: 'Codingal Parent',
        profession: 'Nurse',
      },
      {
        image: 'scratch-programming/testimonials/nala',
        name: 'Nala',
        content:
          'I enjoy dragging and dropping the blocks, the blocks are so colorful. Scratch classes are fun.',
        subheading: 'Grade 2, Codingal Student',
      },
      {
        image: 'scratch-programming/testimonials/john',
        name: 'John Thomas',
        content:
          'Codingal has made my child excited to learn coding. He always look forward to the next class.',
        subheading: 'Codingal Parent',
        profession: 'Researcher',
      },
    ],
  },
  HPE_CODEWARS_HACKATHON: {
    courseName: 'HPE Codewars Hackathon',
    testimonials: [
      {
        image: 'hpe-codewars-hackathon/testimonials/siddharth',
        name: 'Siddharth Narayanabhatla',
        content:
          'Along with developing websites, this course has also helped me improve my problem solving skills.',
        subheading: 'Grade 8, Codingal Student',
      },
      {
        image: 'hpe-codewars-hackathon/testimonials/soumya',
        name: 'Soumya Ranjan',
        content:
          'I am learning to create websites using HTML and CSS which is so fun that it makes me want to learn more.',
        subheading: 'Grade 9, Codingal Student',
      },
      {
        image: 'hpe-codewars-hackathon/testimonials/aditya',
        name: 'Aditya Viswanadha',
        content:
          'The course is very interesting. The hands-on projects helped me to improve my website building skills.',
        subheading: 'Grade 9, Codingal Student',
      },
      {
        image: 'hpe-codewars-hackathon/testimonials/seema',
        name: 'Seema Singh',
        content:
          'I cannot believe that my kid has learned to develop amazing websites at an early age. Thank you Codingal.',
        subheading: 'Codingal Parent',
      },
    ],
  },
  HPE_CODEWARS_CODE_BATTLE: {
    courseName: 'HPE Codewars Code Battle',
    testimonials: [
      {
        image: 'hpe-codewars-code-battle/testimonials/mohit',
        name: 'Mohit Hulse',
        content:
          'I always enjoy Codingal competitions as the increasing difficulty levels as we progress make it so much more exciting.',
        subheading: 'HPE CodeWars Winner, Grade 11',
      },
      {
        image: 'hpe-codewars-code-battle/testimonials/siddhant',
        name: 'Siddhant',
        content:
          'Codingal gave me the once-in-a-lifetime experience of seeing my code run on the International Space Station.',
        subheading: 'HPE CodeWars Winner, Grade 10',
      },
      {
        image: 'hpe-codewars-code-battle/testimonials/swastik',
        name: 'Swastik',
        content:
          'I won the HPE CodeWars competition and got the opportunity to run an experiment on the ISS thanks to Codingal.',
        subheading: 'HPE CodeWars Winner, Grade 12',
      },
      {
        image: 'hpe-codewars-code-battle/testimonials/sushil',
        name: 'Sushil Raaja U',
        content:
          'As a student preparing for the IOI, Codingal has helped me greatly improve my problem solving and algorithmic thinking.',
        subheading: 'HPE Code Battle #1 Winner, Grade 11',
      },
      {
        image: 'hpe-codewars-code-battle/testimonials/aarav',
        name: 'Aarav Eeshan',
        content:
          'The algorithmic problems in Codingal’s competitions are super intriguing and I always get to learn something new.',
        subheading: 'HPE Code Battle Winner, Grade 11',
      },
    ],
  },
  PYTHON_FOR_KIDS: {
    courseName: 'Python for Kids',
    testimonials: [
      {
        image: 'python-for-kids/testimonials/lily',
        name: 'Lily Zane',
        content:
          'Codingal has helped me learn Python by making the learning process fun and interactive. Colorful syntax and indentation make writing code enjoyable.',
        subheading: 'Grade 9, Codingal Student',
      },
      {
        image: 'python-for-kids/testimonials/ishan',
        name: 'Ishan',
        content:
          'My teacher gives very clear instruction. With Python programming course by Codingal, I can create my own games.',
        subheading: 'Grade 7, Codingal Student',
      },
      {
        image: 'python-for-kids/testimonials/maxwell',
        name: 'Maxwell',
        content:
          'Codingal has helped me transition from block based programming to text based programming through their Python programming course.',
        subheading: 'Grade 6, Codingal Student',
      },
      {
        image: 'python-for-kids/testimonials/juke',
        name: 'Juke Williams',
        content:
          "Codingal's Python programming course made it easier for my child to learn to build his favorite games.",
        subheading: 'Codingal Parent',
      },
      {
        image: 'python-for-kids/testimonials/julia',
        name: 'Julia',
        content:
          'The Python course at Codingal help me in scripting and creating data sets. The Python libraries make it easy to share code projects.',
        subheading: 'Grade 10, Codingal Student',
      },
      {
        image: 'python-for-kids/testimonials/andrea',
        name: 'Andrea Agnes',
        content:
          "Codingal's personalized journey has really helped my child learn Python at her own pace and understanding.",
        subheading: 'Codingal Parent',
      },
    ],
  },
  ROBLOX: {
    courseName: 'Roblox',
    testimonials: [
      {
        image: 'roblox/testimonials/mike',
        name: 'Mike',
        content:
          'The Roblox course at Codingal has helped me create my games. It is very straightforward, and I can build my own characters, terrains, and more.',
        subheading: 'Grade 5, Codingal Student',
      },
      {
        image: 'roblox/testimonials/neha',
        name: 'Neha',
        content:
          'I like to code because I can program my things and create the games I want! Whatever comes into my imagination, I can code it.',
        subheading: 'Grade 7, Codingal Student',
      },
      {
        image: 'roblox/testimonials/elliot',
        name: 'Elliot Aidan',
        content:
          'I have tried other coding programs for my kid, but I do not find them as informative and fun as Codingal’s course. My son enjoys every coding lesson. I am grateful to the teachers at Codingal; they are generous, responsive, and supportive.',
        subheading: 'Codingal Parent',
      },
      {
        image: 'roblox/testimonials/kamla',
        name: 'Kamla Sharma',
        content:
          'My daughter loves playing games. Codingal’s Roblox course is a perfect course that excites her to learn to code while building her own game in a fun way.',
        subheading: 'Codingal Parent',
      },
    ],
  },
  AP_COMPUTER_SCIENCE_A: {
    courseName: 'AP Computer Science A',
    testimonials: [
      {
        image: 'ap-computer-science-a/testimonials/leo',
        name: 'Leo Gate ',
        content:
          'The AP Computer Science A course at Codingal has helped me understand coding through exploring concepts of OOPs in Java, data structures and algorithms.',
        subheading: 'Grade 11, Codingal Student',
      },
      {
        image: 'ap-computer-science-a/testimonials/zeni',
        name: 'Zeni Fincher',
        content:
          'I went through multiple practice questions. The AP Computer Science A course  introduced me to Java programming concepts including arrays, recursion and strings.',
        subheading: 'Grade 12, Codingal Student',
      },
      {
        image: 'ap-computer-science-a/testimonials/dan',
        name: 'Dan Petra',
        content:
          'My son is preparing for college. This course has helped him test his programming knowledge. The Course challenge can really  help your child understand what they need to review.',
        subheading: 'Codingal Parent',
      },
      {
        image: 'ap-computer-science-a/testimonials/jay',
        name: 'Jay',
        content:
          'My daughter got familiar with the concepts of Java programming. This course is best suited for my child as the school she attends does not provide a similar class.',
        subheading: 'Codingal Parent',
      },
    ],
  },
}

const SCRATCH_PROGRAMMING_FAQ: FaqType[] = [
  {
    questionName: 'What is Scratch programming?',
    acceptedAnswerText:
      'Scratch is a free block-based programming language designed primarily for kids. It’s a visual programming interface where kids can code interactive stories, games, and animations and share their creations through an online community. Scratch coding courses for kids make learning fun and interactive.',
    acceptedAnswerJsx: (
      <>
        Scratch is a free block-based programming language designed primarily
        for kids. It’s a visual programming interface where kids can code
        interactive stories, games, and animations and share their creations
        through an online community. Scratch coding courses for kids make
        learning fun and interactive.
      </>
    ),
  },
  {
    questionName: 'What is Scratch programming used for?',
    acceptedAnswerText:
      'Scratch programming is used to create interactive stories, games, animations, and much more by dragging and dropping code blocks.',
    acceptedAnswerJsx: (
      <>
        Scratch programming is used to create interactive stories, games,
        animations, and much more by dragging and dropping code blocks.
      </>
    ),
  },
  {
    questionName: 'Is Scratch programming easy to learn for kids?',
    acceptedAnswerText: `Scratch uses a drag and drop method which lets children perform actions quickly and easily. This approach makes Scratch programming courses for kids easy to learn and fun. Coding for kids is fun & children can now master Scratch coding courses with Codingal's Scratch programming course for kids, curated especially for grade 1-8 students. The specialized scratch coding course for kids helps children develop skills such as creative thinking, problem-solving, storytelling, and animation.`,
    acceptedAnswerJsx: (
      <>
        Scratch uses a drag and drop method which lets children perform actions
        quickly and easily. This approach makes Scratch programming courses for
        kids easy to learn and fun. <br />
        Coding for kids is fun & children can now master Scratch coding courses
        with Codingal’s Scratch programming course for kids, curated especially
        for grade 1-8 students. The{' '}
        <strong>specialized scratch coding course for kids</strong> helps
        children develop skills such as creative thinking, problem-solving,
        storytelling, and animation.
      </>
    ),
  },
  {
    questionName: 'How should kids get started with Scratch programming?',
    acceptedAnswerText:
      'Here are three easy steps for kids to get started with Scratch: Sign up on the Scratch website for free. Explore the “ideas” section to access the getting started tutorial. Create your first project using Scratch.',
    acceptedAnswerJsx: (
      <>
        Here are three easy steps for kids to get started with Scratch:
        <ul>
          <li>Sign up on the Scratch website for free.</li>
          <li>
            Explore the “ideas” section to access the getting started tutorial.
          </li>
          <li>Create your first project using Scratch.</li>
        </ul>
      </>
    ),
  },
  {
    questionName: 'How can kids learn Scratch programming for free?',
    acceptedAnswerText:
      'Kids can learn Scratch programming by signing up on the Scratch website for free. It’s a free-to-use programming language tool that allows children to learn how to code in a visually engaging way.',
    acceptedAnswerJsx: (
      <>
        Kids can learn Scratch programming by signing up on the Scratch website
        for free. It’s a free-to-use programming language tool that allows
        children to learn how to code in a visually engaging way.
      </>
    ),
  },
  {
    questionName: 'Is Scratch a good programming language for kids?',
    acceptedAnswerText:
      'Yes. Scratch is one of the best programming languages designed primarily for kids to learn to code. It encourages kids to bring their imagination to life by creating innovative projects.',
    acceptedAnswerJsx: (
      <>
        Yes. Scratch is one of the best programming languages designed primarily
        for kids to learn to code. It encourages kids to bring their imagination
        to life by creating innovative projects.
      </>
    ),
  },
  {
    questionName: 'Why should kids learn Scratch programming?',
    acceptedAnswerText:
      'Scratch is a great platform for kids to get started to learn to code. It’s an interactive, drag-and-drop programming language that allows kids to create animations, apps, and games. With Scratch, they learn to think creatively, work collaboratively, and reason systematically. Codingal offers the best Scratch programming course for kids to master the sun and interactive Scratch platform.',
    acceptedAnswerJsx: (
      <>
        Scratch is a great platform for kids to get started to learn to code.
        It’s an interactive, drag-and-drop programming language that allows kids
        to create animations, apps, and games. With Scratch, they learn to think
        creatively, work collaboratively, and reason systematically.{' '}
        <a
          href="https://www.codingal.com/"
          target="blank"
          rel="noreferrer"
          className="text-blue-200 underline"
        >
          Codingal
        </a>{' '}
        offers the best Scratch programming course for kids to master their
        coding skills using the interactive{' '}
        <a
          href="https://www.codingal.com/coding-for-kids/blog/introduction-to-scratch-coding/"
          target="blank"
          rel="noreferrer"
          className="text-blue-200 underline"
        >
          scratch coding
        </a>{' '}
        platform.
      </>
    ),
  },
  {
    questionName: 'Is Scratch a good way for kids to learn to code?',
    acceptedAnswerText:
      'Scratch is one of the best ways for kids to learn to code. It involves dragging and dropping elements instead of writing code while children still learn about coding logic. The bright and colorful interface of Scratch is engaging and fun for most kids. It provides visual clues through predefined groups of code (blocks), such as events, controls, commands, loops, conditions, and more. These blocks can be selected, modified, and sequenced to create games, apps, animations, and more. Codingal’s Scratch coding courses for kids is a top-rated coding course for kids that helps them to learn programmatic thinking and get an enthusiastic head start into real-world coding skills.',
    acceptedAnswerJsx: (
      <>
        Scratch is one of the best ways for kids to learn to code. It involves
        dragging and dropping elements instead of writing code while children
        still learn about coding logic. <br /> The bright and colorful interface
        of Scratch is engaging and fun for most kids. It provides visual clues
        through predefined groups of code (blocks), such as events, controls,
        commands, loops, conditions, and more. These blocks can be selected,
        modified, and sequenced to create games, apps, animations, and more.
        Codingal’s Scratch coding courses for kids is a top-rated coding course
        for kids that helps them to learn programmatic thinking and get an
        enthusiastic head start into real-world coding skills.
      </>
    ),
  },
  {
    questionName:
      'What are the benefits of learning Scratch programming for kids?',
    acceptedAnswerText:
      'Learning Scratch programming will help children learn the basic coding concepts along with problem-solving, project building, design thinking, and much more. Kids can now master the core principles of coding with Codingal’s best Scratch coding course for kids.',
    acceptedAnswerJsx: (
      <>
        Learning Scratch programming will help children learn the basic coding
        concepts along with problem-solving, project building, design thinking,
        and much more. Kids can now master the core principles of coding with
        Codingal’s best{' '}
        <a
          href="/coding-for-kids/blog/introduction-to-scratch-coding/"
          target="blank"
          rel="noreferrer"
          className="text-blue-200 underline"
        >
          Scratch coding
        </a>{' '}
        course for kids.
      </>
    ),
  },
  {
    questionName:
      'What are the learning outcomes of the Scratch programming course for kids?',
    acceptedAnswerText:
      'The Scratch programming course will help kids learn the core principles of coding using the interactive Scratch platform. Codingal’s Scratch programming course for Grade 1-8 kids helps them master the essential principles of coding including events, coordinates, loops, sequences, sprites, animation, and much more. The course will also help the kid develop other essential skills including creative thinking, problem-solving, and storytelling.',
    acceptedAnswerJsx: (
      <>
        The Scratch programming course will help kids learn the core principles
        of coding using the interactive Scratch platform. Codingal’s Scratch
        programming course for Grade 1-8 kids helps them master the essential
        principles of coding including events, coordinates, loops, sequences,
        sprites, animation, and much more. The course will also help the kid
        develop other essential skills including creative thinking,
        problem-solving, and storytelling.
      </>
    ),
  },
]

const PYTHON_FOR_KIDS_FAQ: FaqType[] = [
  {
    questionName: 'What are Python games for kids?',
    acceptedAnswerText:
      'Scratch is a free block-based programming language designed primarily for kids. It’s a visual programming interface where kids can code interactive stories, games, and animations and share their creations through an online community. Scratch coding courses for kids make learning fun and interactive.',
    acceptedAnswerJsx: (
      <>
        Kids can learn how to code in Python by creating multiple interactive
        and fun games. Some popular Python games for kids include Ping pong,
        Rock paper scissors, Snake game, Tic-Tac-Toe, etc. Python games help
        kids learn Python and explore new libraries of Python.
      </>
    ),
  },
  {
    questionName: 'What are Loops in Python?',
    acceptedAnswerText:
      'A loop statement in Python programming allows you to run a statement or group of statements multiple times. For example, the ATM software is in a loop to process transaction after transaction until you acknowledge that you have no more to do. Sometimes, we are required to execute a code several times. So there, we use the concept of loops.',
    acceptedAnswerJsx: (
      <>
        A loop statement in Python programming allows you to run a statement or
        group of statements multiple times. For example, the ATM software is in
        a loop to process transaction after transaction until you acknowledge
        that you have no more to do. Sometimes, we are required to execute a
        code several times. So there, we use the concept of loops.
      </>
    ),
  },
  {
    questionName: 'What are Python functions?',
    acceptedAnswerText:
      'A function is a block of codes of related statements that performs a specific task. Functions help break our program into small blocks of code. As the program grows larger and larger, functions make it more organized and manageable. Suppose repeated code occurs in a program. The function can include those codes and execute them when needed by calling that function. Programmers working on large projects can divide the workload by making different functions.',
    acceptedAnswerJsx: (
      <>
        A function is a block of codes of related statements that performs a
        specific task. Functions help break our program into small blocks of
        code. As the program grows larger and larger, functions make it more
        organized and manageable. Suppose repeated code occurs in a program. The
        function can include those codes and execute them when needed by calling
        that function. Programmers working on large projects can divide the
        workload by making different functions.
      </>
    ),
  },
  {
    questionName: 'What is Exception Handling in Python?',
    acceptedAnswerText:
      'An exception can be defined as an unusual condition in a program resulting in an interruption in the flow of the program. Whenever an exception occurs, the program stops the execution, and thus the other code is not executed. An exception is an object that represents the error. Python provides a way to handle the exception so that c can execute the code without any interruption.',
    acceptedAnswerJsx: (
      <>
        An exception can be defined as an unusual condition in a program
        resulting in an interruption in the flow of the program. Whenever an
        exception occurs, the program stops the execution, and thus the other
        code is not executed. An exception is an object that represents the
        error. Python provides a way to handle the exception so that c can
        execute the code without any interruption.
      </>
    ),
  },
  {
    questionName: 'What are Python Data Structures?',
    acceptedAnswerText:
      'Data Structures help you in storing the collection of data, managing it, and performing operations on them. Data Structures help you to make modifications easier on collections of data. In Python, we have four Data Structures- List, Dictionary, Tuple, and Set.',
    acceptedAnswerJsx: (
      <>
        Data Structures help you in storing the collection of data, managing it,
        and performing operations on them. Data Structures help you to make
        modifications easier on collections of data. In Python, we have four
        Data Structures- List, Dictionary, Tuple, and Set.
      </>
    ),
  },
  {
    questionName: 'What is Object Oriented Programming in Python?',
    acceptedAnswerText:
      'Object-Oriented Programming revolves around Classes and Objects and aims to increase the reusability of code. Object-oriented programming concepts create simpler code to understand, have higher readability, and are reusable. So, what exactly are these Classes and Objects? Consider Fruit as a class, Apple and Orange as Objects for better understanding. So, apples and oranges are real-world entities with some attributes and behavior. The primary idea behind OOPs is to combine data and the functions that operate with it into a single unit such that no other parts of the code may access it.',
    acceptedAnswerJsx: (
      <>
        Object-Oriented Programming revolves around Classes and Objects and aims
        to increase the reusability of code. Object-oriented programming
        concepts create simpler code to understand, have higher readability, and
        are reusable. So, what exactly are these Classes and Objects? Consider
        Fruit as a class, Apple and Orange as Objects for better understanding.
        So, apples and oranges are real-world entities with some attributes and
        behavior. The primary idea behind OOPs is to combine data and the
        functions that operate with it into a single unit such that no other
        parts of the code may access it.
      </>
    ),
  },
  {
    questionName: 'What is Python Dictionary?',
    acceptedAnswerText:
      'Python dictionary is a collection of items used to store data values. Each item in the dictionary has a key/value pair. Value of the key-value pair can be a number, a string, a list, a tuple, or even another dictionary. Python dictionary is defined by placing a sequence of items within curly {} braces. Python dictionary allows us to write effective codes.',
    acceptedAnswerJsx: (
      <>
        Python dictionary is a collection of items used to store data values.
        Each item in the dictionary has a key/value pair. Value of the key-value
        pair can be a number, a string, a list, a tuple, or even another
        dictionary. Python dictionary is defined by placing a sequence of items
        within curly {} braces. Python dictionary allows us to write effective
        codes.
      </>
    ),
  },
  {
    questionName: 'How can I download & install Python?',
    acceptedAnswerText:
      'You can download  and install Python from Python software foundation website -  https://www.python.org/',
    acceptedAnswerJsx: (
      <>
        You can download and install Python from Python software foundation
        website -{' '}
        <a
          href="https://www.python.org/"
          target="_blank"
          rel="noreferrer"
          className="text-blue-200 underline"
        >
          https://www.python.org/
        </a>
      </>
    ),
  },
  {
    questionName: 'Why should kids learn Python programming?',
    acceptedAnswerText:
      'Python is the perfect coding language to get kids started off with. Python programming for kids helps improve and develop multiple facets of their educational syllabus including maths, writing, and creativity. It will help them develop a more concise and structured approach to storytelling with the English language. It will also aid them with problem solving too.Python leads them to developing simple and efficient solutions using critical thinking.Needless to say, Python developers are in high demand.',
    acceptedAnswerJsx: (
      <>
        Python is the perfect coding language to get kids started off with.
        Python programming for kids helps improve and develop multiple facets of
        their educational syllabus including maths, writing, and creativity. It
        will help them develop a more concise and structured approach to
        storytelling with the English language. It will also aid them with
        problem solving too.Python leads them to developing simple and efficient
        solutions using critical thinking.Needless to say, Python developers are
        in high demand.
      </>
    ),
  },
  {
    questionName: 'Is Python kid-friendly?',
    acceptedAnswerText:
      'Yes, Python programming is kid-friendly. It  is a programming language that is particularly good for kids wanting to learn how to code. Python is great for kids, especially as a first language. Kids new to python programming can go right from the very first simple “Hello world” programs to creating games in the future.',
    acceptedAnswerJsx: (
      <>
        Yes, Python programming is kid-friendly. It is a programming language
        that is particularly good for kids wanting to learn how to code. Python
        is great for kids, especially as a first language. Kids new to python
        programming can go right from the very first simple “Hello world”
        programs to creating games in the future.
      </>
    ),
  },
  {
    questionName:
      'How can I be sure that Python programming is the right program for my kid?',
    acceptedAnswerText:
      'Python’s syntax is concise and easily readable by humans. It mirrors the human language far better than any other programming language which makes it an ideal language for beginners. Writing code in Python is similar to writing commands in plain English. As beginners, kids will quickly pick up the basics and learn how to use this language for game development and animation.',
    acceptedAnswerJsx: (
      <>
        Python’s syntax is concise and easily readable by humans. It mirrors the
        human language far better than any other programming language which
        makes it an ideal language for beginners. Writing code in Python is
        similar to writing commands in plain English. As beginners, kids will
        quickly pick up the basics and learn how to use this language for game
        development and animation.
      </>
    ),
  },
  {
    questionName: 'Why should kids learn to code?',
    acceptedAnswerText:
      'Coding for kids helps improve their logical thinking, critical thinking, problem-solving skills, creativity, mathematics and writing skills and also gives them valuable skills in life and at the workplace. Coding provides a competitive advantage when applying for colleges, internships, and jobs. In addition, basic programming knowledge can change how kids interact daily with the technologies they use. It’s a basic literacy–one we can’t afford to overlook.',
    acceptedAnswerJsx: (
      <>
        <a
          href="https://www.codingal.com/coding-for-kids/"
          target="_blank"
          rel="noopener noreferrer"
          className="text-blue-200 underline"
        >
          Coding for kids
        </a>{' '}
        helps improve their logical thinking, critical thinking, problem-solving
        skills, creativity, mathematics and writing skills and also gives them
        valuable skills in life and at the workplace. Coding provides a
        competitive advantage when applying for colleges, internships, and jobs.
        In addition, basic programming knowledge can change how kids interact
        daily with the technologies they use. It’s a basic literacy–one we can’t
        afford to overlook.
      </>
    ),
  },
  {
    questionName:
      'My child is a beginner, is previous coding experience required?',
    acceptedAnswerText:
      'Our coding courses for kids have been developed so that a complete beginner can follow along without any trouble. With technology increasingly prevalent across so many industries, even basic coding knowledge can help kids regardless of the career they ultimately decide to pursue.',
    acceptedAnswerJsx: (
      <>
        Our coding courses for kids have been developed so that a complete
        beginner can follow along without any trouble. With technology
        increasingly prevalent across so many industries, even basic coding
        knowledge can help kids regardless of the career they ultimately decide
        to pursue.{' '}
      </>
    ),
  },
]
const ROBLOX: FaqType[] = [
  {
    questionName: 'Is Roblox free?',
    acceptedAnswerText:
      'Roblox is a free to use platform on which users can create and play 3D games.',
    acceptedAnswerJsx: (
      <>
        Roblox is a free to use platform on which users can create and play 3D
        games.
      </>
    ),
  },
  {
    questionName: 'What is Roblox used for?',
    acceptedAnswerText:
      'Roblox is an online platform on which kids can create and play games. ',
    acceptedAnswerJsx: (
      <>Roblox is an online platform on which kids can create and play games.</>
    ),
  },
  {
    questionName: 'What is Roblox studio used for?',
    acceptedAnswerText:
      'Roblox studio is used to design and code the games. You can download Roblox studio on your system and start creating games with Roblox coding.',
    acceptedAnswerJsx: (
      <>
        Roblox studio is used to design and code the games. You can download
        Roblox studio on your system and start creating games with Roblox
        coding.
      </>
    ),
  },
  {
    questionName: 'Why should kids learn to code?',
    acceptedAnswerText:
      'Coding for kids helps improve their logical thinking, critical thinking, problem-solving skills, creativity, mathematics and writing skills and also gives them valuable skills in life and at the workplace. Coding provides a competitive advantage when applying for colleges, internships, and jobs. In addition, basic programming knowledge can change how kids interact daily with the technologies they use. It’s a basic literacy–one we can’t afford to overlook.',
    acceptedAnswerJsx: (
      <>
        <a
          href="https://www.codingal.com/coding-for-kids/"
          target="blank"
          rel="noreferrer"
          className="text-blue-200 underline"
        >
          Coding for kids
        </a>{' '}
        helps improve their logical thinking, critical thinking, problem-solving
        skills, creativity, mathematics and writing skills and also gives them
        valuable skills in life and at the workplace. <br />
        Coding provides a competitive advantage when applying for colleges,
        internships, and jobs. In addition, basic programming knowledge can
        change how kids interact daily with the technologies they use. It’s a
        basic literacy–one we can’t afford to overlook.
      </>
    ),
  },
  {
    questionName: 'Which programming language does Roblox studio use?',
    acceptedAnswerText:
      'Roblox studio uses Lua scripting language. It’s relatively easy to learn, making it ideal for young codes to learn game development.',
    acceptedAnswerJsx: (
      <>
        Roblox studio uses Lua scripting language. It’s relatively easy to
        learn, making it ideal for young codes to learn game development.
      </>
    ),
  },
  {
    questionName: 'Is Roblox safe for kids?',
    acceptedAnswerText:
      'If you take advantage of all the parental control features and stay involved in what your child’s playing and who they’re playing with, Roblox can be reasonably safe for kids.',
    acceptedAnswerJsx: (
      <>
        If you take advantage of all the parental control features and stay
        involved in what your child’s playing and who they’re playing with,
        Roblox can be reasonably safe for kids.
      </>
    ),
  },
  {
    questionName: 'Is Roblox studio and Roblox same?',
    acceptedAnswerText:
      'Roblox studio is used to design and code the games. Games in Roblox are called ‘experiences’. Whereas Roblox is used to visiting the ‘experiences’.',
    acceptedAnswerJsx: (
      <>
        Roblox studio is used to design and code the games. Games in Roblox are
        called ‘experiences’. Whereas Roblox is used to visiting the
        ‘experiences’.
      </>
    ),
  },
  {
    questionName:
      'My child is a beginner, is previous coding experience required?',
    acceptedAnswerText:
      'Our coding courses for kids have been developed so that a complete beginner can follow along without any trouble. With technology increasingly prevalent across so many industries, even basic coding knowledge can help kids regardless of the career they ultimately decide to pursue. ',
    acceptedAnswerJsx: (
      <>
        Our coding courses for kids have been developed so that a complete
        beginner can follow along without any trouble. With technology
        increasingly prevalent across so many industries, even basic coding
        knowledge can help kids regardless of the career they ultimately decide
        to pursue.
      </>
    ),
  },
]
const AP_COMPUTER_SCIENCE_A: FaqType[] = [
  {
    questionName: 'What do you learn in AP Computer Science A?',
    acceptedAnswerText:
      'In AP Computer Science A, you will learn the fundamentals of java programming language, data structures and algorithms, strings and arrays and object-oriented programming.',
    acceptedAnswerJsx: (
      <>
        In AP Computer Science A, you will learn the fundamentals of java
        programming language, data structures and algorithms, strings and arrays
        and object-oriented programming.
      </>
    ),
  },
  {
    questionName: 'Is AP Computer Science A for beginners?',
    acceptedAnswerText:
      'AP Computer Science A is an introductory course for all students. Any high school student with basic understanding of Computer Science can enrol for this course.',
    acceptedAnswerJsx: (
      <>
        AP Computer Science A is an introductory course for all students. Any
        high school student with basic understanding of Computer Science can
        enrol for this course.
      </>
    ),
  },
  {
    questionName:
      'My child is a beginner, is previous coding experience required?',
    acceptedAnswerText:
      'Our coding courses for kids and teens have been developed so that a complete beginner can follow along without any trouble. With technology increasingly prevalent across so many industries, even basic coding knowledge can help kids and teens regardless of the career they ultimately decide to pursue. ',
    acceptedAnswerJsx: (
      <>
        Our coding courses for kids and teens have been developed so that a
        complete beginner can follow along without any trouble. With technology
        increasingly prevalent across so many industries, even basic coding
        knowledge can help kids and teens regardless of the career they
        ultimately decide to pursue.
      </>
    ),
  },
  {
    questionName: 'How hard is AP Computer Science A?',
    acceptedAnswerText:
      'AP computer science A is considered easy. If you have a previous understanding of programming it will be easier for you to learn AP computer science A.',
    acceptedAnswerJsx: (
      <>
        AP computer science A is considered easy. If you have a previous
        understanding of programming it will be easier for you to learn AP
        computer science A.
      </>
    ),
  },
  {
    questionName: 'What is AP Computer Science A equivalent to?',
    acceptedAnswerText:
      'AP Computer Science A is the equivalent of a first-semester college-level computer science course. Students with no prior computer science experience can do well in AP Computer Science A, which focuses on computing skills related to Java programming.',
    acceptedAnswerJsx: (
      <>
        AP Computer Science A is the equivalent of a first-semester
        college-level computer science course. Students with no prior computer
        science experience can do well in AP Computer Science A, which focuses
        on computing skills related to Java programming.
      </>
    ),
  },
  {
    questionName:
      'What is the difference between AP Computer Science A and principles?',
    acceptedAnswerText:
      'The AP Computer Science Principles does not have a fixed programming language; students are expected to understand programming concepts and algorithms using pseudocode and flowcharts. Teachers can use specific programming languages such as Python, Java, or C++ as examples, but they are not required for the written portion of the exam. On the other hand, AP Computer Science A focuses more on programming. Object-oriented programming is stressed, and students learn using Java as a programming language. Students must be comfortable with Java syntax, libraries, and structures for the exam.',
    acceptedAnswerJsx: (
      <>
        The AP Computer Science Principles does not have a fixed programming
        language; students are expected to understand programming concepts and
        algorithms using pseudocode and flowcharts. Teachers can use specific
        programming languages such as Python, Java, or C++ as examples, but they
        are not required for the written portion of the exam. On the other hand,
        AP Computer Science A focuses more on programming. Object-oriented
        programming is stressed, and students learn using Java as a programming
        language. Students must be comfortable with Java syntax, libraries, and
        structures for the exam.
      </>
    ),
  },
  {
    questionName: 'What do you need to know for AP Computer Science A?',
    acceptedAnswerText:
      'There is no prerequisite for AP Computer Science A. If you are in high school and have basic knowledge of programming, you can take AP Computer Science A course.',
    acceptedAnswerJsx: (
      <>
        There is no prerequisite for AP Computer Science A. If you are in high
        school and have basic knowledge of programming, you can take AP Computer
        Science A course.
      </>
    ),
  },
  {
    questionName: 'Should I take AP Computer Science A?',
    acceptedAnswerText:
      'If you want to pursue Computer Science and start earning college credit in high school, you should take AP Computer Science A. It will help you gain insight into coding by using Java and explore topics like modularity, variables, arrays, and the impact of computing.',
    acceptedAnswerJsx: (
      <>
        If you want to pursue Computer Science and start earning college credit
        in high school, you should take AP Computer Science A. It will help you
        gain insight into coding by using Java and explore topics like
        modularity, variables, arrays, and the impact of computing.
      </>
    ),
  },
  {
    questionName: 'Is AP Computer Science A worth it?',
    acceptedAnswerText:
      'AP Computer Science A is entirely focused on Java programming. If you plan to study computer science at university, taking AP computer science A will be worth it as it will help you gain knowledge about actual programming.',
    acceptedAnswerJsx: (
      <>
        AP Computer Science A is entirely focused on Java programming. If you
        plan to study computer science at university, taking AP computer science
        A will be worth it as it will help you gain knowledge about actual
        programming.
      </>
    ),
  },
  {
    questionName: 'Is AP Computer Science A, Python or Java?',
    acceptedAnswerText:
      'The AP Computer Science A course and exam entirely focuses on Java.',
    acceptedAnswerJsx: (
      <>The AP Computer Science A course and exam entirely focuses on Java.</>
    ),
  },
  {
    questionName: 'What is AP Computer Science A vs AB?',
    acceptedAnswerText:
      'AP Computer Science A focus on object-oriented programming and tests students on their knowledge of Java. Whereas AP Computer Science AB is a discontinued course.',
    acceptedAnswerJsx: (
      <>
        AP Computer Science A focus on object-oriented programming and tests
        students on their knowledge of Java. Whereas AP Computer Science AB is a
        discontinued course.
      </>
    ),
  },
  {
    questionName: 'How long is AP Computer Science A?',
    acceptedAnswerText:
      'AP Computer Science A will take you around a half year to complete the course.',
    acceptedAnswerJsx: (
      <>
        AP Computer Science A will take you around a half year to complete the
        course.
      </>
    ),
  },
  {
    questionName: 'Is AP Computer Science A Python?',
    acceptedAnswerText:
      'No. In AP Computer Science A python is not covered. AP Computer Science A focuses on Java programming.',
    acceptedAnswerJsx: (
      <>
        No. In AP Computer Science A python is not covered. AP Computer Science
        A focuses on Java programming.
      </>
    ),
  },
  {
    questionName: 'Which AP Computer Science is easier?',
    acceptedAnswerText:
      'AP Computer Science principle is comparatively easy to AP Computer Science A as those with no coding experience can learn AP Computer Science principle.',
    acceptedAnswerJsx: (
      <>
        AP Computer Science principle is comparatively easy to AP Computer
        Science A as those with no coding experience can learn AP Computer
        Science principle.
      </>
    ),
  },
  {
    questionName: 'Should I take AP CSA or AP CSP?',
    acceptedAnswerText:
      'If you are new to programming and Computer Science you should take AP CSP. If you want to pursue Computer Science in college you should take AP CSA as it focuses on Java and object-oriented programming.',
    acceptedAnswerJsx: (
      <>
        If you are new to programming and Computer Science you should take AP
        CSP. If you want to pursue Computer Science in college you should take
        AP CSA as it focuses on Java and object-oriented programming.
      </>
    ),
  },
  {
    questionName: 'Can you self-study AP CS A?',
    acceptedAnswerText:
      'You can self-study for AP CS A if you are comfortable with programming but if you are someone who is new to programming we recommend taking an online course.',
    acceptedAnswerJsx: (
      <>
        You can self-study for AP CS A if you are comfortable with programming
        but if you are someone who is new to programming we recommend taking an
        online course.
      </>
    ),
  },
  {
    questionName: 'How do you get a 5 on AP CS A?',
    acceptedAnswerText:
      'You can get a 5 on AP CS A by staying up to date with the resources, and practicing the previous year‘s questions. You can take an online course at Codingal to get the best guidance and study material.',
    acceptedAnswerJsx: (
      <>
        You can get a 5 on AP CS A by staying up to date with the resources, and
        practicing the previous year’s questions. You can take an online course
        at Codingal to get the best guidance and study material.
      </>
    ),
  },
  {
    questionName: 'What is the easiest AP course?',
    acceptedAnswerText:
      'AP Computer Science principle is considered as the easiest AP course based on the students rating. ',
    acceptedAnswerJsx: (
      <>
        AP Computer Science principle is considered as the easiest AP course
        based on the students rating.{' '}
      </>
    ),
  },

  {
    questionName: 'Why should kids and teens learn to code?',
    acceptedAnswerText:
      'Coding for kids and teens helps improve their logical thinking, critical thinking, problem-solving skills, creativity, mathematics and writing skills and also gives them valuable skills in life and at the workplace. Coding provides a competitive advantage when applying for colleges, internships, and jobs. In addition, basic programming knowledge can change how kids and teens interact daily with the technologies they use. It’s a basic literacy–one we can’t afford to overlook.',
    acceptedAnswerJsx: (
      <>
        <a
          href="https://www.codingal.com/coding-for-kids/"
          target="blank"
          rel="noreferrer"
          className="text-blue-200 underline"
        >
          Coding for kids and teens
        </a>{' '}
        helps improve their logical thinking, critical thinking, problem-solving
        skills, creativity, mathematics and writing skills and also gives them
        valuable skills in life and at the workplace. <br />
        Coding provides a competitive advantage when applying for colleges,
        internships, and jobs. In addition, basic programming knowledge can
        change how kids and teens interact daily with the technologies they use.
        It’s a basic literacy–one we can’t afford to overlook.
      </>
    ),
  },
  {
    questionName: 'How can I trust Codingal?',
    acceptedAnswerText:
      'Codingal has been founded by leading edtech entrepreneurs, IIT graduates, Google & Amazon alumni and also backed by leading investors like Y Combinator and Rebright Partners. You can know more about us here - https://www.codingal.com/about-us/. Further, we provide money back guarantee if you decide to discontinue after enrollment due to any reason.',
    acceptedAnswerJsx: (
      <>
        Codingal has been founded by leading edtech entrepreneurs, IIT
        graduates, Google & Amazon alumni and also backed by leading investors
        like Y Combinator and Rebright Partners. You can know more about us here
        -{' '}
        <a
          href="https://www.codingal.com/about-us/"
          target="blank"
          rel="noreferrer"
          className="text-blue-200 underline"
        >
          https://www.codingal.com/about-us/
        </a>
        . Further, we provide money back guarantee if you decide to discontinue
        after enrollment due to any reason.
      </>
    ),
  },
]

const AI_AND_ML: FaqType[] = [
  {
    questionName: 'What is AI and machine learning for kids?',
    acceptedAnswerText:
      'AI, or Artificial Intelligence, is all about creating smart machines that can think and learn like humans! For kids, this means exploring how computers can recognize images, understand speech, and even make decisions. Machine learning, a part of AI, teaches these machines to improve over time by learning from data. In our courses, kids can dive into fun projects, like building chatbots or games, while learning these exciting concepts in a way that’s easy to understand!',
    acceptedAnswerJsx: (
      <>
        AI, or Artificial Intelligence, is all about creating smart machines
        that can think and learn like humans! For kids, this means exploring how
        computers can recognize images, understand speech, and even make
        decisions. Machine learning, a part of AI, teaches these machines to
        improve over time by learning from data. In our courses, kids can dive
        into fun projects, like building chatbots or games, while learning these
        exciting concepts in a way that’s easy to understand!
      </>
    ),
  },
  {
    questionName: 'How can kids and teens learn artificial intelligence?',
    acceptedAnswerText:
      'Kids and teens can learn artificial intelligence through engaging courses that combine hands-on projects with interactive lessons. They can start with block-based coding platforms for younger learners, where they create fun applications using visual programming. As they grow older, they can transition to text-based coding with Python and other programming languages. Our courses offer a mix of tutorials, creative projects, and real-world applications, making learning AI both fun and informative!',
    acceptedAnswerJsx: (
      <>
        Kids and teens can learn artificial intelligence through engaging
        courses that combine hands-on projects with interactive lessons. They
        can start with block-based coding platforms for younger learners, where
        they create fun applications using visual programming. As they grow
        older, they can transition to text-based coding with Python and other
        programming languages. Our courses offer a mix of tutorials, creative
        projects, and real-world applications, making learning AI both fun and
        informative!
      </>
    ),
  },
  {
    questionName: 'Are there online machine learning classes for kids?',
    acceptedAnswerText:
      "Firstly, it's important to understand that machine learning is a subset of artificial intelligence. In our courses, we cover both AI and machine learning, providing a comprehensive learning experience for kids and teens! We offer machine learning classes specifically designed for younger learners, allowing them to study at their own pace. Using engaging platforms and tools, students can explore these complex topics in a fun and accessible way. Our courses include exciting projects that help kids understand how machine learning works while developing valuable skills for the future!",
    acceptedAnswerJsx: (
      <>
        Firstly, it&apos;s important to understand that machine learning is a
        subset of artificial intelligence. In our courses, we cover both AI and
        machine learning, providing a comprehensive learning experience for kids
        and teens! We offer machine learning classes specifically designed for
        younger learners, allowing them to study at their own pace. Using
        engaging platforms and tools, students can explore these complex topics
        in a fun and accessible way. Our courses include exciting projects that
        help kids understand how machine learning works while developing
        valuable skills for the future!
      </>
    ),
  },
  {
    questionName:
      'How long does it typically take to complete an AI and Machine Learning course?',
    acceptedAnswerText:
      'Typically, if a learner opts for 2 classes a week, they can complete the 48-lesson course in 6 months, which is what we recommend considering busy study and extracurricular schedules. However, learners can choose to take more than 2 classes per week to finish the course in 3 months on a fast track, or they can opt for fewer classes, which may extend the completion time to up to a year.',
    acceptedAnswerJsx: (
      <>
        Typically, if a learner opts for 2 classes a week, they can complete the
        48-lesson course in 6 months, which is what we recommend considering
        busy study and extracurricular schedules. However, learners can choose
        to take more than 2 classes per week to finish the course in 3 months on
        a fast track, or they can opt for fewer classes, which may extend the
        completion time to up to a year.
      </>
    ),
  },
  {
    questionName: 'Is AI & Machine Learning hard to learn?',
    acceptedAnswerText:
      'Learning AI and machine learning can be challenging, but it’s manageable with the right approach! The complexity often depends on a few factors, like your prior knowledge of programming and mathematics. However, many resources and courses are designed specifically for kids and beginners, making these topics more accessible. With engaging projects, hands-on activities, and kid-friendly platforms, learners can gradually build their understanding and skills. The key is to take it step by step and have fun while exploring the exciting world of AI and ML!',
    acceptedAnswerJsx: (
      <>
        Learning AI and machine learning can be challenging, but it’s manageable
        with the right approach! The complexity often depends on a few factors,
        like your prior knowledge of programming and mathematics. However, many
        resources and courses are designed specifically for kids and beginners,
        making these topics more accessible.
        <br />
        With engaging projects, hands-on activities, and kid-friendly platforms,
        learners can gradually build their understanding and skills. The key is
        to take it step by step and have fun while exploring the exciting world
        of AI & Machine Learning!
      </>
    ),
  },
  {
    questionName: 'What can you do with machine learning?',
    acceptedAnswerText:
      'With machine learning, you can do a wide variety of things! You can create intelligent applications that can recognize images and sounds, make predictions based on data, and even power chatbots that understand and respond to user queries. Machine learning is used in areas like healthcare for disease diagnosis, finance for fraud detection, and entertainment for personalized recommendations, like suggesting movies or songs. Essentially, it allows you to build systems that learn from data and improve over time!',
    acceptedAnswerJsx: (
      <>
        With machine learning, you can do a wide variety of things! You can
        create intelligent applications that can recognize images and sounds,
        make predictions based on data, and even power chatbots that understand
        and respond to user queries. Machine learning is used in areas like
        healthcare for disease diagnosis, finance for fraud detection, and
        entertainment for personalized recommendations, like suggesting movies
        or songs. Essentially, it allows you to build systems that learn from
        data and improve over time!
      </>
    ),
  },
  {
    questionName: 'What type of jobs can kids get after learning ML and AI?',
    acceptedAnswerText:
      'After learning machine learning and AI, kids can explore exciting career paths in technology! They might start as junior data analysts, AI assistants, or software developers. As they gain more experience, they could pursue roles like data scientists, machine learning engineers, or AI researchers. The skills learned in ML and AI are in high demand, opening doors to a variety of jobs in tech, healthcare, finance, and many other industries. Plus, these skills are valuable in many future careers, not just those directly related to AI!',
    acceptedAnswerJsx: (
      <>
        After learning machine learning and AI, kids can explore exciting career
        paths in technology! They might start as junior data analysts, AI
        assistants, or software developers. As they gain more experience, they
        could pursue roles like data scientists, machine learning engineers, or
        AI researchers. The skills learned in ML and AI are in high demand,
        opening doors to a variety of jobs in tech, healthcare, finance, and
        many other industries. Plus, these skills are valuable in many future
        careers, not just those directly related to AI!
      </>
    ),
  },
  {
    questionName:
      'Does Codingal offer kids and teens Machine Learning certificates?',
    acceptedAnswerText:
      'Yes, Codingal offers machine learning certificates for kids and teens! Upon completing our courses, students receive a certificate that showcases their skills and knowledge in machine learning and artificial intelligence. This certification can be a great addition to their educational portfolio, helping them stand out in future academic or job applications. It’s a fantastic way to recognize their hard work and accomplishments in learning these important technologies!',
    acceptedAnswerJsx: (
      <>
        Yes, Codingal offers machine learning certificates for kids and teens!
        Upon completing our courses, students receive a certificate that
        showcases their skills and knowledge in machine learning and artificial
        intelligence. This certification can be a great addition to their
        educational portfolio, helping them stand out in future academic or job
        applications. It’s a fantastic way to recognize their hard work and
        accomplishments in learning these important technologies!
      </>
    ),
  },
]

const THUNKABLE: FaqType[] = [
  {
    questionName: 'What is Thunkable in Python?',
    acceptedAnswerText:
      'Thunkable is a visual programming platform that allows users to create mobile apps without needing to write complex code. While Thunkable itself doesn’t use Python directly, it uses a block-based coding system, which is similar to Python in that it introduces logical thinking and problem-solving. Thunkable also has an option to use Python for some advanced integrations and features through custom APIs or external services, but it’s mostly geared towards block-based development for beginners and young learners.',
    acceptedAnswerJsx: (
      <>
        Thunkable is a visual programming platform that allows users to create
        mobile apps without needing to write complex code. While Thunkable
        itself doesn’t use Python directly, it uses a block-based coding system,
        which is similar to Python in that it introduces logical thinking and
        problem-solving. Thunkable also has an option to use Python for some
        advanced integrations and features through custom APIs or external
        services, but it’s mostly geared towards block-based development for
        beginners and young learners.
      </>
    ),
  },
  {
    questionName: 'How can kids and teens learn Thunkable?',
    acceptedAnswerText:
      'Kids and teens can learn Thunkable through online courses, tutorials, or workshops designed for young learners. Thunkable’s easy drag-and-drop interface makes it ideal for kids to start learning app development by creating games, useful apps, or even simple tools. Platforms like Codingal offer structured lessons where kids can build projects while learning key coding concepts.',
    acceptedAnswerJsx: (
      <>
        Kids and teens can learn Thunkable through online courses, tutorials, or
        workshops designed for young learners. Thunkable’s easy drag-and-drop
        interface makes it ideal for kids to start learning app development by
        creating games, useful apps, or even simple tools. Platforms like
        Codingal offer structured lessons where kids can build projects while
        learning key coding concepts.
      </>
    ),
  },
  {
    questionName: 'Are there online Thunkable classes for kids?',
    acceptedAnswerText:
      'Yes, there are several online Thunkable classes designed specifically for kids. Websites like Codingal, Udemy, and even Thunkable’s own resources offer step-by-step tutorials and interactive lessons to help kids and teens learn how to design, develop, and deploy their own apps using Thunkable.',
    acceptedAnswerJsx: (
      <>
        Yes, there are several online Thunkable classes designed specifically
        for kids. Websites like Codingal, Udemy, and even Thunkable’s own
        resources offer step-by-step tutorials and interactive lessons to help
        kids and teens learn how to design, develop, and deploy their own apps
        using Thunkable.
      </>
    ),
  },
  {
    questionName: 'How long does it typically take to learn Thunkable?',
    acceptedAnswerText:
      "The time it takes to learn Thunkable depends on the learner's age, experience, and how frequently they practice. For beginners, it may take a few weeks to get comfortable with the platform and complete simple projects. More advanced projects, like games or apps with AI integration, may take a few months. Generally, kids can start creating basic apps within a few hours after starting lessons.",
    acceptedAnswerJsx: (
      <>
        The time it takes to learn Thunkable depends on the learner’s age,
        experience, and how frequently they practice. For beginners, it may take
        a few weeks to get comfortable with the platform and complete simple
        projects. More advanced projects, like games or apps with AI
        integration, may take a few months. Generally, kids can start creating
        basic apps within a few hours after starting lessons.
      </>
    ),
  },
  {
    questionName: 'Is Thunkable hard for kids to learn?',
    acceptedAnswerText:
      'No, Thunkable is designed to be user-friendly and accessible for kids. It uses a drag-and-drop interface, which means kids don’t need to know how to code with complex syntax. It’s a great entry point for learning coding logic and app development. However, some advanced features (like database management or API integration) might be more challenging and require more time and practice.',
    acceptedAnswerJsx: (
      <>
        No, Thunkable is designed to be user-friendly and accessible for kids.
        It uses a drag-and-drop interface, which means kids don’t need to know
        how to code with complex syntax. It’s a great entry point for learning
        coding logic and app development. However, some advanced features (like
        database management or API integration) might be more challenging and
        require more time and practice.
      </>
    ),
  },
  {
    questionName: 'What can you do with Thunkable?',
    acceptedAnswerText: `With Thunkable, kids can create a wide range of apps, including:

        Games (like racing or puzzle games using sensors)
        Weather apps that fetch real-time data
        Health apps that calculate BMI or track fitness
        Messaging apps for chatting with friends
        Image recognition apps that detect objects
        QR code scanners for scanning and linking to websites
        AI-powered apps that can translate text or convert speech to text.
        Thunkable allows creativity to run wild, making it possible for kids to build fun, interactive, and practical apps for everyday use!`,
    acceptedAnswerJsx: (
      <>
        With Thunkable, kids can create a wide range of apps, including:
        <br />
        <ul>
          <li>Games (like racing or puzzle games using sensors)</li>
          <li>Weather apps that fetch real-time data</li>
          <li>Health apps that calculate BMI or track fitness</li>
          <li>Messaging apps for chatting with friends</li>
          <li>Image recognition apps that detect objects</li>
          <li>QR code scanners for scanning and linking to websites</li>
          <li>
            AI-powered apps that can translate text or convert speech to text.
          </li>
          <li>
            Thunkable allows creativity to run wild, making it possible for kids
            to build fun, interactive, and practical apps for everyday use!
          </li>
        </ul>
      </>
    ),
  },
]

const JAVA: FaqType[] = [
  {
    questionName: 'What is Java for Kids?',
    acceptedAnswerText:
      'Java for Kids is a beginner-friendly introduction to the fundamentals of Java programming. Java is a versatile, object-oriented language (OOP) widely used in web development, mobile apps, and more. Codingal’s course simplifies Java concepts, enabling kids to learn programming through engaging projects. Think of Java as a Swiss Army knife—just as it has tools for different tasks, Java can be used to create apps, games, websites, and even control robots!',
    acceptedAnswerJsx: (
      <>
        Java for Kids is a beginner-friendly introduction to the fundamentals of
        Java programming. Java is a versatile, object-oriented language (OOP)
        widely used in web development, mobile apps, and more. Codingal’s course
        simplifies Java concepts, enabling kids to learn programming through
        engaging projects. Think of Java as a Swiss Army knife—just as it has
        tools for different tasks, Java can be used to create apps, games,
        websites, and even control robots!
      </>
    ),
  },
  {
    questionName: 'How can kids learn Java coding?',
    acceptedAnswerText:
      'Kids can learn Java coding through interactive lessons, fun coding challenges, and real-world projects. Codingal’s structured curriculum is designed to make learning Java simple and engaging for children, with expert instructors guiding them every step of the way.',
    acceptedAnswerJsx: (
      <>
        Kids can learn Java coding through interactive lessons, fun coding
        challenges, and real-world projects. Codingal’s structured curriculum is
        designed to make learning Java simple and engaging for children, with
        expert instructors guiding them every step of the way.
      </>
    ),
  },
  {
    questionName: 'Are there online Java coding classes for kids?',
    acceptedAnswerText:
      'Yes! Codingal offers online Java coding classes specifically designed for kids. These classes provide flexibility and allow students to learn at their own pace, from anywhere, with live instruction and support.',
    acceptedAnswerJsx: (
      <>
        Yes! Codingal offers <b>online Java coding classes</b> specifically
        designed for kids. These classes provide flexibility and allow students
        to learn at their own pace, from anywhere, with live instruction and
        support.
      </>
    ),
  },
  {
    questionName: 'Is it hard to learn Java?',
    acceptedAnswerText:
      'While Java may seem challenging at first, it’s an ideal language for kids to learn because it teaches important programming concepts that are foundational for all coding. With Codingal’s beginner-friendly curriculum, Java is broken down into manageable lessons that make it fun and easy to understand.',
    acceptedAnswerJsx: (
      <>
        While Java may seem challenging at first, it’s an ideal language for
        kids to learn because it teaches important programming concepts that are
        foundational for all coding. With Codingal’s beginner-friendly
        curriculum, Java is broken down into manageable lessons that make it fun
        and easy to understand.
      </>
    ),
  },
  {
    questionName: 'What can you do with the Java programming language?',
    acceptedAnswerText:
      'Java is a powerful language used to build a wide range of applications, from websites and mobile apps to games and even robots. Learning Java opens doors for kids to create their own projects and solve real-world problems through programming.',
    acceptedAnswerJsx: (
      <>
        Java is a powerful language used to build a wide range of applications,
        from websites and mobile apps to games and even robots. Learning Java
        opens doors for kids to create their own projects and solve real-world
        problems through programming.
      </>
    ),
  },
  {
    questionName: 'Does Codingal offer certificates for Java?',
    acceptedAnswerText: `Yes! Upon completing the Java course, students receive a STEM.org-certified Coding Grandmaster certificate, which recognizes their achievements and expertise in Java programming. This certificate can be proudly shared with teachers, peers, and future academic institutions or employers.`,
    acceptedAnswerJsx: (
      <>
        Yes! Upon completing the Java course, students receive a{' '}
        <b>STEM.org-certified Coding Grandmaster certificate</b>, which
        recognizes their achievements and expertise in Java programming. This
        certificate can be proudly shared with teachers, peers, and future
        academic institutions or employers.
      </>
    ),
  },
  {
    questionName: 'What grades and age groups is the Java course suitable for?',
    acceptedAnswerText: `The Java course is designed for middle and high school students (grades 6 to 12). It’s perfect for kids who are progressing from block-based programming (often used in earlier coding stages) to text-based programming, allowing them to deepen their coding skills and tackle more complex projects. No prior coding experience is required, making it ideal for beginners ready to advance.`,
    acceptedAnswerJsx: (
      <>
        The Java course is designed for{' '}
        <b>middle and high school students (grades 6 to 12)</b>. It’s perfect
        for kids who are progressing from <b>block-based programming</b> (often
        used in earlier coding stages) to <b>text-based programming</b>,
        allowing them to deepen their coding skills and tackle more complex
        projects. No prior coding experience is required, making it ideal for
        beginners ready to advance.
      </>
    ),
  },
]

const TRINKET: FaqType[] = [
  {
    questionName: 'What is Trinket in Python?',
    acceptedAnswerText:
      'Trinket is an online platform that allows users to write, run, and share Python code directly from a web browser—no downloads or installations required. It’s especially great for beginners, offering an interactive and visual way to learn coding. With tools like Python’s Turtle module, kids can create drawings, animations, and games while instantly seeing their code come to life. Trinket makes learning Python fun, accessible, and engaging.',
    acceptedAnswerJsx: (
      <>
        Trinket is an online platform that allows users to write, run, and share
        Python code directly from a web browser—no downloads or installations
        required. It’s especially great for beginners, offering an interactive
        and visual way to learn coding. With tools like Python’s Turtle module,
        kids can create drawings, animations, and games while instantly seeing
        their code come to life. Trinket makes learning Python fun, accessible,
        and engaging.
      </>
    ),
  },
  {
    questionName: 'How can kids and teens learn Trinket?',
    acceptedAnswerText:
      'At Codingal, students learn Trinket through structured, project-based lessons designed specifically for young learners. With guidance from expert instructors, they explore coding concepts by building creative projects that encourage experimentation, problem-solving, and logical thinking.',
    acceptedAnswerJsx: (
      <>
        At <b>Codingal</b>, students learn Trinket through structured,
        project-based lessons designed specifically for young learners. With
        guidance from expert instructors, they explore coding concepts by
        building creative projects that encourage experimentation,
        problem-solving, and logical thinking.
      </>
    ),
  },
  {
    questionName: 'Are there online Trinket classes for kids?',
    acceptedAnswerText:
      'Yes! Codingal offers live, online Trinket classes for kids and teens. These interactive sessions are led by experienced coding teachers and tailored to each student’s pace, making it easy and fun to learn Python from the comfort of home.',
    acceptedAnswerJsx: (
      <>
        Yes! Codingal offers <b>live, online Trinket classes</b> for kids and
        teens. These interactive sessions are led by experienced coding teachers
        and tailored to each student’s pace, making it easy and fun to learn
        Python from the comfort of home.
      </>
    ),
  },
  {
    questionName: 'How long does it typically take to learn Trinket?',
    acceptedAnswerText:
      'Kids can start creating simple projects with Trinket in just a few sessions. A basic introduction can be completed in a few weeks, while deeper understanding and more advanced projects typically develop over a few months, depending on the child’s age and interest level.',
    acceptedAnswerJsx: (
      <>
        Kids can start creating simple projects with Trinket in just a few
        sessions. A basic introduction can be completed in a few weeks, while
        deeper understanding and more advanced projects typically develop over a
        few months, depending on the child’s age and interest level.
      </>
    ),
  },
  {
    questionName: 'Is Trinket hard for kids to learn?',
    acceptedAnswerText:
      'Not at all! Trinket is designed with beginners in mind. Its clean interface, instant feedback, and visual outputs make it very approachable for kids. Plus, with the support of Codingal’s experienced instructors, even first-time coders can quickly gain confidence and enjoy the learning process.',
    acceptedAnswerJsx: (
      <>
        Not at all! Trinket is designed with beginners in mind. Its clean
        interface, instant feedback, and visual outputs make it very
        approachable for kids. Plus, with the support of Codingal’s experienced
        instructors, even first-time coders can quickly gain confidence and
        enjoy the learning process.
      </>
    ),
  },
]

const PYGAME: FaqType[] = [
  {
    questionName: 'What is Pygame?',
    acceptedAnswerText:
      'Pygame is a tool that helps you make fun games and animations using Python, a programming language. It lets you add pictures, sounds, and actions to your games, making them interactive and exciting!',
    acceptedAnswerJsx: (
      <>
        Pygame is a tool that helps you make fun games and animations using
        Python, a programming language. It lets you add pictures, sounds, and
        actions to your games, making them interactive and exciting!
      </>
    ),
  },
  {
    questionName: 'How can kids learn Pygame?',
    acceptedAnswerText:
      'Kids can learn Pygame through online classes, where they will follow fun and simple steps to create their own games and animations. It’s a hands-on way to learn coding while having fun!',
    acceptedAnswerJsx: (
      <>
        Kids can learn Pygame through online classes, where they will follow fun
        and simple steps to create their own games and animations. It’s a
        hands-on way to learn coding while having fun!
      </>
    ),
  },
  {
    questionName: 'Are there online Pygame classes for kids?',
    acceptedAnswerText:
      'Yes, there are online classes specifically for kids to learn Pygame. These classes are designed to be fun and easy, with lots of cool projects to make, like games and animations.',
    acceptedAnswerJsx: (
      <>
        Yes, there are online classes specifically for kids to learn Pygame.
        These classes are designed to be fun and easy, with lots of cool
        projects to make, like games and animations.
      </>
    ),
  },
  {
    questionName: 'How long does it take to learn Pygame?',
    acceptedAnswerText:
      'It depends on how often you practice, but usually, kids can start making simple games in a few weeks. With more practice, you can build bigger and cooler projects!',
    acceptedAnswerJsx: (
      <>
        It depends on how often you practice, but usually, kids can start making
        simple games in a few weeks. With more practice, you can build bigger
        and cooler projects!
      </>
    ),
  },
  {
    questionName: 'Is Pygame hard for kids to learn?',
    acceptedAnswerText:
      'Pygame is designed to be beginner-friendly. If you know a little bit about how to use a computer, you can start learning and having fun right away. The lessons will guide you through everything step by step.',
    acceptedAnswerJsx: (
      <>
        Pygame is designed to be beginner-friendly. If you know a little bit
        about how to use a computer, you can start learning and having fun right
        away. The lessons will guide you through everything step by step.
      </>
    ),
  },
  {
    questionName: 'What can I make with Pygame?',
    acceptedAnswerText: `You can make all sorts of cool things with Pygame! Create simple games like “Space Invaders,” “Snake,” or even your own animated stories. It’s like bringing your ideas to life!`,
    acceptedAnswerJsx: (
      <>
        You can make all sorts of cool things with Pygame! Create simple games
        like “Space Invaders,” “Snake,” or even your own animated stories. It’s
        like bringing your ideas to life!
      </>
    ),
  },
  {
    questionName: 'Do I need to know Python before starting with Pygame?',
    acceptedAnswerText: `No worries! You don’t need to know Python before starting with Pygame. Our classes will teach you the basics of Python while you learn to make fun games!`,
    acceptedAnswerJsx: (
      <>
        No worries! You don’t need to know Python before starting with Pygame.
        Our classes will teach you the basics of Python while you learn to make
        fun games!
      </>
    ),
  },
  {
    questionName: 'Can I make my own games with Pygame?',
    acceptedAnswerText: `Yes, absolutely! You can create your own games with Pygame, like platformers, puzzles, or even fun animations where things move around based on your commands.`,
    acceptedAnswerJsx: (
      <>
        Yes, absolutely! You can create your own games with Pygame, like
        platformers, puzzles, or even fun animations where things move around
        based on your commands.
      </>
    ),
  },
  {
    questionName: 'Is Pygame used to make big games?',
    acceptedAnswerText: `Pygame is great for beginners and small games. Big games, like the ones you play on consoles, need more advanced tools, but Pygame is the perfect way to start learning how games work!`,
    acceptedAnswerJsx: (
      <>
        Pygame is great for beginners and small games. Big games, like the ones
        you play on consoles, need more advanced tools, but Pygame is the
        perfect way to start learning how games work!
      </>
    ),
  },
  {
    questionName: 'How does Pygame help kids learn problem-solving?',
    acceptedAnswerText: `When you make games with Pygame, you’ll run into fun challenges! Solving these problems will help you think like a programmer—breaking big tasks into smaller ones and figuring out the best way to fix any mistakes along the way.`,
    acceptedAnswerJsx: (
      <>
        When you make games with Pygame, you’ll run into fun challenges! Solving
        these problems will help you think like a programmer—breaking big tasks
        into smaller ones and figuring out the best way to fix any mistakes
        along the way.
      </>
    ),
  },
]

const ANIMATION: FaqType[] = [
  {
    questionName: 'What is Animation?',
    acceptedAnswerText:
      'Animation is the process of creating the illusion of movement by displaying a sequence of images or objects. It combines creativity and technology to bring characters, objects, and stories to life. For example, beloved cartoons like Tom and Jerry and Mickey Mouse use animation to create lifelike movements and expressions, making the characters feel real and relatable to audiences of all ages.',
    acceptedAnswerJsx: (
      <>
        Animation is the process of creating the illusion of movement by
        displaying a sequence of images or objects. It combines creativity and
        technology to bring characters, objects, and stories to life. For
        example, beloved cartoons like Tom and Jerry and Mickey Mouse use
        animation to create lifelike movements and expressions, making the
        characters feel real and relatable to audiences of all ages.
      </>
    ),
  },
  {
    questionName: 'How can kids and teens learn Animation?',
    acceptedAnswerText:
      'Kids and teens can learn animation through beginner-friendly tools and platforms like Scratch, Tynker, or Roblox Studio. They can also take courses designed for their age group, where they explore concepts like motion, storytelling, and effects using block-based and text-based programming.',
    acceptedAnswerJsx: (
      <>
        Kids and teens can learn animation through beginner-friendly tools and
        platforms like Scratch, Tynker, or Roblox Studio. They can also take
        courses designed for their age group, where they explore concepts like
        motion, storytelling, and effects using block-based and text-based
        programming.
      </>
    ),
  },
  {
    questionName: 'Are there online Animation classes for kids?',
    acceptedAnswerText:
      'Yes, many online platforms offer animation classes for kids. These include interactive courses tailored to different skill levels, often focusing on tools like Scratch, Roblox, or Python, and covering essential animation principles.',
    acceptedAnswerJsx: (
      <>
        Yes, many online platforms offer animation classes for kids. These
        include interactive courses tailored to different skill levels, often
        focusing on tools like Scratch, Roblox, or Python, and covering
        essential animation principles.
      </>
    ),
  },
  {
    questionName: 'How long does it typically take to learn Animation?',
    acceptedAnswerText:
      'The time it takes to learn animation varies based on the complexity and platform. Kids can pick up basic animation concepts and create simple projects in just a few weeks, while mastering advanced techniques may take a few months of consistent practice.',
    acceptedAnswerJsx: (
      <>
        The time it takes to learn Thunkable depends on the learner’s age,
        experience, and how frequently they practice. For beginners, it may take
        a few weeks to get comfortable with the platform and complete simple
        projects. More advanced projects, like games or apps with AI
        integration, may take a few months. Generally, kids can start creating
        basic apps within a few hours after starting lessons.
      </>
    ),
  },
  {
    questionName: 'Is Animation hard for kids to learn?',
    acceptedAnswerText:
      'Animation is not hard for kids to learn, especially when taught with age-appropriate tools and methods. Platforms like Scratch use block-based coding, making it intuitive and enjoyable for kids to create animations and learn essential skills.',
    acceptedAnswerJsx: (
      <>
        With animation, you can create fun stories, design interactive games,
        develop educational content, and even explore career paths in digital
        art, gaming, and media production. It’s a versatile skill that combines
        creativity and technical know-how.
      </>
    ),
  },
  {
    questionName: 'What can you do with Animation?',
    acceptedAnswerText:
      'With animation, you can create fun stories, design interactive games, develop educational content, and even explore career paths in digital art, gaming, and media production. It’s a versatile skill that combines creativity and technical know-how.',
    acceptedAnswerJsx: (
      <>
        With animation, you can create fun stories, design interactive games,
        develop educational content, and even explore career paths in digital
        art, gaming, and media production. It’s a versatile skill that combines
        creativity and technical know-how.
      </>
    ),
  },
  {
    questionName: 'What are the benefits of learning Animation for kids?',
    acceptedAnswerText: `Animation helps kids develop creativity, problem-solving skills, and an understanding of technology. It also improves storytelling abilities and logical thinking.`,
    acceptedAnswerJsx: (
      <>
        Animation helps kids develop creativity, problem-solving skills, and an
        understanding of technology. It also improves storytelling abilities and
        logical thinking.
      </>
    ),
  },
  {
    questionName: 'What tools are best for kids learning Animation?',
    acceptedAnswerText: `Some great tools for kids include Scratch, Tynker, Roblox Studio, Pencil2D, and block-based animation tools. For older kids, platforms like Blender or Adobe Animate can be introduced.`,
    acceptedAnswerJsx: (
      <>
        Some great tools for kids include Scratch, Tynker, Roblox Studio,
        Pencil2D, and block-based animation tools. For older kids, platforms
        like Blender or Adobe Animate can be introduced.
      </>
    ),
  },
  {
    questionName: 'At what age can kids start learning Animation?',
    acceptedAnswerText:
      'Kids as young as 7 or 8 can start learning basic animation concepts using simple tools like Scratch or Tynker. Older kids can explore more advanced techniques and platforms.',
    acceptedAnswerJsx: (
      <>
        Kids as young as 7 or 8 can start learning basic animation concepts
        using simple tools like Scratch or Tynker. Older kids can explore more
        advanced techniques and platforms.
      </>
    ),
  },
  {
    questionName: 'Can learning Animation help in other subjects?',
    acceptedAnswerText: `Yes, learning animation can complement subjects like math, science, and art by teaching concepts such as geometry, physics (motion and timing), and visual design.`,
    acceptedAnswerJsx: (
      <>
        Yes, learning animation can complement subjects like math, science, and
        art by teaching concepts such as geometry, physics (motion and timing),
        and visual design.
      </>
    ),
  },
  {
    questionName: 'How does Animation prepare kids for the future?',
    acceptedAnswerText: `Animation equips kids with technical and creative skills that are valuable in fields like game development, digital media, advertising, and software design. It also fosters critical thinking and collaboration.`,
    acceptedAnswerJsx: (
      <>
        Animation equips kids with technical and creative skills that are
        valuable in fields like game development, digital media, advertising,
        and software design. It also fosters critical thinking and
        collaboration.
      </>
    ),
  },
  {
    questionName: 'What’s the difference between 2D and 3D Animation?',
    acceptedAnswerText: `Some great tools for kids include Scratch, Tynker, Roblox Studio, Pencil2D, and block-based animation tools. For older kids, platforms like Blender or Adobe Animate can be introduced.`,
    acceptedAnswerJsx: (
      <>
        2D animation involves flat, two-dimensional drawings that move on a
        single plane, while 3D animation creates objects with depth, allowing
        them to move in a virtual three-dimensional space. Both can be explored
        by kids using age-appropriate tools.
      </>
    ),
  },
]

const TURTLE: FaqType[] = [
  {
    questionName: 'What is Python Turtle?',
    acceptedAnswerText:
      'Python Turtle is a module in Python that allows students to draw graphics and create animations by controlling a virtual "turtle" on the screen.',
    acceptedAnswerJsx: (
      <>
        Python Turtle is a module in Python that allows students to draw
        graphics and create animations by controlling a virtual
        &quot;turtle&quot; on the screen.
      </>
    ),
  },
  {
    questionName: 'How can my child learn Python Turtle?',
    acceptedAnswerText:
      'At Codingal, your child will learn Python Turtle through interactive lessons and hands-on projects. Our curriculum starts with simple shapes and gradually moves to complex designs.',
    acceptedAnswerJsx: (
      <>
        At Codingal, your child will learn Python Turtle through interactive
        lessons and hands-on projects. Our curriculum starts with simple shapes
        and gradually moves to complex designs.
      </>
    ),
  },
  {
    questionName: 'Is Python Turtle suitable for young beginners?',
    acceptedAnswerText:
      'Yes, Python Turtle is perfect for elementary school students and beginners. It introduces coding concepts in a fun and visual way.',
    acceptedAnswerJsx: (
      <>
        Yes, Python Turtle is perfect for elementary school students and
        beginners. It introduces coding concepts in a fun and visual way.
      </>
    ),
  },
  {
    questionName: 'How long does it take to learn Python Turtle?',
    acceptedAnswerText:
      'Most students complete the Python Turtle course in 6 to 8 weeks. The pace is flexible and based on the learner’s progress.',
    acceptedAnswerJsx: (
      <>
        Most students complete the Python Turtle course in 6 to 8 weeks. The
        pace is flexible and based on the learner’s progress.
      </>
    ),
  },
  {
    questionName: 'Can my child use Python Turtle on any device?',
    acceptedAnswerText:
      'Yes, Python Turtle works on both Windows and Mac computers. It can be run on any device that supports Python.',
    acceptedAnswerJsx: (
      <>
        Yes, Python Turtle works on both Windows and Mac computers. It can be
        run on any device that supports Python.
      </>
    ),
  },
  {
    questionName: 'What kind of projects will my child work on in the course?',
    acceptedAnswerText:
      'Projects include drawing shapes, geometric patterns, interactive designs, and recursive patterns, helping kids apply core coding concepts.',
    acceptedAnswerJsx: (
      <>
        Projects include drawing shapes, geometric patterns, interactive
        designs, and recursive patterns, helping kids apply core coding
        concepts.
      </>
    ),
  },
  {
    questionName: 'Do I need any prior coding experience for Python Turtle?',
    acceptedAnswerText:
      'No prior experience is needed. The Python Turtle course is beginner-friendly and perfect for kids starting their coding journey.',
    acceptedAnswerJsx: (
      <>
        No prior experience is needed. The Python Turtle course is
        beginner-friendly and perfect for kids starting their coding journey.
      </>
    ),
  },
  {
    questionName:
      'Will my child get a certificate after completing the course?',
    acceptedAnswerText:
      'Yes, your child will receive a Codingal Python Turtle Developer Certificate upon completion.',
    acceptedAnswerJsx: (
      <>
        Yes, your child will receive a Codingal Python Turtle Developer
        Certificate upon completion.
      </>
    ),
  },
  {
    questionName: 'How can I get started with Python Turtle for my child?',
    acceptedAnswerText:
      'You can start by signing up for a free trial class with Codingal. Our expert instructors will guide your child through Python Turtle.',
    acceptedAnswerJsx: (
      <>
        You can start by signing up for a free trial class with Codingal. Our
        expert instructors will guide your child through Python Turtle.
      </>
    ),
  },
]

const MIT_APP_INVENTOR: FaqType[] = [
  {
    questionName: 'What is MIT App Inventor for Kids?',
    acceptedAnswerText:
      'MIT App Inventor is a block-based coding platform where kids can build real mobile apps using visual logic—no prior experience needed.',
    acceptedAnswerJsx: (
      <>
        MIT App Inventor is a block-based coding platform where kids can build
        real mobile apps using visual logic—no prior experience needed.
      </>
    ),
  },
  {
    questionName: 'How can kids learn MIT App Inventor coding?',
    acceptedAnswerText:
      'With Codingal’s guided curriculum, live classes, and expert teachers, kids learn to build apps step-by-step in a fun and interactive way.',
    acceptedAnswerJsx: (
      <>
        With Codingal’s guided curriculum, live classes, and expert teachers,
        kids learn to build apps step-by-step in a fun and interactive way.
      </>
    ),
  },
  {
    questionName: 'Are there online MIT App Inventor classes for kids?',
    acceptedAnswerText:
      'Yes, Codingal offers 100% online MIT App Inventor classes with live, interactive lessons accessible from anywhere.',
    acceptedAnswerJsx: (
      <>
        Yes, Codingal offers 100% online MIT App Inventor classes with live,
        interactive lessons accessible from anywhere.
      </>
    ),
  },
  {
    questionName: 'How long does it typically take to complete a course?',
    acceptedAnswerText:
      'Most students complete the MIT App Inventor course in 12–16 weeks depending on pace and depth of projects.',
    acceptedAnswerJsx: (
      <>
        Most students complete the MIT App Inventor course in 12–16 weeks
        depending on pace and depth of projects.
      </>
    ),
  },
  {
    questionName: 'Is MIT App Inventor programming hard to learn?',
    acceptedAnswerText:
      'No! The drag-and-drop interface makes it beginner-friendly and perfect for kids aged 8–16.',
    acceptedAnswerJsx: (
      <>
        No! The drag-and-drop interface makes it beginner-friendly and perfect
        for kids aged 8–16.
      </>
    ),
  },
  {
    questionName: 'What can you do with MIT App Inventor?',
    acceptedAnswerText:
      'Kids can create Android apps like calculators, quizzes, drawing tools, and even AI-enabled apps like those using speech recognition.',
    acceptedAnswerJsx: (
      <>
        Kids can create Android apps like calculators, quizzes, drawing tools,
        and even AI-enabled apps like those using speech recognition.
      </>
    ),
  },
  {
    questionName: 'What type of jobs can kids get after learning this?',
    acceptedAnswerText:
      'While a beginner platform, MIT App Inventor builds a foundation for careers in app development, software engineering, and UI/UX design.',
    acceptedAnswerJsx: (
      <>
        While a beginner platform, MIT App Inventor builds a foundation for
        careers in app development, software engineering, and UI/UX design.
      </>
    ),
  },
  {
    questionName: 'Does Codingal provide certificates for this course?',
    acceptedAnswerText:
      'Yes! Students receive a Developer Certificate from Codingal upon completion, backed by STEM.org accreditation.',
    acceptedAnswerJsx: (
      <>
        Yes! Students receive a Developer Certificate from Codingal upon
        completion, backed by STEM.org accreditation.
      </>
    ),
  },
]

const BOOTSTRAP: FaqType[] = [
  {
    questionName: 'What is Bootstrap?',
    acceptedAnswerText:
      'Bootstrap is a front-end framework with pre-designed templates and components that helps create responsive websites quickly.',
    acceptedAnswerJsx: (
      <>
        Bootstrap is a front-end framework with pre-designed templates and
        components that helps create responsive websites quickly.
      </>
    ),
  },
  {
    questionName: 'How can kids and teens learn Bootstrap?',
    acceptedAnswerText:
      'They can learn through interactive lessons and hands-on projects that teach how to use components to design mobile-friendly websites.',
    acceptedAnswerJsx: (
      <>
        They can learn through interactive lessons and hands-on projects that
        teach how to use components to design mobile-friendly websites.
      </>
    ),
  },
  {
    questionName: 'Are there online Bootstrap classes for kids?',
    acceptedAnswerText:
      'Yes! Codingal offers online classes that teach Bootstrap along with other web development tools in an interactive, personalized way.',
    acceptedAnswerJsx: (
      <>
        Yes! Codingal offers online classes that teach Bootstrap along with
        other web development tools in an interactive, personalized way.
      </>
    ),
  },
  {
    questionName: 'How long does it typically take to learn Bootstrap?',
    acceptedAnswerText:
      'Kids can grasp the basics of Bootstrap in 4–6 weeks and gradually build more complex projects over time.',
    acceptedAnswerJsx: (
      <>
        Kids can grasp the basics of Bootstrap in 4–6 weeks and gradually build
        more complex projects over time.
      </>
    ),
  },
  {
    questionName: 'Is Bootstrap hard for kids to learn?',
    acceptedAnswerText:
      'No. Bootstrap is beginner-friendly and easy to learn with structured lessons and guided practice.',
    acceptedAnswerJsx: (
      <>
        No. Bootstrap is beginner-friendly and easy to learn with structured
        lessons and guided practice.
      </>
    ),
  },
  {
    questionName: 'What can you do with Bootstrap?',
    acceptedAnswerText:
      'Students can build responsive websites using ready-made components like buttons, forms, and navigation menus.',
    acceptedAnswerJsx: (
      <>
        Students can build responsive websites using ready-made components like
        buttons, forms, and navigation menus.
      </>
    ),
  },
  {
    questionName: 'How does Bootstrap help with web development?',
    acceptedAnswerText:
      'Bootstrap provides ready-made components that speed up development and let students focus on structure and design.',
    acceptedAnswerJsx: (
      <>
        Bootstrap provides ready-made components that speed up development and
        let students focus on structure and design.
      </>
    ),
  },
  {
    questionName: 'Can kids create their own websites with Bootstrap?',
    acceptedAnswerText:
      'Yes! Kids can build their own professional-looking websites that are fully responsive and mobile-friendly.',
    acceptedAnswerJsx: (
      <>
        Yes! Kids can build their own professional-looking websites that are
        fully responsive and mobile-friendly.
      </>
    ),
  },
  {
    questionName: 'What age group is suitable for learning Bootstrap?',
    acceptedAnswerText:
      'Bootstrap is great for middle and high schoolers with basic HTML/CSS knowledge. Younger students may need an introduction first.',
    acceptedAnswerJsx: (
      <>
        Bootstrap is great for middle and high schoolers with basic HTML/CSS
        knowledge. Younger students may need an introduction first.
      </>
    ),
  },
  {
    questionName: 'What skills do kids develop by learning Bootstrap?',
    acceptedAnswerText:
      'Kids develop web design, layout, coding, problem-solving, and design thinking skills through Bootstrap.',
    acceptedAnswerJsx: (
      <>
        Kids develop web design, layout, coding, problem-solving, and design
        thinking skills through Bootstrap.
      </>
    ),
  },
  {
    questionName: 'How does learning Bootstrap benefit future careers?',
    acceptedAnswerText:
      'It equips students with real-world skills for tech careers like software development, design, and freelancing.',
    acceptedAnswerJsx: (
      <>
        It equips students with real-world skills for tech careers like software
        development, design, and freelancing.
      </>
    ),
  },
]

export const FAQ: Record<CourseNameType, FaqType[]> = {
  SCRATCH_PROGRAMMING: SCRATCH_PROGRAMMING_FAQ,
  PYTHON_FOR_KIDS: PYTHON_FOR_KIDS_FAQ,
  ROBLOX: ROBLOX,
  AP_COMPUTER_SCIENCE_A: AP_COMPUTER_SCIENCE_A,
  AI_AND_ML: AI_AND_ML,
  THUNKABLE: THUNKABLE,
  JAVA: JAVA,
  TRINKET: TRINKET,
  PYGAME: PYGAME,
  ANIMATION: ANIMATION,
  TURTLE: TURTLE,
  MIT_APP_INVENTOR: MIT_APP_INVENTOR,
  BOOTSTRAP: BOOTSTRAP,
}

export const COURSE_MODULE_DATA: Record<CourseNameType, ModuleType[]> = {
  SCRATCH_PROGRAMMING: [
    {
      title: 'Scratch Programming Fundamentals',
      moduleNumber: 1,
      logo: '/images/courses/scratch-programming/scratch-module-1.png',
      outline:
        'Get introduced to the Scratch platform and understand the block-based coding interface.',
      lessons: [
        {
          title: 'Introduction to Scratch',
          outline:
            'Build your first Scratch project, play with sprites, and learn controls and navigation.',
        },
        {
          title: 'Direction Sense',
          outline:
            'Understand the concept of time and direction by learning how and when to move the sprites.',
        },
        {
          title: 'Moving and gliding',
          outline: 'Learn to build games using move and glide blocks.',
        },
        {
          title: 'Tour the Solar System',
          outline: 'Build a block-based project around the solar system.',
        },
        {
          title: 'Transitions',
          outline:
            'Learn the concept of transition and motion to form logical connections between knowledge and interaction.',
        },
        {
          title: 'Conditional statements and Loops',
          outline:
            'Learn about conditional statements (if-else) and loops (for,repeat) in Scratch.',
        },
      ],
    },
    {
      title: 'Scratch Intermediate',
      moduleNumber: 2,
      logo: '/images/courses/scratch-programming/scratch-module-2.png',
      outline:
        'Get introduced to coordinates, understand the pen feature, and learn to use the broadcast block.',
      lessons: [
        {
          title: 'Mario Dash',
          outline:
            'Create a super fun Mario Dash game while understanding the use of sensing operators ',
        },
        {
          title: 'Catch Game',
          outline:
            'You will learn to create clones and use them in a project while making a catch game in Scratch.',
        },
        {
          title: 'DJ Cat',
          outline: 'Create a fantastic DJ project using looks and sounds.',
        },
        {
          title: 'Drawing with Pen',
          outline: 'Draw with pen blocks in Scratch.',
        },
        {
          title: 'Interlink Characters',
          outline:
            'Interlink different characters present on the stage using broadcast in Scratch.',
        },
        {
          title: 'Create a quiz',
          outline: 'Create a quiz using variables and broadcast.',
        },
      ],
    },
    {
      title: 'Scratch advance',
      moduleNumber: 3,
      logo: '/images/courses/scratch-programming/scratch-module-3.png',
      outline:
        'Learn to clone your sprites and understand the conditional statements and number system.',
      lessons: [
        {
          title: 'Snake and Food',
          outline: 'Create a Snake and food game using the concept of cloning.',
        },
        {
          title: 'Integer game',
          outline: 'Create integer games using a number line in Scratch.',
        },
        {
          title: 'Fundamentals of game building',
          outline:
            'Learn the basics of game building and how to make a car game in Scratch.',
        },
        {
          title: 'Car Game',
          outline:
            'Get introduced to Functions in Scratch. Build a Car Game using the concepts of motion.',
        },
        {
          title: 'Hungry Casey I',
          outline:
            'Create a firing game,"Hungry Casey” using variables and cloning in Scratch.',
        },
        {
          title: 'Hungry Casey II',
          outline:
            'Create a firing game,"Hungry Casey” using variables and cloning in Scratch.',
        },
      ],
    },
    {
      title: 'Scratch Specialization',
      moduleNumber: 4,
      logo: '/images/courses/scratch-programming/scratch-module-4.png',
      outline:
        'Introduction to animation effects, create an advanced level of games, and learn advanced features like text to speech.',
      lessons: [
        {
          title: 'Build your microscope',
          outline: 'Build a microscope using Scratch.',
        },
        {
          title: 'My Al friend GIGA',
          outline: 'Make an Artificial intelligence friend GIGA in Scratch.',
        },
        {
          title: 'Pacman I',
          outline: 'Create the popular Pacman game in Scratch.',
        },
        {
          title: 'Pacman II',
          outline: 'Create the popular Pacman game in Scratch.',
        },
        {
          title: 'Paint I',
          outline:
            'Create a paint project in Scratch using concepts learned so far.',
        },
        {
          title: 'Paint II',
          outline:
            'Create a paint project in Scratch using concepts learned so far.',
        },
      ],
    },
    {
      title: 'Artificial Intelligence - I',
      moduleNumber: 5,
      logo: '/images/courses/scratch-programming/scratch-module-5.png',
      outline:
        'Introduction to the magical world of Artificial Intelligence. Build projects using the text, sound, and image training.',
      lessons: [
        {
          title: 'Introduction to Artificial Intelligence',
          outline:
            'Learn Artificial Intelligence using some real-life examples. Introduction to a new platform called machine learning for kids.',
        },
        {
          title: 'Application Programming Interface',
          outline: 'Learn about Application Programming Interface (API).',
        },
        {
          title: 'Introduction to Machine Learning',
          outline:
            'Understand the difference between Machine Learning and Deep learning. Learn to train the computer to recognize sounds.',
        },
        {
          title: 'Types of Machine Learning',
          outline:
            'Learn about the types and categories of machine learning with multiple examples. Train the computer to recognize colors.',
        },
        {
          title: 'Deep Learning',
          outline:
            'Understand Deep learning, Image Processing, and Image Recognition. Train the computer to recognize handwriting.',
        },
        {
          title: 'Image classification',
          outline:
            'Learn about Image classification and teach a computer how to recognize handwriting.',
        },
      ],
    },
    {
      title: 'Artificial Intelligence - II',
      moduleNumber: 6,
      logo: '/images/courses/scratch-programming/scratch-module-6.png',
      outline:
        'Create your own Al project by understanding Artificial Intelligence with the help of text, vision training and speech recognition.',
      lessons: [
        {
          title: 'Speech Recognition',
          outline:
            'Understand the difference between speech and voice recognition, create a spy project in Scratch that can recognize your speech.',
        },
        {
          title: 'Supervised learning',
          outline:
            'Understand what is supervised learning and train computers to recognize the language.',
        },
        {
          title: 'Create Riddles',
          outline: 'Train your models to create Riddles.',
        },
        {
          title: 'Overfitting',
          outline:
            'Learn about overfitting and how computers can make mistakes if poorly trained.',
        },
        {
          title: 'Neural  Networks I',
          outline:
            'Learn about the Neural Networks concept. Create a Rock Paper Scissors game using all the concepts you have learned in this module.',
        },
        {
          title: 'Rock Paper Scissors Game',
          outline:
            'Learn about the Neural Networks concept. Create a Rock Paper Scissors game using all the concepts you have learned in this module.',
        },
      ],
    },
  ],
  HPE_CODEWARS_HACKATHON: [
    {
      title: 'Frontend development',
      moduleNumber: 1,
      logo: '/images/courses/hpe-codewars-hackathon/module-1.png',
      outline:
        'Introduction to HTML, links, tables, media and semantic elements in HTML. You will also learn forms in HTML and get introduced to CSS, background effects and text styling properties along with psuedo classes and box model.',
      lessons: [
        {
          title: 'Introduction to HTML',
          outline:
            'You will be introduced to the structure of HTML, br tag, comments, header tags, paragraph, formatting tags, block quotes, semantics, section and article.',
        },
        {
          title: 'Links, tables, media and semantic elements in HTML',
          outline:
            'Get introduced to hyperlink, list types, table, image, audio, videos, media, footer and nav tag.',
        },
        {
          title: 'Forms in HTML',
          outline:
            'You will learn about HTML form, form elements, name attributes and input attributes.',
        },
        {
          title: 'Introduction to CSS',
          outline:
            'Learn about basics of CSS and types of CSS & selectors along with the comments in CSS.',
        },
        {
          title: 'Background effects and text styling properties',
          outline:
            'In this lesson you will learn about CSS background and color, CSS border, CSS gradients, CSS Fonts, CSS text-decoration and text-transform.',
        },
        {
          title: 'Pseudo classes',
          outline:
            'You will learn about Pseudo classes, CSS forms,navigation bar, CSS Float',
        },
      ],
    },
    {
      title: 'Advance Frontend development',
      moduleNumber: 2,
      logo: '/images/courses/hpe-codewars-hackathon/module-2.png',
      outline:
        'Introduction to box model, positioning, opacity, bootstrap, containers in bootstrap, bootstrap utilities, syntax guide and commenting, variables. You will also learn functions in JS, scope of variable in functions, methods, loops, switch and website demo day.',
      lessons: [
        {
          title: 'Box model',
          outline:
            'In this lesson you will learn about box model, positioning and opacity.',
        },
        {
          title: 'Introduction to Bootstrap',
          outline:
            'In this lesson you will learn get introduced to bootstrap, containers in bootstrap',
        },
        {
          title: 'Bootstrap utilities',
          outline: 'Get introduced bootstrap utilities.',
        },
        {
          title: 'Introduction to JavaScript',
          outline:
            'In this lesson you will get introduced syntax guide and commenting, variables, Functions in JS, Scope of variable in functions.',
        },
        {
          title: 'Introduction to methods, loops and switches in JavaScript',
          outline:
            'You will get introduced to methods, loops and switches in JavaScript.',
        },
        {
          title: 'Website Demo Day',
          outline: 'Website Demo Day and get ready to build your project.',
        },
      ],
    },
  ],
  HPE_CODEWARS_CODE_BATTLE: [
    {
      title: 'Basics of implementation of Mathematics',
      moduleNumber: 1,
      logo: '/images/courses/hpe-codewars-code-battle/module-1.png',
      outline:
        'Introduction to Python, print statements, variables, and operators Introduction to conditional statement & various loops in Python Introduction to functions in Python.',
      lessons: [
        {
          title: 'Welcome to Python programming',
          outline:
            'You will be introduced to Python programming, print statements, variables, and operators.',
        },
        {
          title: 'Loops in Python',
          outline:
            'Get introduced to conditional statements and various loops in Python. ',
        },
        {
          title: 'Introduction to functions in Python',
          outline:
            'You will get well equipped with the basics of functions in Python.',
        },
        {
          title: 'Data Structures in Python',
          outline:
            'Learn about Operations on list, Python data structures - lists, tuples and their functions.',
        },
        {
          title: 'Mathematical magic',
          outline:
            'Learn about Palindrome number, Computing power a^b, Prime number and its properties, Armstrong numbers, Factors of a number.',
        },
        {
          title: 'Algorithm & Array',
          outline:
            'You will learn about important algorithms based on odd occurring numbers in an array and how to find them using bit manipulation.',
        },
      ],
    },
    {
      title: 'Recursion, array, searching and sorting',
      moduleNumber: 2,
      logo: '/images/courses/hpe-codewars-code-battle/module-1.png',
      outline:
        'Learn the concepts of recursions, arrays and algorithms. solve problems and get ready for the challenge.',
      lessons: [
        {
          title: 'Recursive problems',
          outline:
            'In this lesson  you will learn about recursive problems and algorithms.',
        },
        {
          title: 'Array problems',
          outline:
            'In this lesson you will learn about reverse arrays in groups, array problems and algorithms.',
        },
        {
          title: 'Selection and bubble sort',
          outline:
            'Learn about the Kadane algorithm and how you can use it to find the maximum array sum and its applications, bubble sort and selection sort.',
        },
        {
          title: 'Insertion and Quick sort',
          outline:
            'In this lesson you will solve general problems using algorithms in the data structure, insertion sort and quick sort.',
        },
        {
          title: 'Comparison between Sorting Algorithms',
          outline:
            'You will learn what is a merge sort and how the sorting algorithm differs from each other based on time and space complexity.',
        },
        {
          title: 'Practice problems',
          outline: 'Solve practice problems and get ready for the finale.',
        },
      ],
    },
  ],
  PYTHON_FOR_KIDS: [
    {
      title: 'Python Basics',
      moduleNumber: 1,
      outline:
        'Get introduced to Python, including data types, operators, and conditional statements, and write basic codes in Python.',
      logo: '/images/courses/python-for-kids/modules/module-1.png',
      lessons: [
        {
          title: 'Welcome to Python programming',
          outline:
            'Learn about computer programs, different programming languages, introduction to Python, the history of Python, algorithms, flowcharts, and repl.it platform.',
        },
        {
          title: 'Getting started with programming',
          outline:
            'Introduction to print statements, identifiers, keywords, and variables in Python.',
        },
        {
          title: 'Data types in Python',
          outline:
            'Learn about data types, typecasting, user input, and string operations.',
        },
        {
          title: 'Python operators I',
          outline:
            'Learn about Python operators and the application of various basic operators in Python.',
        },
        {
          title: 'Conditional statements',
          outline:
            'Learn about decision-making statements in Python programs. Also, learn to use the if-else statement.',
        },
        {
          title: 'Python operators II',
          outline:
            'Learn about conditional statements if, elif, else, and the logical operators used in Python programming.',
        },
        {
          title: 'Python operators III',
          outline:
            'Learn about identity operators, membership operators, and bitwise operators and the application of these operators in Python programming.',
        },
        {
          title: 'Python challenge',
          outline:
            'Learn to write programs using all the concepts learned in the module.',
        },
      ],
    },
    {
      title: 'Loops in Python',
      moduleNumber: 2,
      outline:
        'Learn about conditional statements, understand essential programming concepts, i.e., loops and nested conditional statements, and use Python library Turtle for creating graphics.',
      logo: '/images/courses/python-for-kids/modules/module-2.png',
      lessons: [
        {
          title: 'Nested conditional statements',
          outline:
            'Learn about nested conditional statements, the basics of conditional statements, and work on nested conditional statements in Python programming.',
        },
        {
          title: 'Loops',
          outline:
            'Learn about programming loops and their types, the use of loops in programs, and how to work with them to solve various problems.',
        },
        {
          title: 'While loop',
          outline:
            'Get introduced to the while loop, how and when to use them in your Python program.',
        },
        {
          title: 'Nested loop',
          outline:
            'Learn about nested loops, how to use nested loops, and the syntax of the nested loops in the Python program.',
        },
        {
          title: 'Patterns',
          outline:
            'Get introduced to nested loop’s application and how to make exciting patterns through nested loops in Python programming.',
        },
        {
          title: 'Introduction to turtle',
          outline:
            'Learn about Turtle library in Python to create graphics and learn some essential functions used in the Turtle library.',
        },
      ],
    },
    {
      title: 'Python Functions and Modules',
      moduleNumber: 3,
      outline:
        'Learn about the concept of functions in Python and exceptions in programming.',
      logo: '/images/courses/python-for-kids/modules/module-3.png',
      lessons: [
        {
          title: 'Function',
          outline:
            'Get well equipped with knowledge of the basics of functions in Python.',
        },
        {
          title: 'Arguments',
          outline:
            'Get introduced to arguments in Python functions, types of arguments, recursion, and docstring here.',
        },
        {
          title: 'Keywords',
          outline:
            'Learn about a few essential keywords like return, continue, break, and pass in a Python program.',
        },
        {
          title: 'Exception Handling',
          outline:
            'Learn about exception handling, various error handling techniques, and essential exceptions in Python.',
        },
        {
          title: 'Random and math module',
          outline:
            'Learn about the Random and math module, know about predefined and essential methods in your Python program that these libraries provide.',
        },
        {
          title: 'Date, time, and calendar',
          outline:
            "Learn about the Python program's date, time, and calendar module.",
        },
      ],
    },
    {
      title: 'Data Structures in Python',
      moduleNumber: 4,
      outline:
        "Learn about the concept of data structures, requirements, and the details of Python's most commonly used data structures. In this module, you will also learn how to debug the code.",
      logo: '/images/courses/python-for-kids/modules/module-4.png',
      lessons: [
        {
          title: 'Getting started with Lists',
          outline:
            'Get introduced to data structures, create a list, and perform operations on a list.',
        },
        {
          title: "It's a tuple!",
          outline: 'Learn about tuple and perform operations on a tuple.',
        },
        {
          title: 'All about Dictionary',
          outline:
            'Get introduced to Python data structure - Dictionaries and perform various operations and iterations on the dictionary in Python.',
        },
        {
          title: 'Sets and Arrays',
          outline:
            'Learn about sets and arrays used in Python and perform different operations on sets and arrays.',
        },
        {
          title: 'Advanced Python functions',
          outline:
            'Learn advanced functions used in Python programming like - map, zip, and exit, also the concepts of list comprehension and dictionary comprehension.',
        },
        {
          title: 'Create games',
          outline:
            'Create games like Tic Tac Toe and Number Guessing Game using all the previously learned concepts.',
        },
      ],
    },
    {
      title: 'Object Oriented Programming',
      moduleNumber: 5,
      outline:
        'Learn about Object Oriented Programming concepts and how to implement them in Python to optimize the code and make it reusable.',
      logo: '/images/courses/python-for-kids/modules/module-5.png',
      lessons: [
        {
          title: 'Object-Oriented Programming',
          outline:
            'Get introduced to basic object-oriented programming concepts in Python, like - objects, classes, and attributes.',
        },
        {
          title: 'More on Object-Oriented Programming',
          outline:
            'Learn about essential concepts of Object-Oriented Programming to understand how classes work.',
        },
        {
          title: 'Inheritance',
          outline:
            'Get introduced to inheritance in Python and implement inheritance in Python to make the code reusable.',
        },
        {
          title: 'Encapsulation and Special functions',
          outline:
            'Learn about encapsulation that helps you restrict access to variables and methods.',
        },
        {
          title: 'Abstraction and Polymorphism',
          outline:
            'Get knowledge about abstraction and polymorphism to showcase only the essential aspects and hide the inner workings/implementation.',
        },
        {
          title: 'Challenges in Object-Oriented Programming',
          outline:
            'Learn about operator overloading and practice previously learned concepts of Object-Oriented Programming in Python.',
        },
      ],
    },
    {
      title: 'Game Building with Pygame',
      moduleNumber: 6,
      outline:
        'Learn about all the necessary components for creating a video game step by step using the Pygame module of Python.',
      logo: '/images/courses/python-for-kids/modules/module-6.png',
      lessons: [
        {
          title: 'Let’s begin with Pygame',
          outline:
            'Learn about basic concepts of the game building using Pygame and create video games through the Pygame library.',
        },
        {
          title: 'Basic game building concepts',
          outline:
            'Create polygons using the functions of Pygame and use key detection in the game.',
        },
        {
          title: "Let's Add Sprites",
          outline:
            'Learn to add sprites to the Pygame Screen and control them in the game.',
        },
        {
          title: "Let's Level Up The Game",
          outline:
            'Learn to make a game interactive with different effects like catchy background, collisions, and sound to your game.',
        },
        {
          title: 'Space Invader Project - Part 1',
          outline:
            'Build the capstone project of a space invader game to implement all the learnings of Pygame.',
        },
        {
          title: 'Space Invader Project - Part 2',
          outline:
            'Build the capstone project of a space invader game to implement all the learnings of Pygame.',
        },
      ],
    },
    {
      title: 'GUI using Python Tkinter',
      moduleNumber: 7,
      outline:
        'Get introduced to the Python library Tkinter to create GUI applications that perform the required task without showing the backend code like an application. At the end of this module, you will develop an application.',
      logo: '/images/courses/python-for-kids/modules/module-7.png',
      lessons: [
        {
          title: 'Widgets for starters!',
          outline:
            'Learn about the basics of the Python library Tkinter and the different widget options available.',
        },
        {
          title: 'Tkinter geometry managers',
          outline:
            'Learn how to use various geometry managers to organize better and control your GUI application layout using Python Tkinter.',
        },
        {
          title: 'Where’s the event?',
          outline:
            "Learn about events, use event handlers in Tkinter, and increase an application's interactivity by using a message box.",
        },
        {
          title: 'Build a text editor',
          outline:
            'Create a text editor application using the Python Tkinter library, and learn to open and save the files using Tkinter in Python.',
        },
        {
          title: 'Denomination calculator',
          outline:
            'Learn to use Top Level in the Tkinter library to add multiple windows to the application.',
        },
        {
          title: 'Restaurant Management System',
          outline:
            'Create a Restaurant Management System application using all the concepts from the Tkinter library of Python.',
        },
      ],
    },
  ],
  ROBLOX: [
    {
      title: 'Introduction to Roblox studio',
      moduleNumber: 1,
      logo: '/images/courses/roblox/roblox-module-1.png',
      outline:
        'Get introduced to Roblox coding while building fun games on Roblox studio. Learn Lua, the text-based scripting language used in Roblox.',
      lessons: [
        {
          title: 'Discovering Roblox',
          outline:
            'In this lesson, we will introduce students to Roblox and get them familiarized with the platform.',
        },
        {
          title: 'Basic Roblox coding',
          outline:
            'Get introduced to basic Roblox coding by adding more elements to the game and increasing difficulty.',
        },
        {
          title: 'Adding advance obstacles through coding',
          outline:
            'Deep-dive into Roblox programming by increasing difficulty through speed blocks, lava blocks and much more.',
        },
        {
          title: 'Creating a User Interface in Roblox',
          outline:
            'Learn how to create a user interface through scripting in Roblox.',
        },
        {
          title: 'Creating Models Using Roblox Properties',
          outline:
            'Using properties of Roblox programming, build the model of a house including doors, windows and stairs by introducing the concept of Union & Negate.',
        },
        {
          title: 'Creating a Mini Game in Roblox',
          outline:
            'Recall previously learned lessons and use all the concepts to make a mini-game.',
        },
      ],
    },
    {
      title: 'Creating an Obby',
      moduleNumber: 2,
      logo: '/images/courses/roblox/roblox-module-2.png',
      outline:
        'Using simple shapes, create a mini Obby game. Learn to build a multi-level game that has different obstacles.',
      lessons: [
        {
          title: 'Setting up Game Information',

          outline:
            'Learn how to add a game name, description and a game icon on the Roblox website. Start building the scene for the game in this module.',
        },
        {
          title: 'Building Levels',
          outline:
            'Set up the scene further by adding obstacles with different difficulty levels while learning Roblox coding.',
        },
        {
          title: 'Creating Checkpoints',
          outline:
            'Set up the scene further by adding checkpoints at different difficulty levels.',
        },
        {
          title: 'Creating a Shop with a Shopping Menu',
          outline:
            'Add a shop at the beginning of the Roblox game. Once the player visits the shop, a shopping menu will appear.',
        },
        {
          title: 'New Obstacles Incoming!',
          outline:
            'Set up a game scene in Roblox with spinning balls, disappearing stairs and obstacles on which, when the player steps, the dimensions of the player change.',
        },
        {
          title: 'Tick! Tock!',
          outline:
            'Set up a timer in the Roblox game. The player will have to finish the game within a specific time to win. If the player loses, the timer resets and the player starts from the spawn location.',
        },
      ],
    },
    {
      title: 'Creating a 2D platformer',
      moduleNumber: 3,
      logo: '/images/courses/roblox/roblox-module-3.png',
      outline:
        'Learn how to change the controls for the camera and player. This will help you make it act like a 2D platformer.',
      lessons: [
        {
          title: 'Setting up camera and player control',
          outline:
            'Learn how to change the controls of the camera and player in Roblox studio.',
        },
        {
          title: 'Designing the level layout',
          outline:
            'Learn how to collect coins and keep up score while learning Roblox programming. Also, learn how to avoid death traps so that the player doesn’t die in the game.',
        },
        {
          title: 'Adding enemies and power',
          outline:
            'Learn how to add powerup to give player invisibility in Roblox and avoid being attacked by enemies.',
        },
        {
          title: 'Boss fight',
          outline:
            'Learn how to create a customised AI boss that the player has to defeat in order to move to the next level.',
        },
        {
          title: 'Creating levels ',
          outline:
            'Learn to design new levels in Roblox that will look different from the previous level and will also have challenging obstacles.',
        },
        {
          title: 'Finishing and testing the game',
          outline: 'Finish scripting and testing the game you created.',
        },
      ],
    },
    {
      title: 'Tycoon game',
      moduleNumber: 4,
      logo: '/images/courses/roblox/roblox-module-4.png',
      outline:
        'Learn advanced Roblox programming concepts while creating your own Tycoon game.',
      lessons: [
        {
          title: 'Setting up Tycoon system',

          outline:
            'Learn how to create a setup for the Tycoon for adding, creating and destroying components in Roblox.',
        },
        {
          title: 'Unlocking objects and leaderstats',
          outline:
            'Learn how to unlock objects and spawn objects into platforms.',
        },
        {
          title: 'Making money in Tycoon',
          outline:
            'Learn to set a way for the player to earn cash by creating a machine that creates objects that are shipped on a conveyor belt.',
        },
        {
          title: 'Saving data and upgrading',
          outline:
            'Learn how to save the data in Roblox for not only the money made so far, but also have the ability to upgrade items for the game as well.',
        },
        {
          title: 'Ownership and game passes',
          outline:
            'Learn how players can create ownership of the items they purchased.',
        },
        {
          title: 'Finishing and testing the game',
          outline:
            'Finish scripting and testing the game you created with Roblox coding.',
        },
      ],
    },
    {
      title: 'Racing game',
      moduleNumber: 5,
      logo: '/images/courses/roblox/roblox-module-5.png',
      outline:
        'Learn to create an addictive racing game. Players can create their track and checkpoints to track how many laps the player has collected.',
      lessons: [
        {
          title: 'Creating a car',

          outline:
            'Learn to build cars for the racing game, script how fast the cars go and give plates the ability to enter vehicles.',
        },
        {
          title: 'Creating a racetrack',
          outline: 'Learn to create a racing track for the Roblox game.',
        },
        {
          title: 'Scripting the game',
          outline:
            'Learn to spawn car objects onto the track for players to drive.',
        },
        {
          title: 'Game loop!',
          outline:
            'Learn creating a way to set, reset, destroy objects and also resetting game play elements for the game on Roblox studio.',
        },
        {
          title: 'Building a lobby area',
          outline:
            'In this lesson, we will learn to build lobby area for players entering the game.',
        },
        {
          title: 'Finishing and testing the game',
          outline: 'IFinish scripting and testing the game you created.',
        },
      ],
    },
    {
      title: 'Battle Royale',
      moduleNumber: 6,
      logo: '/images/courses/roblox/roblox-module-6.png',
      outline:
        'Create a Battle Royale Game with mini maps of different designs. Learn to build a lobby area for players entering the game.',
      lessons: [
        {
          title: 'Setting up lobby for Battle Royale',
          outline:
            'Build a lobby area for players as they wait for the game to start.',
        },
        {
          title: 'Creating Maps!',
          outline: 'Learn to create mini maps with different design layouts. ',
        },
        {
          title: 'Creating a Map voting application',
          outline: 'Learn to create a map voting application.',
        },
        {
          title: 'Creating in-game mechanic',
          outline:
            'Create in game mechanics for Battle Royale which will include health, stamina, kill count etc.',
        },
        {
          title: 'Setting up elements',
          outline:
            'While learning Roblox coding, create game loops which will have elements like ability to watch other players, respawning to lobby, checking winners etc.',
        },
        {
          title: 'Finishing and testing the game',
          outline:
            'Finish scripting and testing the game you created with Roblox coding.',
        },
      ],
    },
  ],
  AP_COMPUTER_SCIENCE_A: [
    {
      title: 'Introduction to Java',
      moduleNumber: 1,
      logo:
        '/images/courses/ap-computer-science-a/ap-computer-science-a-module-1.png',
      outline:
        'Learn the basics of the most popular language, i.e. Java, with the help of different activities. Also, learn about data types, conditionals and loops and functions. In addition, learn to create patterns.',
      lessons: [
        {
          title: 'Hey Java',
          outline: 'Get introduced to the language of possibilities, i.e.Java.',
        },
        {
          title: 'Welcome to data types & operators',
          outline:
            'Get introduced to the various data types and operators in Java. Also, learn about methods.',
        },
        {
          title: 'Conditionals',
          outline:
            'Get introduced to conditional statements and their application using java.',
        },
        {
          title: 'Advanced conditionals',
          outline:
            'Deep-dive into conditional statements specially the nested conditionals asked in the AP Computer Science A exam.',
        },
        {
          title: 'Iteration',
          outline: 'Introduction to loops in java and their applications.',
        },
        {
          title: 'Advanced iteration',
          outline:
            'Deep-dive into loops, specially the nested loops asked in the AP Computer Science A exam.',
        },
      ],
    },
    {
      title: 'Object Oriented Programming Using Java',
      moduleNumber: 2,
      logo:
        '/images/courses/ap-computer-science-a/ap-computer-science-a-module-2.png',
      outline:
        'Continue learning java by working on oops concepts, i.e., classes, objects, libraries, polymorphism, encapsulation, inheritance, abstraction and last but not least file handling.',
      lessons: [
        {
          title: 'OOPS!',

          outline:
            'Introduction to the Object-Oriented Programming System (OOPs) concepts.',
        },
        {
          title: 'Let’s inherit!',
          outline:
            'Get introduced to the concept of inheritance in Java using various activities.',
        },
        {
          title: 'Can you access me',
          outline:
            'Get introduced to many keywords like super, overriding, and overloading. Also, learn about different types of access modifiers and packages.',
        },
        {
          title: 'Get set go',
          outline:
            'Get introduced to the concept of encapsulation and learn about setters and getters.',
        },
        {
          title: 'I exist in many forms',
          outline:
            'Get introduced to the concept of polymorphism and abstraction.',
        },
        {
          title: 'Handling & Interface',
          outline: 'Learn the concept of file handling and interfaces.',
        },
      ],
    },
    {
      title: 'Practice questions on OOPS',
      moduleNumber: 3,
      logo:
        '/images/courses/ap-computer-science-a/ap-computer-science-a-module-3.png',
      outline:
        'Learn to solve the Free Response Question asked previously in the AP Computer Science A Exam about the OOPS concept.',
      lessons: [
        {
          title: 'Practice Free Response Question 1',

          outline:
            'Solve previously asked Free Response Questions about classes.',
        },
        {
          title: 'Practice Free Response Question 2',
          outline:
            'Solve previously asked Free Response Questions about objects.',
        },
        {
          title: 'Practice Free Response Question 3',

          outline:
            'Solve previously asked Free Response Questions about methods.',
        },
        {
          title: 'Practice Free Response Question 4',
          outline:
            'Solve previously asked Free Response Questions of complex type.',
        },
        {
          title: 'Practice Free Response Question 5',

          outline:
            'Solve previously asked Free Response Questions of complex type.',
        },
        {
          title: 'Practice Free Response Question 6',
          outline:
            'Solve previously asked Free Response Questions of complex type.',
        },
      ],
    },
    {
      title: 'Arrays, 2-D arrays & arraylist in Java',
      moduleNumber: 4,
      logo:
        '/images/courses/ap-computer-science-a/ap-computer-science-a-module-4.png',
      outline:
        'Elaborate introduction to the concepts of arrays,2d arrays & arraylist to help you know how arrays get space in the heap and memory mapping.',
      lessons: [
        {
          title: 'Introduction to arrays',
          outline:
            'Get introduced to arrays, declare an array, access its elements, and perform operations.',
        },
        {
          title: 'Kadanes algorithm',
          outline:
            'Learn about the Kadane algorithm and how to use it to find the maximum array sum and its applications.',
        },
        {
          title: 'Introduction to 2-dimensional arrays',
          outline: 'Get introduced to the concept of 2-Dimensional arrays.',
        },
        {
          title: 'Problems on 2-dimensional arrays',
          outline: 'Practice problems based on 2-Dimensional arrays.',
        },
        {
          title: 'Introduction to array list',
          outline: 'Get introduced to the concept of arraylist.',
        },
        {
          title: 'Problems on array list',
          outline: 'Practice problems related to arraylist.',
        },
      ],
    },
    {
      title: 'Introduction to recursion',
      moduleNumber: 5,
      logo:
        '/images/courses/ap-computer-science-a/ap-computer-science-a-module-5.png',
      outline:
        'Learn about recursion process, recursive function and how recursion works.',
      lessons: [
        {
          title: 'Call me recursion',
          outline:
            'Learn the basic concepts of recursion, how to use recursion, base case and recursive calls.',
        },
        {
          title: 'Types of recursion',
          outline:
            'Learn about types of recursion, their respective time complexities and how to use them.',
        },
        {
          title: 'Tower of Hanoi',
          outline:
            'Get introduced to the classic recursion problem name Tower of Hanoi.',
        },
        {
          title: 'Recursion in arrays',
          outline: 'Learn how to use and solve recursion in arrays.',
        },
        {
          title: 'Recursion in arraylist',
          outline: 'Learn how to use and solve recursion in arraylist.',
        },
        {
          title: 'Brainstorm',
          outline:
            'Solve some tricky recursive functions and analyse those functions.',
        },
      ],
    },
    {
      title: 'Introduction to searching & sorting',
      moduleNumber: 6,
      logo:
        '/images/courses/ap-computer-science-a/ap-computer-science-a-module-6.png',
      outline:
        'Learn about searching and sorting algorithms, concepts and  implementation using Java.',
      lessons: [
        {
          title: 'Introduction to binary search',
          outline:
            'Get introduced to the binary search algorithms using iteration & recursion both.',
        },
        {
          title: 'Introduction to bubble sort',
          outline:
            'Get introduced to sorting algorithms using iteration & recursion both.',
        },
        {
          title: 'Introduction to selection sort',
          outline:
            'Learn about selection sort algorithms using iteration & recursion both.',
        },
        {
          title: 'Introduction to insertion sort',
          outline:
            'Get introduced to the insertion sort algorithms using iteration & recursion both.',
        },
        {
          title: 'Introduction to quick sort',
          outline:
            'Learn about the quick sort algorithms using iteration & recursion both.',
        },
        {
          title: 'Introduction to serge sort',
          outline:
            'Get introduced to the merge sort algorithms using iteration & recursion both.',
        },
      ],
    },
    {
      title: 'Introduction to strings',
      moduleNumber: 7,
      logo:
        '/images/courses/ap-computer-science-a/ap-computer-science-a-module-7.png',
      outline:
        'Learn about palindrome, anagram, and basic problem solving in strings, {EXTRAS: Rabin Karp Algorithm, KMP Algorithm }',
      lessons: [
        {
          title: 'Introduction to Strings',
          outline:
            'Learn about the basics of strings in Python. Also, learn some basic operations on strings in Java.',
        },
        {
          title: 'String methods',
          outline:
            'Get introduced to the basics of strings in Java. Also, learn about operations on strings in Python and string methods.',
        },
        {
          title: 'String game',
          outline:
            'Learn counting the frequency of each character in the string and related programs.',
        },
        {
          title: 'Previous years problem discussion I',
          outline:
            'Discuss and practice previous year’s AP Computer Science A questions on strings.',
        },
        {
          title: 'String algorithms',
          outline:
            'Get introduced to KMP & Rabin Karp algorithm to the students.',
        },
        {
          title: 'Previous years problem discussion II',
          outline:
            'Discuss and practice previous year’s AP Computer Science A questions on strings.',
        },
      ],
    },
    {
      title: 'Practice question on arrays',
      moduleNumber: 8,
      logo:
        '/images/courses/ap-computer-science-a/ap-computer-science-a-module-8.png',
      outline:
        'Learn to solve the Free Response Question asked previously in the AP Computer Science A Exam about the Arrays concept.',
      lessons: [
        {
          title: 'PQ FRQ 1',
          outline:
            'Solve previously asked Free Response Questions about 1-Dimensional arrays.',
        },
        {
          title: 'PQ FRQ 2',
          outline:
            'Solve previously asked Free Response Questions about 2-Dimensional arrays.',
        },
        {
          title: 'PQ FRQ 3',
          outline:
            'Solve previously asked Free Response Questions about arraylist.',
        },
        {
          title: 'PQ FRQ 4',
          outline:
            'Solve previously asked Free Response Questions about recursion using 1-Dimensional arrays.',
        },
        {
          title: 'PQ FRQ 5',
          outline:
            'Solve previously asked Free Response Questions about recursion using 2-Dimensional arrays.',
        },
        {
          title: 'PQ FRQ 6',
          outline:
            'Solve previously asked Free Response Questions about recursion using arraylist.',
        },
      ],
    },
  ],
}

export const COURSE_RESOURCE_DATA: Record<
  CourseNameType,
  courseResourceType
> = {
  SCRATCH_PROGRAMMING: [
    {
      title: 'Introduction to Scratch - Block based programming language',
      description:
        'Block coding is a process used in computer programming where text-based software codes change to a visual block format to create animated games, characters, and even stories.',
      link:
        'https://www.codingal.com/coding-for-kids/coding-guides/block-coding-guide/',
    },
    {
      title: 'Learn to build Solar Project using Scratch programming',
      description:
        "Scratch is a free block-based programming platform designed primarily for kids. It's a visual programming interface where kids can code interactive stories, games and animations and share their creations through an online community.",
      link:
        'https://www.codingal.com/coding-for-kids/blog/how-to-build-a-solar-system-project/',
    },
    {
      title: '5 benefits of learning Scratch for kid',
      description:
        'Scratch programming encourages creative thinking in kids. It’s designed primarily for kids to encourage creativity by providing them with an easy to learn yet powerful programming platform. Tap on read more to know more about other benefits of Scratch programming.',
      link:
        'https://www.codingal.com/coding-for-kids/blog/benefits-of-learning-scratch-for-kids/',
    },
    {
      title: '7 best Scratch games for kids',
      description:
        'Flappy Bird is one of the most popular games on Scratch. It’s a side-scroller game where you control a bird attempting to fly between columns of green pipes without hitting them. Tap on read more to explore the best Scratch games for kids.',
      link:
        'https://www.codingal.com/coding-for-kids/blog/7-best-scratch-games-for-kids-to-learn-coding/',
    },
    {
      title: 'A beginner’s guide to block-based and text-based coding',
      description:
        'Block-based code will help you understand the basics and is a great place to start. Whereas, text-based coding is a natural progression for kids already versed in block-based coding.',
      link:
        'https://www.codingal.com/coding-for-kids/blog/block-based-and-text-based-coding/',
    },
    {
      title: '10 Best Scratch Projects for Kids',
      description:
        'Maze Starter is a fun Scratch project where you move a ball to reach the goal using the arrow keys on your keyboard. You can customise the game by adding more obstacles and constructing new levels. Tap on read more to explore the best Scratch projects for kids.',
      link:
        'https://www.codingal.com/coding-for-kids/blog/scratch-for-kids-projects-beginners/',
    },
  ],
  PYTHON_FOR_KIDS: [
    {
      title: 'Python for Kids: The Ultimate Guide to Learn Python',
      description:
        'Python is one of the popular and versatile programming languages. It is beginner friendly and therefore the best programming language to start with. It is considered to be one of the most used languages in the world.',
      link:
        'https://www.codingal.com/coding-for-kids/coding-guides/python-programming-for-kids-the-ultimate-guide-to-learn-python/',
    },
    {
      title: 'Strings in Python',
      description:
        'Python string is a data type in which the programming language stores any data in a computer. Python string is an array of bytes. A string is a sequence of characters. It is among the most popular data types in Python.',
      link: 'https://www.codingal.com/coding-for-kids/blog/strings-in-python/',
    },
    {
      title: 'Loops in Python',
      description:
        'Loops are a formidable abstraction in Python. Iterative looping, which loops employ, helps us eliminate repetitive code. This is incredibly useful when you want to repeat a function numerous times.',
      link: 'https://www.codingal.com/coding-for-kids/blog/loops-in-python/',
    },
    {
      title:
        'Top 5 reasons why Python programming for kids is perfect for your child',
      description:
        'Python is quick to learn even for kids or school students. Python programming is easy to read, write and learn. Tap on read more to know more about other reasons why Python programming for kids is perfect for your child.',
      link:
        'https://www.codingal.com/coding-for-kids/blog/top-5-reasons-why-python-programming-for-kids-is-perfect-for-your-child/',
    },
    {
      title: 'Dictionaries in Python',
      description:
        'Dictionary in Python is a collection of key values used to store data like a map, unlike other data types, which hold only a single value as an element.',
      link:
        'https://www.codingal.com/coding-for-kids/blog/dictionaries-in-python/',
    },
  ],
}

export const RECOMMENDED_COURSE_PAGE_NAMES = [
  'SCRATCH_PROGRAMMING',
  'ROBOTICS',
  'ROBLOX',
] as const

type StudentProjectsType = Record<
  CourseNameType,
  {
    heading: string
    contents: {
      category: string
      title: string
      studentName: string
      studentGrade: string
      link?: string
      image: {
        name: string
        alt: string
      }
    }[]
  }
>
export const STUDENT_PROJECTS: StudentProjectsType = {
  SCRATCH_PROGRAMMING: {
    heading:
      'Amazing projects our students have created through our training program',
    contents: [
      {
        category: 'Scratch Project',
        title: 'Frog, snake, & angel',
        studentName: 'Bhavi Tushar Chavan',
        studentGrade: 'Grade 1',
        link: 'https://scratch.mit.edu/projects/546408701',
        image: {
          name: 'scratch-programming/projects/project-1',
          alt: 'Frog, snake, & angel',
        },
      },
      {
        category: 'Scratch Project',
        title: 'Basketball court',
        studentName: 'Arihaan',
        studentGrade: 'Grade 5',
        link: 'https://scratch.mit.edu/projects/517066280',
        image: {
          name: 'scratch-programming/projects/project-2',
          alt: 'Basketball court',
        },
      },
      {
        category: 'Scratch Project',
        title: 'Flying bees',
        studentName: 'Adhyan',
        studentGrade: 'Grade 4',
        link: 'https://scratch.mit.edu/projects/479526004',
        image: {
          name: 'scratch-programming/projects/project-3',
          alt: 'Flying bees',
        },
      },
      {
        category: 'Scratch Project',
        title: 'Talking duck',
        studentName: 'Luv',
        studentGrade: 'Grade 4',
        link: 'https://scratch.mit.edu/projects/478140246',
        image: {
          name: 'scratch-programming/projects/project-4',
          alt: 'Talking duck',
        },
      },
      {
        category: 'Scratch Project',
        title: 'Sibling conversation',
        studentName: 'Omee',
        studentGrade: 'Grade 7',
        link: 'https://scratch.mit.edu/projects/508130300',
        image: {
          name: 'scratch-programming/projects/project-5',
          alt: 'Sibling conversation',
        },
      },
    ],
  },
  PYTHON_FOR_KIDS: {
    heading: 'Amazing projects our students have created through our program',
    contents: [
      {
        category: 'Python Project',
        title: 'Triple number swap',
        studentName: 'Krishna Ajay Jeswani',
        studentGrade: 'Grade 9',
        link: 'https://replit.com/@Gerumpity/Triple-Number-Swap#main.py',
        image: {
          name: 'python-for-kids/projects/project-1',
          alt: 'Triple number swap',
        },
      },
      {
        category: 'Python Project',
        title: 'Mirrored Triangle',
        studentName: 'Danish Piroozmand',
        studentGrade: 'Grade 7',
        link: 'https://replit.com/@DanishPiroozman/Mirrored-Triangle?v=1',
        image: {
          name: 'python-for-kids/projects/project-2',
          alt: 'Mirrored Triangle',
        },
      },
      {
        category: 'Python Project',
        title: 'Random Password challenge',
        studentName: 'Reyan Gupta',
        studentGrade: 'Grade 9',
        link: 'https://replit.com/@RebellionStrike/random-password-normal?v=1',
        image: {
          name: 'python-for-kids/projects/project-3',
          alt: 'Random Password challenge',
        },
      },
      {
        category: 'Python Project',
        title: 'Rock paper scissor',
        studentName: 'Piyush Kushwaha',
        studentGrade: 'Grade 10',
        link:
          'https://replit.com/@gamer-boy-piyus/MeaslyProfuseCloudcomputing?v=1',
        image: {
          name: 'python-for-kids/projects/project-4',
          alt: 'Rock paper scissor',
        },
      },
      {
        category: 'Python Project',
        title: 'Jumpy connections',
        studentName: 'Yashneil Tripathi',
        studentGrade: 'Grade 9',
        link: 'https://replit.com/@YashneilPlays/PinkJumpyConnections?v=1',
        image: {
          name: 'python-for-kids/projects/project-5',
          alt: 'Jumpy connections',
        },
      },
      {
        category: 'Python Project',
        title: 'Age calculator',
        studentName: 'Rajan Yadav',
        studentGrade: 'Grade 8',
        link: 'https://replit.com/@RajanYadav1/Age-Calculator-1?v=1',
        image: {
          name: 'python-for-kids/projects/project-6',
          alt: 'Age calculator',
        },
      },
    ],
  },
  ROBLOX: {
    heading: 'Our students created some amazing projects',
    contents: [
      {
        category: 'Roblox Project',
        title: 'Race with speed',
        studentName: 'Mike',
        studentGrade: 'Grade 5',
        image: {
          name: 'roblox/projects/project-1',
          alt: 'Race with speed',
        },
      },
      {
        category: 'Roblox Project',
        title: 'Pizza land',
        studentName: 'Onyx Bank',
        studentGrade: 'Grade 4',
        image: {
          name: 'roblox/projects/project-2',
          alt: 'Pizza land',
        },
      },
      {
        category: 'Roblox Project',
        title: 'Hide and seek',
        studentName: 'Owen Sameul',
        studentGrade: 'Grade 5',
        image: {
          name: 'roblox/projects/project-3',
          alt: 'Hide and seek',
        },
      },
      {
        category: 'Roblox Project',
        title: 'Obby game',
        studentName: 'Tala Daniel',
        studentGrade: 'Grade 8',
        image: {
          name: 'roblox/projects/project-4',
          alt: 'Obby game',
        },
      },
      {
        category: 'Roblox Project',
        title: 'Rainbow stairs',
        studentName: 'Flora Navy',
        studentGrade: 'Grade 7',
        image: {
          name: 'roblox/projects/project-5',
          alt: 'Rainbow stairs',
        },
      },
      {
        category: 'Roblox Project',
        title: 'Amusement park',
        studentName: 'Neha',
        studentGrade: 'Grade 7',
        image: {
          name: 'roblox/projects/project-6',
          alt: 'Amusement park',
        },
      },
    ],
  },
  AP_COMPUTER_SCIENCE_A: {
    heading: 'Our students created some amazing projects',
    contents: [
      {
        category: 'Java Project',
        title: 'Mind Riddler',
        studentName: 'Jack Grayson',
        studentGrade: 'Grade 9',
        image: {
          name: 'ap-computer-science-a/projects/project-1',
          alt: 'Mind Riddler',
        },
      },
      {
        category: 'Java Project',
        title: 'Tower of Hanoi',
        studentName: 'Luca  Jaxon',
        studentGrade: 'Grade 10',
        image: {
          name: 'ap-computer-science-a/projects/project-2',
          alt: 'Tower of Hanoi',
        },
      },
      {
        category: 'Java Project',
        title: 'GetSet',
        studentName: 'Indigo Caleb',
        studentGrade: 'Grade 11',
        image: {
          name: 'ap-computer-science-a/projects/project-3',
          alt: 'GetSet',
        },
      },
      {
        category: 'Java Project',
        title: 'Access Me',
        studentName: 'Savannah',
        studentGrade: 'Grade 12',
        image: {
          name: 'ap-computer-science-a/projects/project-4',
          alt: 'Access Me',
        },
      },
    ],
  },
}

type MasterClassesType = Record<
  CourseNameType,
  {
    heading: string
    contents: {
      title: string
      link: string
      image: {
        name: string
        alt: string
      }
    }[]
  }
>
export const MASTER_CLASSES: MasterClassesType = {
  SCRATCH_PROGRAMMING: {
    heading: 'Scratch programming masterclasses',
    contents: [
      {
        title: 'Halloween Costume Game on Scratch',
        link:
          'https://www.codingal.com/coding-for-kids/masterclasses/scratch-game-halloween/',
        image: {
          name: '/scratch-programming/masterclasses/masterclass-1',
          alt: 'Halloween Costume Game on Scratch',
        },
      },
      {
        title: 'Animation and Game Design Masterclass',
        link:
          'https://www.codingal.com/coding-for-kids/masterclasses/animation-and-game-design-masterclass/',
        image: {
          name: '/scratch-programming/masterclasses/masterclass-2',
          alt: 'Animation and Game Design Masterclass',
        },
      },
      {
        title: 'Dance Party with Spiderman using Scratch Programming',
        link:
          'https://www.codingal.com/coding-for-kids/masterclasses/dance-party-with-spiderman-using-scratch-programming/',
        image: {
          name: '/scratch-programming/masterclasses/masterclass-3',
          alt: 'Dance Party with Spiderman using Scratch Programming',
        },
      },
      {
        title: 'Captain America Game using Scratch Programming',
        link:
          'https://www.codingal.com/coding-for-kids/masterclasses/building-a-captain-america-game-on-scratch/',
        image: {
          name: '/scratch-programming/masterclasses/masterclass-4',
          alt: 'Captain America Game using Scratch Programming',
        },
      },
    ],
  },
  PYTHON_FOR_KIDS: {
    heading: 'Python for Kids masterclasses',
    contents: [
      {
        title: 'Create a healthcare application using Python',
        link: 'https://www.youtube.com/watch?v=lQqKbxl5-60&t=298s',
        image: {
          name: '/python-for-kids/masterclasses/masterclass-1',
          alt: 'Create a healthcare application using Python',
        },
      },
      {
        title: 'BMI calculator using Python',
        link: 'https://www.youtube.com/watch?v=KX0go_va6HM&t=1125s',
        image: {
          name: '/python-for-kids/masterclasses/masterclass-2',
          alt: 'BMI calculator using Python',
        },
      },
    ],
  },
}

export const VIDEO_RESOURCES: MasterClassesType = {
  SCRATCH_PROGRAMMING: {
    heading:
      'Refresh your knowledge with these informative concept-based videos',
    contents: [
      {
        title:
          'Learn to build a solar system project using Scratch | Scratch tutorial',
        link: 'https://www.youtube.com/watch?v=Fu9gKxxXfgo',
        image: {
          name: '/scratch-programming/video-resources/video-1',
          alt:
            'Learn to build a solar system project using Scratch | Scratch tutorial',
        },
      },
      {
        title: 'Coding for Kids: An Introduction',
        link: 'https://www.youtube.com/watch?v=MGpT8rXRCf8&t=19s',
        image: {
          name: '/scratch-programming/video-resources/video-2',
          alt: 'Coding for Kids: An Introduction',
        },
      },
      {
        title: '5 Best Free Platforms for Kids to Learn to Code',
        link: 'https://www.youtube.com/watch?v=lsCObbRG370&t=29s',
        image: {
          name: '/scratch-programming/video-resources/video-3',
          alt: '5 Best Free Platforms for Kids to Learn to Code',
        },
      },
    ],
  },
  PYTHON_FOR_KIDS: {
    heading:
      'Refresh your knowledge about Python for Kids and Teens with these informative concept-based videos',
    contents: [
      {
        title: 'Types of coding: Block-based and text-based coding',
        link: 'https://www.youtube.com/watch?v=PI5yZGqGRWI',
        image: {
          name: '/python-for-kids/video-resources/video-1',
          alt: 'Types of coding: Block-based and text-based coding',
        },
      },
      {
        title: 'Coding for Kids: An Introduction',
        link: 'https://www.youtube.com/watch?v=MGpT8rXRCf8&t=19s',
        image: {
          name: '/python-for-kids/video-resources/video-2',
          alt: 'Coding for Kids: An Introduction',
        },
      },
      {
        title: 'Top 8 programming languages for kids',
        link: 'https://www.youtube.com/watch?v=-cSRp_T1Mu0',
        image: {
          name: '/python-for-kids/video-resources/video-3',
          alt: 'Top 8 programming languages for kids',
        },
      },
    ],
  },
}

type CourseQuizType = Record<
  CourseNameType,
  {
    heading: string
    contents: {
      title: string
      link: string
      grade: string
      totalProblems: string
      level: string
      tag: string
      image: {
        name: string
        alt: string
      }
    }[]
  }
>
export const COURSE_QUIZZES: CourseQuizType = {
  PYTHON_FOR_KIDS: {
    heading: 'Assess your knowledge with fun quizzes',
    contents: [
      {
        title: 'Fundamentals of Python Programming',
        link:
          'https://www.codingal.com/quizzes/fundamentals-of-python-programming/arena/',
        grade: '6-12',
        totalProblems: '10',
        level: 'Beginner',
        tag: 'Python',
        image: {
          name: '/python-for-kids/quizzes/quiz-1',
          alt: 'Fundamentals of Python Programming',
        },
      },
      {
        title: 'Loops in Python',
        link: 'https://www.codingal.com/quizzes/loops-in-python/arena/',
        grade: '6-12',
        totalProblems: '10',
        level: 'Intermediate',
        tag: 'Python',
        image: {
          name: '/python-for-kids/quizzes/quiz-2',
          alt: 'Loops in Python',
        },
      },
      {
        title: 'Advanced Python Programming',
        link:
          'https://www.codingal.com/quizzes/advanced-python-programming/arena/',
        grade: '6-12',
        totalProblems: '10',
        level: 'Expert',
        tag: 'Python',
        image: {
          name: '/python-for-kids/quizzes/quiz-3',
          alt: 'Advanced Python Programming',
        },
      },
    ],
  },
  SCRATCH_PROGRAMMING: {
    heading: 'Assess your knowledge with fun quizzes',
    contents: [
      {
        title: 'Introduction to Scratch Programming',
        link:
          'https://www.codingal.com/quizzes/introduction-to-scratch-programming/arena/',
        grade: '1-8',
        totalProblems: '10',
        level: 'Beginner',
        tag: 'Scratch',
        image: {
          name: '/scratch-programming/quizzes/quiz-1',
          alt: 'Introduction to Scratch Programming',
        },
      },
      {
        title: 'Scratch intermediate',
        link: 'https://www.codingal.com/quizzes/scratch-intermediate/arena/',
        grade: '1-8',
        totalProblems: '10',
        level: 'Intermediate',
        tag: 'Scratch',
        image: {
          name: '/scratch-programming/quizzes/quiz-2',
          alt: 'Scratch intermediate',
        },
      },
    ],
  },
}
export const RECOMMENDED_TOPICS = [
  {
    topic: 'Scratch programming course',
    link: '/courses/scratch-programming/',
    image: 'scratch-page.jpg',
  },
  {
    topic: 'Python for Kids course',
    link: '/courses/python-for-kids/',
    image: 'python-page.jpg',
    isBestSeller: true,
  },
  {
    topic: 'Coding for kids free resource',
    link: '/coding-for-kids/',
    image: 'coding-for-kids.jpg',
  },
]
